import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { API_BASE } from "../config/orbisConfig";



function buildAuthHeaders(extra = {}) {
    const token =
        localStorage.getItem("cani_auth") ||
        localStorage.getItem("token") ||
        "";

    return {
        ...extra,
        Authorization: token ? `Bearer ${token}` : "",
        "X-CANI-AUTH": token || "",
    };
}

function getLoggedUserId() {
    return (
        Number(localStorage.getItem("user_id")) ||
        Number(localStorage.getItem("id")) ||
        Number(localStorage.getItem("cani_user_id")) ||
        0
    );
}

function parseLocalDateOnly(value) {
    if (!value) return null;
    const raw = String(value);
    const dateOnly = raw.slice(0, 10);
    const match = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
        const [, y, m, d] = match;
        return new Date(Number(y), Number(m) - 1, Number(d));
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(value) {
    if (!value) return "—";
    try {
        const date = parseLocalDateOnly(value);
        if (!date) return value;
        return date.toLocaleDateString("es-MX", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return value;
    }
}

function formatDateTime(value) {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString("es-MX", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return value;
    }
}

function formatLogDayLabel(dateValue) {
    if (!dateValue) return "Sin fecha";

    const d = new Date(dateValue);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const sameDay = (a, b) =>
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();

    if (sameDay(d, today)) return "Hoy";
    if (sameDay(d, yesterday)) return "Ayer";

    return d.toLocaleDateString("es-MX", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

function groupLogsByDay(logs) {
    const groups = [];

    (logs || []).forEach((log) => {
        const label = formatLogDayLabel(log.created_at);
        const lastGroup = groups[groups.length - 1];

        if (!lastGroup || lastGroup.label !== label) {
            groups.push({ label, items: [log] });
        } else {
            lastGroup.items.push(log);
        }
    });

    return groups;
}


function isInterventionLog(log = {}) {
    const action = String(log?.action_type || "").toLowerCase();
    const comment = String(log?.comment || "").trim();

    return (
        action === "intervencion" ||
        /^\[(Documentar causa del bloqueo|Solicitar evidencia|Cargar evidencia|Reprogramar fecha compromiso|Escalar decisión|Marcar bloqueo resuelto|Registrar excepción|Convertir en riesgo)\]/i.test(comment)
    );
}

function buildReviewCycles(logs) {
    const ordered = [...(logs || [])].sort((a, b) => {
        const aTime = new Date(a.created_at || 0).getTime();
        const bTime = new Date(b.created_at || 0).getTime();

        if (aTime !== bTime) return aTime - bTime;

        return Number(a.id || 0) - Number(b.id || 0);
    });

    const cycles = [];
    let currentCycle = null;

    ordered.forEach((log) => {
        const action = String(log?.action_type || "").toLowerCase();

        if (action === "enviado_revision") {
            currentCycle = {
                id: log.id,
                sentLog: log,
                reviewLog: null,
                otherLogs: [],
            };

            cycles.push(currentCycle);
            return;
        }

        if (
            ["aprobado", "rechazado", "solicitud_cambios", "con_cambios"].includes(action) &&
            currentCycle &&
            !currentCycle.reviewLog
        ) {
            currentCycle.reviewLog = log;
            return;
        }

        if (currentCycle) {
            currentCycle.otherLogs.push(log);
        } else {
            cycles.push({
                id: log.id,
                sentLog: null,
                reviewLog: null,
                otherLogs: [log],
            });
        }
    });

    return cycles.reverse();
}
function getEvidenceSummaryText(items = []) {
    const list = Array.isArray(items) ? items : [];

    if (list.length === 0) {
        return "Sin evidencia asociada";
    }

    const total = list.length;
    const withGeo = list.filter((ev) =>
        Number(ev.has_geo || 0) === 1 || (ev.latitude && ev.longitude)
    ).length;

    const images = list.filter((ev) =>
        String(ev.evidence_type || "").toLowerCase() === "imagen" ||
        String(ev.mime_type || "").toLowerCase().startsWith("image/")
    ).length;

    const files = list.filter((ev) =>
        String(ev.evidence_type || "").toLowerCase() === "archivo"
    ).length;

    const comments = list.filter((ev) =>
        String(ev.evidence_type || "").toLowerCase() === "comentario"
    ).length;

    const parts = [];

    if (images > 0) parts.push(`${images} imagen(es)`);
    if (files > 0) parts.push(`${files} archivo(s)`);
    if (comments > 0) parts.push(`${comments} comentario(s)`);
    if (withGeo > 0) parts.push(`${withGeo} con ubicación`);

    return parts.length > 0 ? parts.join(" · ") : `${total} evidencia(s)`;
}

function getCycleResultText(reviewLog) {
    if (!reviewLog) {
        return {
            label: "Pendiente de revisión",
            detail: "El avance fue enviado y está esperando decisión del revisor.",
            bg: "#ffffff",
            border: "#fde68a",
            color: "#92400e",
        };
    }

    const action = String(reviewLog?.action_type || "").toLowerCase();
    const newStatus = String(reviewLog?.new_status || "").toLowerCase();
    const oldProgress = reviewLog?.old_progress_percent;
    const newProgress = reviewLog?.new_progress_percent;

    if (action === "aprobado") {
        if (newStatus === "terminada" || Number(newProgress || 0) >= 100) {
            return {
                label: "Tarea terminada",
                detail: `El avance fue aprobado y la tarea llegó al ${newProgress || 100}%.`,
                bg: "#ecfdf5",
                border: "#a7f3d0",
                color: "#047857",
            };
        }

        return {
            label: "Avance aprobado",
            detail: `El avance fue aprobado y la tarea continúa en proceso al ${newProgress || oldProgress || 0}%.`,
            bg: "#ecfdf5",
            border: "#a7f3d0",
            color: "#047857",
        };
    }

    if (action === "rechazado") {
        return {
            label: "Avance rechazado",
            detail: "El avance fue rechazado y debe ser corregido por el responsable.",
            bg: "#ffffff",
            border: "#fecaca",
            color: "#b91c1c",
        };
    }

    if (action === "solicitud_cambios" || action === "con_cambios") {
        return {
            label: "Cambios solicitados",
            detail: "El revisor solicitó ajustes antes de aprobar este avance.",
            bg: "#ffffff",
            border: "#fdba74",
            color: "#9a3412",
        };
    }

    return {
        label: "Movimiento registrado",
        detail: "Este ciclo tiene movimientos registrados en la bitácora.",
        bg: "#f8fafc",
        border: "#e5e7eb",
        color: "#374151",
    };
}

function getLogTitle(log) {
    const action = String(log?.action_type || "").toLowerCase();

    switch (action) {
        case "actualizacion_avance":
            return "Avance actualizado";
        case "enviado_revision":
            return "Enviado a revisión";
        case "aprobado":
            return "Avance aprobado";
        case "rechazado":
            return "Avance rechazado";
        case "solicitud_cambios":
        case "con_cambios":
            return "Cambios solicitados";
        case "comentario":
            return "Comentario";
        default:
            return log?.action_label || log?.action_type || "Movimiento";
    }
}

function getLogDotColor(log) {
    const action = String(log?.action_type || "").toLowerCase();
    const review = String(log?.review_status || "").toLowerCase();

    if (action === "aprobado" || review === "aprobado") return "#10b981";
    if (action === "rechazado" || review === "rechazado") return "#ef4444";
    if (action === "solicitud_cambios" || review === "con_cambios") return "#f97316";
    if (action === "enviado_revision" || review === "en_revision" || review === "pendiente_revision") return "#f59e0b";
    if (action === "actualizacion_avance") return "#2563eb";

    return "#9ca3af";
}

function getLogSummary(log) {
    const oldP = log?.old_progress_percent;
    const newP = log?.new_progress_percent;
    const oldS = log?.old_status;
    const newS = log?.new_status;
    const parts = [];

    if (oldP !== null && oldP !== undefined && newP !== null && newP !== undefined && String(oldP) !== String(newP)) {
        parts.push(`Avance ${oldP}% → ${newP}%`);
    }

    if (oldS && newS && oldS !== newS) {
        parts.push(`Estatus ${oldS} → ${newS}`);
    }

    return parts.join(" · ");
}

function getLogComment(log) {
    const action = String(log?.action_type || "").toLowerCase();
    const comment = String(log?.comment || "").trim();

    if (comment) return comment;

    switch (action) {
        case "actualizacion_avance":
            return "Se registró avance en la tarea.";
        case "enviado_revision":
            return "La tarea fue enviada a revisión.";
        case "aprobado":
            return "La revisión fue aprobada.";
        case "rechazado":
            return "La revisión fue rechazada.";
        case "solicitud_cambios":
        case "con_cambios":
            return "Se solicitaron cambios.";
        default:
            return "Sin comentario";
    }
}

const TASK_DEPENDENCY_TYPES = [
    {
        value: "FS",
        label: "Fin a Inicio (FS)",
        short: "La tarea anterior debe terminar para que esta pueda iniciar.",
        example: "Ejemplo: terminar diseño antes de iniciar desarrollo.",
    },
    {
        value: "SS",
        label: "Inicio a Inicio (SS)",
        short: "Esta tarea puede iniciar cuando la tarea anterior también inicia.",
        example: "Ejemplo: iniciar supervisión al mismo tiempo que inicia ejecución.",
    },
    {
        value: "FF",
        label: "Fin a Fin (FF)",
        short: "Esta tarea solo puede terminar cuando la tarea anterior termina.",
        example: "Ejemplo: cerrar validación cuando termina la implementación.",
    },
    {
        value: "SF",
        label: "Inicio a Fin (SF)",
        short: "Esta tarea termina cuando la tarea anterior inicia. Es poco común.",
        example: "Ejemplo: cerrar turno anterior cuando inicia el nuevo turno.",
    },
];

function getDependencyTypeInfo(value) {
    return (
        TASK_DEPENDENCY_TYPES.find((item) => item.value === value) ||
        TASK_DEPENDENCY_TYPES[0]
    );
}


function getStatusStyle(status) {
    switch (String(status || "").toLowerCase()) {
        case "pendiente":
            return { bg: "#f3f4f6", color: "#374151", label: "Pendiente" };
        case "en_proceso":
            return { bg: "#dbeafe", color: "#1d4ed8", label: "En proceso" };
        case "bloqueada":
            return { bg: "#ffffff", color: "#b91c1c", label: "Bloqueada", border: "#fecaca" };
        case "terminada":
            return { bg: "#dcfce7", color: "#166534", label: "Terminada" };
        case "cancelada":
            return { bg: "#f3f4f6", color: "#6b7280", label: "Cancelada" };
        default:
            return { bg: "#f3f4f6", color: "#374151", label: status || "Sin estatus" };
    }
}

function getReviewStyle(reviewStatus) {
    switch (String(reviewStatus || "").toLowerCase()) {
        case "en_revision":
        case "pendiente_revision":
            return { bg: "#ffffff", color: "#92400e", label: "En revisión", border: "#fde68a" };
        case "aprobado":
            return { bg: "#dcfce7", color: "#166534", label: "Aprobado" };
        case "rechazado":
            return { bg: "#ffffff", color: "#b91c1c", label: "Rechazado", border: "#fecaca" };
        case "con_cambios":
            return { bg: "#ffffff", color: "#9a3412", label: "Con cambios", border: "#fdba74" };
        case "borrador":
            return { bg: "#f3f4f6", color: "#374151", label: "Borrador" };
        default:
            return { bg: "#f3f4f6", color: "#374151", label: reviewStatus || "—" };
    }
}


function getTaskActionMessage(task, permissions = {}) {
    const status = String(task?.status || "").toLowerCase();
    const reviewStatus = String(task?.review_status || "").toLowerCase();

    if (status === "terminada") {
        return "Resultado cerrado. La tarea quedó terminada y su trazabilidad queda disponible para consulta.";
    }

    if (status === "bloqueada") {
        return "Ruta bloqueada. Esta tarea no debe avanzar hasta resolver la dependencia o condición que la detiene.";
    }

    if (["en_revision", "pendiente_revision"].includes(reviewStatus)) {
        if (permissions.can_review) {
            return "El responsable ya envió avance. Revisa evidencia, comentario y ubicación antes de decidir.";
        }
        return "En espera de revisión. El avance ya fue enviado y está pendiente de decisión del revisor.";
    }

    if (["rechazado", "con_cambios"].includes(reviewStatus)) {
        if (permissions.can_progress) {
            return "El revisor solicitó ajustes. Corrige la entrega y reenvía el avance con evidencia.";
        }
        return "La tarea tiene cambios solicitados. Consulta la observación del revisor y el historial de validación.";
    }

    if (reviewStatus === "aprobado" && status === "en_proceso") {
        return "Aprobado parcial. El avance anterior fue validado y puedes registrar el siguiente avance.";
    }

    if (permissions.can_progress) {
        return "Registra el resultado, adjunta evidencia y envía el avance en un solo flujo.";
    }

    if (permissions.can_review) {
        return "Valida el avance, revisa la evidencia y toma una decisión con trazabilidad.";
    }

    return "Consulta el estado, evidencia y ciclos de validación de esta tarea.";
}

function getFriendlyReviewLabel(value) {
    switch (String(value || "").toLowerCase()) {
        case "en_revision":
        case "pendiente_revision":
            return "En espera de revisión";
        case "aprobado":
            return "Aprobado";
        case "rechazado":
            return "Rechazado";
        case "con_cambios":
            return "Cambios solicitados";
        case "borrador":
            return "Sin revisión activa";
        default:
            return value || "—";
    }
}

function getStatusTooltip(status) {
    switch (String(status || "").toLowerCase()) {
        case "pendiente":
            return "La tarea aún no inicia. Revisa fecha, responsable y dependencias antes de activar trabajo.";
        case "en_proceso":
            return "La tarea está activa. El responsable puede registrar avance y evidencia cuando corresponda.";
        case "bloqueada":
            return "La tarea no debe avanzar hasta resolver la dependencia o bloqueo operativo.";
        case "terminada":
            return "La tarea fue cerrada y su trazabilidad queda disponible para consulta.";
        case "cancelada":
            return "La tarea fue cancelada y ya no requiere ejecución operativa.";
        default:
            return "Estado operativo de la tarea.";
    }
}

function getReviewTooltip(reviewStatus, taskStatus = "") {
    const review = String(reviewStatus || "").toLowerCase();
    const status = String(taskStatus || "").toLowerCase();

    if (review === "aprobado" && status === "en_proceso") {
        return "Aprobado parcial: el último avance fue validado, pero la tarea aún puede continuar hasta el cierre.";
    }

    switch (review) {
        case "en_revision":
        case "pendiente_revision":
            return "En espera de revisión: el responsable ya envió avance y el revisor debe tomar una decisión.";
        case "aprobado":
            return "Avance aprobado por el revisor.";
        case "rechazado":
            return "Avance rechazado: el responsable debe corregir y reenviar con evidencia suficiente.";
        case "con_cambios":
            return "Cambios solicitados: el revisor pidió ajustes antes de aprobar el avance.";
        case "borrador":
            return "Sin revisión activa en este momento.";
        default:
            return "Estado de revisión del avance.";
    }
}


function getFriendlyStatusLabel(status) {
    const value = String(status || "").toLowerCase();
    const labels = {
        pendiente: "Pendiente",
        en_proceso: "En proceso",
        bloqueada: "Bloqueada",
        terminada: "Terminada",
        cancelada: "Cancelada",
    };
    return labels[value] || status || "Sin estado";
}

function normalizeBooleanFlag(value) {
    return value === true || Number(value || 0) === 1 || String(value || "").toLowerCase() === "true";
}

function isTaskOverdue(task = {}) {
    const status = String(task?.status || "").toLowerCase();
    if (["terminada", "cancelada"].includes(status)) return false;
    if (!task?.due_date) return false;

    const due = parseLocalDateOnly(task.due_date);
    if (!due) return false;
    due.setHours(23, 59, 59, 999);

    return due.getTime() < Date.now();
}

function getDaysOverdue(task = {}) {
    if (!isTaskOverdue(task)) return 0;

    const due = parseLocalDateOnly(task.due_date);
    if (!due) return 0;
    due.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return Math.max(1, Math.floor((today.getTime() - due.getTime()) / 86400000));
}

function buildTaskIntervention(task = {}, evidenceSummary = {}, evidenceGeoStats = {}) {
    const status = String(task?.status || "").toLowerCase();
    const reviewStatus = String(task?.review_status || "").toLowerCase();
    const evidenceTotal = Number(evidenceSummary?.total ?? task?.evidence_count ?? 0);
    const geoTotal = Number(evidenceSummary?.with_geo ?? task?.geo_evidence_count ?? 0);
    const evidenceRequired = normalizeBooleanFlag(task?.evidence_required) || normalizeBooleanFlag(evidenceSummary?.evidence_required);
    const geoRequired = normalizeBooleanFlag(task?.geo_evidence_required) || normalizeBooleanFlag(evidenceSummary?.geo_evidence_required);
    const overdue = isTaskOverdue(task);
    const blocked = status === "bloqueada" || normalizeBooleanFlag(task?.is_blocked);
    const missingEvidence = evidenceRequired && evidenceTotal <= 0;
    const missingGeo = geoRequired && geoTotal <= 0;
    const lowProgress = Number(task?.progress_percent || 0) <= 10 && !["terminada", "cancelada"].includes(status);
    const correctionRequired = ["rechazado", "con_cambios"].includes(reviewStatus);
    const pendingGeoException = Number(evidenceGeoStats?.exception_requested || 0) > 0;
    const rejectedGeoException = Number(evidenceGeoStats?.exception_rejected || 0) > 0;

    const signals = [];

    if (blocked) {
        signals.push({
            key: "blocked",
            label: "Tarea bloqueada",
            detail: task?.blocked_reason || task?.manual_blocked_reason || "Existe un bloqueo operativo que debe documentarse o resolverse.",
            tone: "danger",
        });
    }

    if (overdue) {
        const days = getDaysOverdue(task);
        signals.push({
            key: "overdue",
            label: `Vencida hace ${days} día${days === 1 ? "" : "s"}`,
            detail: task?.due_date ? `Fecha compromiso: ${formatDate(task.due_date)}.` : "La fecha compromiso ya fue superada.",
            tone: "danger",
        });
    }

    if (missingEvidence) {
        signals.push({ key: "missing_evidence", label: "Evidencia requerida pendiente", detail: "La tarea exige evidencia documental y todavía no tiene registros asociados.", tone: "warning" });
    }

    if (missingGeo) {
        signals.push({ key: "missing_geo", label: "Sin geoevidencia", detail: "La tarea requiere ubicación y no existe evidencia con coordenadas registradas.", tone: "warning" });
    }

    if (pendingGeoException) {
        signals.push({ key: "pending_geo_exception", label: "Excepción geográfica pendiente", detail: "Existe una evidencia fuera de rango que requiere decisión del revisor.", tone: "warning" });
    }

    if (rejectedGeoException) {
        signals.push({ key: "rejected_geo_exception", label: "Geoevidencia rechazada", detail: "Hay evidencia geográfica no válida; se requiere corrección formal.", tone: "danger" });
    }

    if (correctionRequired) {
        signals.push({ key: "correction_required", label: "Corrección requerida", detail: "El revisor solicitó ajustes antes de aprobar el avance.", tone: "warning" });
    }

    if (lowProgress && (blocked || overdue || missingEvidence || missingGeo)) {
        signals.push({ key: "low_progress", label: `Avance bajo: ${Number(task?.progress_percent || 0).toFixed(0)}%`, detail: "El avance registrado no es suficiente para compensar la presión operativa detectada.", tone: "neutral" });
    }

    const requiresIntervention = signals.length > 0 && !["terminada", "cancelada"].includes(status);
    const severity = blocked && overdue && (missingEvidence || missingGeo) ? "critical" : blocked || overdue || rejectedGeoException ? "high" : missingEvidence || missingGeo || pendingGeoException || correctionRequired ? "medium" : "low";

    return { requiresIntervention, severity, signals, evidenceTotal, geoTotal, missingEvidence, missingGeo, blocked, overdue, daysOverdue: getDaysOverdue(task) };
}

function TaskInterventionPanel({ intervention, task, permissions = {}, onAction }) {
    if (!intervention?.requiresIntervention) return null;

    const severityMap = {
        critical: { kicker: "Intervención crítica", title: "Esta tarea requiere decisión PMO", border: "#ef4444", bg: "#fff7ed", icon: "!" },
        high: { kicker: "Intervención requerida", title: "Hay señales operativas que deben atenderse", border: "#f97316", bg: "#fff7ed", icon: "!" },
        medium: { kicker: "Atención PMO", title: "Faltan elementos para sostener la trazabilidad", border: "#f59e0b", bg: "#fffbeb", icon: "•" },
        low: { kicker: "Seguimiento sugerido", title: "Conviene documentar el estado antes de continuar", border: "#64748b", bg: "#f8fafc", icon: "i" },
    };

    const visual = severityMap[intervention.severity] || severityMap.medium;
    const canIntervene = permissions?.can_intervene_task === true;
    const blocked = normalizeBooleanFlag(task?.is_blocked) || String(task?.status || "").toLowerCase() === "bloqueada";

    const noPmoHelp = "Requiere intervención del PM del proyecto. El responsable puede cargar evidencia o reportar impedimentos, pero los cambios de compromiso, excepción o desbloqueo quedan bajo control PMO.";

    const actions = [
        {
            key: "document_block",
            label: "Documentar causa del bloqueo",
            detail: "Explica por qué se detuvo y qué impacto tiene.",
            help: "Acción del PM: registra por qué la tarea está detenida y deja una causa visible para dirección, PM y revisor. Sirve como base para decidir si se solicita evidencia, se reprograma, se escala o se libera el bloqueo.",
            available: canIntervene,
        },
        {
            key: "request_evidence",
            label: "Solicitar evidencia",
            detail: "Pide soporte documental al responsable.",
            help: "Acción del PM: solicita al responsable el soporte necesario para aclarar el bloqueo o comprobar avances. Ayuda a que la decisión quede trazada en la tarea.",
            available: canIntervene,
        },
        {
            key: "upload_evidence",
            label: "Cargar evidencia",
            detail: normalizeBooleanFlag(task?.geo_evidence_required) ? "Abre evidencia y georreferencia." : "Abre evidencia documental.",
            help: "Permite agregar documentos, imágenes o soportes que respalden el estado de la tarea. La evidencia fortalece la trazabilidad antes de revisar, liberar o escalar una decisión.",
            available: true,
        },
        {
            key: "escalate",
            label: "Escalar decisión",
            detail: "Eleva el caso a autoridad superior.",
            help: "Acción del PM: eleva el bloqueo a un nivel superior cuando requiere apoyo directivo, autorización externa o decisión estratégica.",
            available: false,
        },
        {
            key: "reschedule",
            label: "Reprogramar fecha compromiso",
            detail: "Cambia fecha con justificación trazable.",
            help: "Acción del PM: ajusta la fecha compromiso cuando el bloqueo afecta el cronograma. Debe registrarse con justificación porque puede impactar tareas, entregables y lectura ejecutiva.",
            available: canIntervene,
        },
        {
            key: "resolve_block",
            label: "Marcar bloqueo resuelto",
            detail: "Libera la tarea para volver a ejecución.",
            help: "Acción del PM: confirma que el impedimento fue atendido y libera la tarea para que el responsable continúe con el avance. No cambia el porcentaje; solo reactiva la ejecución con trazabilidad.",
            available: canIntervene && blocked,
        },
        {
            key: "exception",
            label: "Registrar excepción",
            detail: normalizeBooleanFlag(task?.geo_evidence_required) ? "Justifica excepción operativa o geográfica." : "Justifica excepción operativa.",
            help: "Acción del PM: documenta una condición temporal o justificada que impide cumplir el flujo normal. Ayuda a mantener control sin ocultar el riesgo ni perder trazabilidad.",
            available: canIntervene,
        },
        {
            key: "convert_risk",
            label: "Convertir en riesgo",
            detail: "Registra el bloqueo como riesgo formal.",
            help: "Acción del PM: convierte el bloqueo en un riesgo formal del proyecto cuando puede afectar alcance, fechas, entregables o continuidad. El riesgo queda registrado en el módulo de riesgos del proyecto.",
            available: canIntervene,
        },
    ];

    return (
        <section id="task-intervention-panel" style={{ border: `1px solid ${visual.border}`, borderLeft: `5px solid ${visual.border}`, borderRadius: 24, background: visual.bg, boxShadow: "0 18px 42px rgba(15,23,42,.08)", overflow: "hidden", marginBottom: 18 }}>
            <div style={{ padding: 20, borderBottom: "1px solid rgba(148,163,184,.35)", display: "grid", gridTemplateColumns: "46px 1fr", gap: 14, alignItems: "start" }}>
                <div style={{ width: 42, height: 42, borderRadius: 16, background: "#111827", color: "#fff", display: "grid", placeItems: "center", fontSize: 22, fontWeight: 950 }}>{visual.icon}</div>
                <div>
                    <div style={{ fontSize: 11, fontWeight: 950, letterSpacing: ".12em", textTransform: "uppercase", color: visual.border }}>{visual.kicker}</div>
                    <div style={{ marginTop: 4, fontSize: 21, fontWeight: 950, color: "#111827", letterSpacing: "-.03em" }}>{visual.title}</div>
                    <div style={{ marginTop: 7, fontSize: 13, lineHeight: 1.55, color: "#475569", fontWeight: 750 }}>
                        ORBIS detectó señales críticas de ejecución: estado <b>{getFriendlyStatusLabel(task?.status)}</b>, avance <b>{Number(task?.progress_percent || 0).toFixed(0)}%</b>, evidencia documental <b>{intervention.evidenceTotal}</b>{normalizeBooleanFlag(task?.geo_evidence_required) ? <> y geoevidencia <b>{intervention.geoTotal}</b></> : null}. La tarea debe convertirse en una ruta de decisión PMO, no en una pantalla pasiva.
                    </div>
                    {!canIntervene ? (
                        <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 14, background: "#fff", border: "1px solid #fed7aa", color: "#9a3412", fontSize: 12, fontWeight: 850, lineHeight: 1.45 }}>
                            Esta tarea requiere intervención del PM del proyecto. El responsable puede documentar evidencia, pero los cambios de compromiso, excepción o desbloqueo quedan bajo control PMO.
                        </div>
                    ) : null}
                </div>
            </div>
            <div className="task-intervention-body" style={{ padding: 18, display: "grid", gridTemplateColumns: "minmax(260px, .9fr) minmax(320px, 1.35fr)", gap: 16 }}>
                <div style={{ display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 950, color: "#334155", textTransform: "uppercase", letterSpacing: ".08em" }}>Motivos detectados</div>
                    {intervention.signals.map((signal) => (
                        <div key={signal.key} style={{ padding: 12, borderRadius: 16, border: "1px solid #e2e8f0", background: "#fff", display: "grid", gap: 4 }}>
                            <div style={{ color: signal.tone === "danger" ? "#b91c1c" : signal.tone === "warning" ? "#92400e" : "#334155", fontSize: 13, fontWeight: 950 }}>{signal.label}</div>
                            <div style={{ color: "#64748b", fontSize: 12, lineHeight: 1.4, fontWeight: 750 }}>{signal.detail}</div>
                        </div>
                    ))}
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 950, color: "#334155", textTransform: "uppercase", letterSpacing: ".08em" }}>Acciones de intervención</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10 }}>
                        {actions.map((action) => (
                            <button
                                key={action.key}
                                type="button"
                                onClick={() => action.available ? onAction?.(action.key) : null}
                                disabled={!action.available}
                                title={action.available ? action.help : `${noPmoHelp} ${action.help || action.detail}`}
                                style={{ textAlign: "left", border: action.available ? "1px solid #cbd5e1" : "1px dashed #cbd5e1", background: action.available ? "#ffffff" : "#f8fafc", color: action.available ? "#0f172a" : "#94a3b8", borderRadius: 16, padding: 13, cursor: action.available ? "pointer" : "not-allowed", boxShadow: action.available ? "0 8px 18px rgba(15,23,42,.05)" : "none" }}
                            >
                                <div style={{ fontSize: 13, fontWeight: 950 }}>{action.label}</div>
                                <div style={{ marginTop: 5, fontSize: 11, lineHeight: 1.35, fontWeight: 750 }}>{action.detail}</div>
                                {!action.available ? <div style={{ marginTop: 7, fontSize: 10, fontWeight: 950, color: "#64748b" }}>{canIntervene ? "En preparación" : "Requiere PM"}</div> : null}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function TaskInterventionHistoryPanel({ data, loading = false }) {
    const items = Array.isArray(data?.items) ? data.items : [];
    const stats = data?.stats || {};

    const severityVisual = {
        critical: { label: "Crítica", bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
        warning: { label: "Advertencia", bg: "#fffbeb", color: "#92400e", border: "#fde68a" },
        info: { label: "Informativa", bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
    };

    const statusVisual = {
        abierta: { label: "Abierta", bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
        en_proceso: { label: "En proceso", bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
        resuelta: { label: "Resuelta", bg: "#ecfdf5", color: "#047857", border: "#bbf7d0" },
        cancelada: { label: "Cancelada", bg: "#f8fafc", color: "#64748b", border: "#e2e8f0" },
    };

    if (loading) {
        return (
            <SectionCard
                title="Bitácora de intervención"
                subtitle="Cargando decisiones e intervenciones registradas para esta tarea crítica."
            >
                <div style={{ padding: 14, borderRadius: 16, background: "#f8fafc", border: "1px solid #e2e8f0", color: "#64748b", fontSize: 13, fontWeight: 800 }}>
                    Cargando bitácora de intervención...
                </div>
            </SectionCard>
        );
    }

    if (!items.length) {
        return null;
    }

    return (
        <SectionCard
            title="Bitácora de intervención"
            subtitle={`${Number(stats.total || items.length || 0)} intervención(es) · ${Number(stats.abiertas || 0)} abierta(s) · ${Number(stats.criticas || 0)} crítica(s)`}
            tone="blocked"
        >
            <div style={{ display: "grid", gap: 12 }}>
                {items.map((item) => {
                    const severity = severityVisual[item.severity] || severityVisual.info;
                    const status = statusVisual[item.status] || statusVisual.abierta;
                    const assignedLabel = item.assigned_to_name ? `Asignado a: ${item.assigned_to_name}` : "Registro informativo";
                    const createdByLabel = item.created_by_name ? `Registrado por: ${item.created_by_name}` : "Registrado por usuario";
                    const dueChange = item.new_due_date
                        ? `Fecha compromiso: ${formatDate(item.previous_due_date)} → ${formatDate(item.new_due_date)}`
                        : null;

                    return (
                        <div
                            key={item.id}
                            style={{
                                border: "1px solid #e2e8f0",
                                borderLeft: `4px solid ${severity.color}`,
                                borderRadius: 18,
                                background: "#ffffff",
                                boxShadow: "0 10px 24px rgba(15,23,42,.05)",
                                padding: 14,
                                display: "grid",
                                gap: 9,
                            }}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 14, fontWeight: 950, color: "#0f172a" }}>{item.action_label || item.title || item.action_type || "Intervención"}</div>
                                    <div style={{ marginTop: 4, fontSize: 12, color: "#64748b", fontWeight: 800 }}>
                                        {formatDateTime(item.created_at)} · {createdByLabel}
                                    </div>
                                </div>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                                    <Badge bg={severity.bg} color={severity.color} title="Severidad de la intervención">{item.severity_label || severity.label}</Badge>
                                    <Badge bg={status.bg} color={status.color} title="Estado de atención de la intervención">{item.status_label || status.label}</Badge>
                                </div>
                            </div>

                            {item.comment ? (
                                <div style={{ color: "#334155", fontSize: 13, lineHeight: 1.5, fontWeight: 750, whiteSpace: "pre-wrap" }}>
                                    {item.comment}
                                </div>
                            ) : null}

                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", color: "#64748b", fontSize: 11, fontWeight: 850 }}>
                                <span>{assignedLabel}</span>
                                {item.escalated_to_name ? <span>· Escalado a: {item.escalated_to_name}</span> : null}
                                {dueChange ? <span>· {dueChange}</span> : null}
                            </div>
                        </div>
                    );
                })}
            </div>
        </SectionCard>
    );
}


function getRoleTooltip(role) {
    const value = String(role || "").toLowerCase();
    if (value.includes("revisor")) return "Rol de revisión: valida evidencia, comentario y avance antes de decidir.";
    if (value.includes("responsable")) return "Rol responsable: registra avance, comentario y evidencia de ejecución.";
    return "Rol de consulta: revisa estado, trazabilidad y evidencias registradas.";
}

function getEvidenceVisual(ev = {}) {
    const type = String(ev.evidence_type || "").toLowerCase();
    const mime = String(ev.mime_type || "").toLowerCase();
    const context = String(ev.evidence_context || "").toLowerCase();

    const sourceLabel = context === "revision" ? "del revisor" : context === "avance" ? "del responsable" : "general";

    if (type === "link") return { icon: "🔗", label: `Link ${sourceLabel}` };
    if (type === "imagen" || mime.startsWith("image/")) return { icon: "📷", label: `Imagen ${sourceLabel}` };
    if (type === "archivo") return { icon: "📄", label: `Documento ${sourceLabel}` };
    if (type === "comentario") return { icon: "💬", label: `Comentario ${sourceLabel}` };
    return { icon: "📎", label: `Evidencia ${sourceLabel}` };
}


function formatGeoDistanceMeters(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    if (n >= 1000) return `${(n / 1000).toFixed(2)} km`;
    return `${n.toFixed(2)} m`;
}

function getGeoExceptionStatus(ev = {}) {
    return String(ev.geo_exception_status || "").toLowerCase();
}

function isGeoExceptionRequested(ev = {}) {
    return (
        String(ev.geo_validation_status || "").toLowerCase() === "out_of_range" &&
        getGeoExceptionStatus(ev) === "requested"
    );
}

function isGeoExceptionRejected(ev = {}) {
    return (
        String(ev.geo_validation_status || "").toLowerCase() === "out_of_range" &&
        getGeoExceptionStatus(ev) === "rejected"
    );
}

function isGeoExceptionBlockingApproval(ev = {}) {
    const geoStatus = String(ev.geo_validation_status || "").toLowerCase();
    const exceptionStatus = getGeoExceptionStatus(ev);

    if (geoStatus !== "out_of_range") return false;
    return !["approved"].includes(exceptionStatus);
}

function formatGeoExceptionReason(ev = {}) {
    return String(ev.geo_exception_reason || "").trim();
}

function getGeoValidationVisual(ev = {}) {
    const status = String(ev.geo_validation_status || "").toLowerCase();
    const exceptionStatus = getGeoExceptionStatus(ev);
    const distance = ev.geo_distance_meters;
    const radius = ev.geo_radius_meters;
    const hasDistance = distance !== null && distance !== undefined && distance !== "";
    const hasRadius = radius !== null && radius !== undefined && radius !== "";
    const distanceDetail = hasDistance && hasRadius
        ? `${formatGeoDistanceMeters(distance)} del punto autorizado · radio ${formatGeoDistanceMeters(radius)}`
        : "La evidencia cuenta con validación geográfica registrada.";

    if (status === "valid") {
        return {
            visible: true,
            icon: "✅",
            label: "Ubicación validada",
            detail: distanceDetail,
            bg: "#ecfdf5",
            border: "#a7f3d0",
            color: "#047857",
        };
    }

    if (status === "out_of_range") {
        if (exceptionStatus === "requested") {
            return {
                visible: true,
                icon: "⚠️",
                label: "Excepción geográfica solicitada",
                detail: distanceDetail,
                bg: "#ffffff",
                border: "#fdba74",
                color: "#9a3412",
            };
        }

        if (exceptionStatus === "rejected") {
            return {
                visible: true,
                icon: "⛔",
                label: "Excepción rechazada",
                detail: distanceDetail,
                bg: "#ffffff",
                border: "#fecaca",
                color: "#b91c1c",
            };
        }

        return {
            visible: true,
            icon: "⚠️",
            label: "Fuera de rango",
            detail: distanceDetail,
            bg: "#ffffff",
            border: "#fdba74",
            color: "#9a3412",
        };
    }

    if (status === "exception" || exceptionStatus === "approved") {
        return {
            visible: true,
            icon: "🟣",
            label: "Excepción autorizada",
            detail: distanceDetail,
            bg: "#f5f3ff",
            border: "#ddd6fe",
            color: "#6d28d9",
        };
    }

    if (status === "pending") {
        return {
            visible: true,
            icon: "⏳",
            label: "Validación pendiente",
            detail: "La evidencia tiene ubicación, pero aún no cuenta con validación de rango.",
            bg: "#f8fafc",
            border: "#e2e8f0",
            color: "#475569",
        };
    }

    if (status === "not_required") {
        return {
            visible: true,
            icon: "📍",
            label: "Ubicación registrada",
            detail: "La tarea no requería validación por radio.",
            bg: "#f8fafc",
            border: "#e2e8f0",
            color: "#475569",
        };
    }

    return { visible: false };
}

function getLogIcon(log = {}) {
    const action = String(log?.action_type || "").toLowerCase();
    const review = String(log?.review_status || "").toLowerCase();
    const hasEvidence = Array.isArray(log?.evidence_items) && log.evidence_items.length > 0;

    if (action === "enviado_revision") return "↑";
    if (action === "aprobado" || review === "aprobado") return "✓";
    if (action === "rechazado" || review === "rechazado") return "⛔";
    if (action === "solicitud_cambios" || action === "con_cambios" || review === "con_cambios") return "↩";
    if (action === "actualizacion_avance") return "↗";
    if (hasEvidence) return "📎";
    if (action === "comentario") return "💬";

    return "•";
}

function getLogCategory(log = {}) {
    const action = String(log?.action_type || "").toLowerCase();
    const hasEvidence = Array.isArray(log?.evidence_items) && log.evidence_items.length > 0;

    if (["aprobado", "rechazado", "solicitud_cambios", "con_cambios"].includes(action)) {
        return "decisiones";
    }

    if (hasEvidence || action === "enviado_revision") {
        return "evidencias";
    }

    if (action === "comentario") {
        return "comentarios";
    }

    return "operacion";
}

function logMatchesFilter(log, filter) {
    if (!filter || filter === "todos") return true;
    return getLogCategory(log) === filter;
}

function Badge({ children, bg = "#f3f4f6", color = "#374151", title = "" }) {
    return (
        <span
            title={title || ""}
            style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "5px 10px",
                borderRadius: 999,
                background: bg,
                color,
                fontSize: 12,
                fontWeight: 800,
                whiteSpace: "nowrap",
                cursor: title ? "help" : "default",
            }}
        >
            {children}
        </span>
    );
}

function InfoBox({ label, value }) {
    return (
        <div
            style={{
                border: "1px solid #e5e7eb",
                borderRadius: 14,
                padding: 14,
                background: "#fff",
            }}
        >
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6, fontWeight: 700 }}>{label}</div>
            <div style={{ fontSize: 14, color: "#111827", fontWeight: 800, whiteSpace: "pre-wrap" }}>{value || "—"}</div>
        </div>
    );
}

function SectionCard({ title, subtitle, children, tone = "default" }) {
    const isReview = tone === "review";
    const isCorrection = tone === "correction";
    const isBlocked = tone === "blocked";

    const accentColor = isBlocked
        ? "#ef4444"
        : isCorrection
            ? "#f97316"
            : isReview
                ? "#2563eb"
                : "#e5e7eb";

    return (
        <section
            style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderLeft: `4px solid ${accentColor}`,
                borderRadius: 18,
                padding: 20,
                boxShadow: "0 10px 28px rgba(15, 23, 42, 0.055)",
                marginBottom: 18,
            }}
        >
            <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 18, fontWeight: 900, color: "#111827" }}>{title}</div>
                {subtitle ? <div style={{ marginTop: 4, color: "#6b7280", fontSize: 13, lineHeight: 1.45 }}>{subtitle}</div> : null}
            </div>
            {children}
        </section>
    );
}


function ActionButton({ children, onClick, disabled = false, bg = "#111827", color = "#fff", border = "1px solid #111827", title = "" }) {
    return (
        <button
            type="button"
            title={title || ""}
            onClick={onClick}
            disabled={disabled}
            style={{
                border,
                background: disabled ? "#9ca3af" : bg,
                color,
                padding: "11px 15px",
                borderRadius: 12,
                cursor: disabled ? "not-allowed" : "pointer",
                fontWeight: 800,
                fontSize: 13,
            }}
        >
            {children}
        </button>
    );
}

function PlatformConfirmModal({ config, onCancel, onConfirm }) {
    if (!config) return null;

    const danger = config.tone === "danger";
    const confirmBg = danger ? "#b91c1c" : "#111827";
    const accent = danger ? "#ef4444" : "#2563eb";

    return (
        <div
            role="dialog"
            aria-modal="true"
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 100000,
                background: "rgba(15, 23, 42, 0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 18,
            }}
        >
            <div
                style={{
                    width: "min(560px, 100%)",
                    background: "#fff",
                    borderRadius: 22,
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 30px 90px rgba(15, 23, 42, 0.38)",
                    overflow: "hidden",
                }}
            >
                <div style={{ borderLeft: `8px solid ${accent}`, padding: "20px 22px 14px" }}>
                    <div style={{ fontSize: 12, fontWeight: 950, color: "#2563eb", textTransform: "uppercase", letterSpacing: ".04em" }}>
                        {config.kicker || "Plataforma PM"}
                    </div>
                    <div style={{ marginTop: 6, fontSize: 20, fontWeight: 950, color: "#0f172a" }}>
                        {config.title || "Confirmar acción"}
                    </div>
                    {config.message ? (
                        <div style={{ marginTop: 8, fontSize: 14, color: "#475569", lineHeight: 1.55 }}>
                            {config.message}
                        </div>
                    ) : null}
                    {Array.isArray(config.details) && config.details.length ? (
                        <div
                            style={{
                                marginTop: 14,
                                display: "grid",
                                gap: 8,
                                padding: 12,
                                borderRadius: 14,
                                background: "#ffffff",
                                border: danger ? "1px solid #fecaca" : "1px solid #bfdbfe",
                            }}
                        >
                            {config.details.map((item, idx) => (
                                <div key={idx} style={{ fontSize: 13, color: danger ? "#991b1b" : "#1e3a8a", lineHeight: 1.45 }}>
                                    <strong>{item.label}:</strong> {item.value}
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>

                <div
                    style={{
                        padding: "14px 22px 20px",
                        display: "flex",
                        gap: 10,
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                        background: "#f8fafc",
                        borderTop: "1px solid #e5e7eb",
                    }}
                >
                    <button
                        type="button"
                        onClick={onCancel}
                        style={{
                            border: "1px solid #d1d5db",
                            background: "#fff",
                            color: "#111827",
                            borderRadius: 12,
                            padding: "11px 15px",
                            fontWeight: 900,
                            cursor: "pointer",
                        }}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        style={{
                            border: `1px solid ${confirmBg}`,
                            background: confirmBg,
                            color: "#fff",
                            borderRadius: 12,
                            padding: "11px 15px",
                            fontWeight: 950,
                            cursor: "pointer",
                        }}
                    >
                        {config.confirmLabel || "Confirmar"}
                    </button>
                </div>
            </div>
        </div>
    );
}


const INTERVENTION_ACTION_CONFIG = {
    document_block: {
        endpointType: "document_block",
        title: "Documentar causa del bloqueo",
        kicker: "Intervención del PM",
        submitLabel: "Guardar causa",
        helper: "Registra la causa del bloqueo y su posible impacto. No desbloquea la tarea; deja trazabilidad para que dirección decida.",
        placeholder: "Ej. La gestión continúa detenida por dependencia de permisos técnicos, ambientales e institucionales...",
        defaultComment: "La tarea permanece bloqueada porque ",
        severity: "critical",
        requiresDate: false,
    },
    request_evidence: {
        endpointType: "request_evidence",
        title: "Solicitar evidencia",
        kicker: "Intervención del PM",
        submitLabel: "Solicitar evidencia",
        helper: "Solicita soporte documental al responsable. Ayuda a cerrar la brecha de trazabilidad sin modificar avance ni estado.",
        placeholder: "Ej. Se solicita cargar evidencia documental de los permisos técnicos, ambientales e institucionales requeridos para continuar.",
        defaultComment: "Se solicita cargar evidencia documental de ",
        severity: "critical",
        assignToOwner: true,
        requiresDate: false,
    },
    reschedule: {
        endpointType: "reschedule_due_date",
        title: "Reprogramar fecha compromiso",
        kicker: "Decisión del PM",
        submitLabel: "Guardar reprogramación",
        helper: "Actualiza la fecha compromiso con justificación trazable. Esta decisión puede impactar dependencias, entregables y lectura ejecutiva.",
        placeholder: "Ej. Se reprograma por dependencia externa de permisos ambientales.",
        defaultComment: "Se reprograma la fecha compromiso porque ",
        severity: "critical",
        requiresDate: true,
    },
    exception: {
        endpointType: "register_exception",
        title: "Registrar excepción operativa",
        kicker: "Decisión del PM",
        submitLabel: "Guardar excepción",
        helper: "Documenta una excepción temporal por bloqueo, vencimiento o falta de evidencia. No oculta la condición; la deja visible y justificada.",
        placeholder: "Ej. Se registra excepción operativa temporal porque el permiso ambiental depende de validación externa.",
        defaultComment: "Se registra excepción operativa temporal porque ",
        severity: "warning",
        requiresDate: false,
    },
    resolve_block: {
        endpointType: "resolve_block",
        title: "Marcar bloqueo resuelto",
        kicker: "Liberación del PM",
        submitLabel: "Liberar bloqueo",
        helper: "Confirma que el impedimento fue atendido. La tarea volverá a ejecución para que el responsable continúe registrando avance y evidencia.",
        placeholder: "Ej. Se confirma que la dependencia institucional fue atendida y la tarea puede continuar en ejecución.",
        defaultComment: "Se confirma que el bloqueo fue atendido porque ",
        severity: "critical",
        requiresDate: false,
        reloadDetail: true,
    },
    convert_risk: {
        endpointType: "convert_to_risk",
        title: "Convertir en riesgo",
        kicker: "Decisión del PM",
        submitLabel: "Crear riesgo",
        helper: "Crea un riesgo formal en el proyecto a partir del bloqueo actual. Úsalo cuando el impedimento pueda afectar alcance, fechas, entregables o continuidad.",
        placeholder: "Ej. El retraso en permisos técnicos y ambientales puede afectar el inicio de intervención y el cumplimiento de fechas del proyecto.",
        defaultComment: "Se convierte este bloqueo en riesgo porque ",
        severity: "critical",
        requiresDate: false,
        reloadDetail: false,
    },
};

function TaskInterventionActionModal({ actionKey, task, form, onChange, onCancel, onSubmit, saving = false }) {
    if (!actionKey) return null;
    const config = INTERVENTION_ACTION_CONFIG[actionKey];
    if (!config) return null;

    const dueDateLabel = task?.due_date ? formatDate(task.due_date) : "Sin fecha compromiso";

    return (
        <div
            role="dialog"
            aria-modal="true"
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 100002,
                background: "rgba(15, 23, 42, 0.48)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 18,
            }}
        >
            <div
                style={{
                    width: "min(680px, 100%)",
                    background: "#fff",
                    borderRadius: 24,
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 30px 90px rgba(15, 23, 42, 0.38)",
                    overflow: "hidden",
                }}
            >
                <div style={{ borderLeft: `8px solid ${config.severity === "critical" ? "#ef4444" : "#f59e0b"}`, padding: "20px 22px 16px" }}>
                    <div style={{ fontSize: 11, fontWeight: 950, color: config.severity === "critical" ? "#b91c1c" : "#92400e", textTransform: "uppercase", letterSpacing: ".1em" }}>
                        {config.kicker}
                    </div>
                    <div style={{ marginTop: 5, fontSize: 22, fontWeight: 950, color: "#0f172a", letterSpacing: "-.03em" }}>
                        {config.title}
                    </div>
                    <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.55, color: "#475569", fontWeight: 750 }}>
                        {config.helper}
                    </div>
                    <div style={{ marginTop: 12, padding: 12, borderRadius: 16, background: "#f8fafc", border: "1px solid #e2e8f0", display: "grid", gap: 4 }}>
                        <div style={{ fontSize: 12, fontWeight: 950, color: "#334155" }}>{task?.wbs_code || "Tarea"} · {task?.name || "Sin nombre"}</div>
                        <div style={{ fontSize: 12, fontWeight: 750, color: "#64748b" }}>Estado: {getFriendlyStatusLabel(task?.status)} · Avance: {Number(task?.progress_percent || 0).toFixed(0)}% · Fecha actual: {dueDateLabel}</div>
                    </div>
                </div>

                <div style={{ padding: "0 22px 22px", display: "grid", gap: 14 }}>
                    {config.requiresDate ? (
                        <label style={{ display: "grid", gap: 6 }}>
                            <span style={{ fontSize: 12, fontWeight: 950, color: "#334155" }}>Nueva fecha compromiso</span>
                            <input
                                type="date"
                                value={form.new_due_date || ""}
                                onChange={(event) => onChange({ ...form, new_due_date: event.target.value })}
                                style={{ border: "1px solid #cbd5e1", borderRadius: 14, padding: "12px 13px", fontSize: 14, fontWeight: 800, color: "#0f172a" }}
                            />
                        </label>
                    ) : null}

                    <label style={{ display: "grid", gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 950, color: "#334155" }}>Comentario / justificación</span>
                        <textarea
                            rows={6}
                            value={form.comment || ""}
                            onChange={(event) => onChange({ ...form, comment: event.target.value })}
                            placeholder={config.placeholder}
                            style={{ border: "1px solid #cbd5e1", borderRadius: 16, padding: 13, fontSize: 14, lineHeight: 1.5, color: "#0f172a", resize: "vertical", fontWeight: 750 }}
                        />
                    </label>

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap", paddingTop: 2 }}>
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={saving}
                            style={{ border: "1px solid #cbd5e1", background: "#fff", color: "#334155", padding: "11px 15px", borderRadius: 12, cursor: saving ? "not-allowed" : "pointer", fontWeight: 850, fontSize: 13 }}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={onSubmit}
                            disabled={saving}
                            style={{ border: "1px solid #111827", background: saving ? "#94a3b8" : "#111827", color: "#fff", padding: "11px 16px", borderRadius: 12, cursor: saving ? "not-allowed" : "pointer", fontWeight: 900, fontSize: 13 }}
                        >
                            {saving ? "Guardando..." : config.submitLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function TraceTabButton({ active, icon, title, subtitle, onClick, tooltip = "" }) {
    return (
        <button
            type="button"
            title={tooltip || subtitle || title}
            onClick={onClick}
            style={{
                flex: "1 1 260px",
                textAlign: "left",
                padding: 16,
                borderRadius: 16,
                border: active ? "1px solid #2563eb" : "1px solid #e5e7eb",
                background: active ? "#eff6ff" : "#fff",
                boxShadow: active
                    ? "0 0 0 3px #dbeafe"
                    : "0 8px 20px rgba(15, 23, 42, 0.04)",
                cursor: "pointer",
                transition: "all 0.2s ease",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                    style={{
                        width: 38,
                        height: 38,
                        borderRadius: 12,
                        background: active ? "#2563eb" : "#f3f4f6",
                        color: active ? "#fff" : "#374151",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                        flexShrink: 0,
                    }}
                >
                    {icon}
                </div>

                <div>
                    <div style={{ fontSize: 15, fontWeight: 900, color: "#111827" }}>
                        {title}
                    </div>
                    <div style={{ marginTop: 3, fontSize: 12, color: "#6b7280" }}>
                        {subtitle}
                    </div>
                </div>
            </div>
        </button>
    );
}

function EvidenceFilterButton({ active, children, onClick, tooltip = "" }) {
    return (
        <button
            type="button"
            title={tooltip || (typeof children === "string" ? children : "")}
            onClick={onClick}
            style={{
                padding: "8px 12px",
                borderRadius: 999,
                border: active ? "1px solid #2563eb" : "1px solid #d1d5db",
                background: active ? "#eff6ff" : "#fff",
                color: active ? "#1d4ed8" : "#374151",
                fontSize: 12,
                fontWeight: 800,
                cursor: "pointer",
            }}
        >
            {children}
        </button>
    );
}

function RequirementItem({ ok, label, detail }) {
    const iconColor = ok ? "#16a34a" : "#f59e0b";

    return (
        <div
            style={{
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
                padding: 10,
                borderRadius: 12,
                background: "#ffffff",
                border: "1px solid #e5e7eb",
            }}
        >
            <div
                style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: iconColor,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 900,
                    flexShrink: 0,
                }}
            >
                {ok ? "✓" : "!"}
            </div>

            <div>
                <div
                    style={{
                        fontSize: 13,
                        fontWeight: 900,
                        color: "#111827",
                    }}
                >
                    {label}
                </div>

                {detail ? (
                    <div
                        style={{
                            marginTop: 3,
                            fontSize: 12,
                            color: "#64748b",
                            lineHeight: 1.4,
                        }}
                    >
                        {detail}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function CompactEvidenceList({ items = [], canDecideException = false, onDecideException = null, decidingExceptionId = null }) {
    const list = Array.isArray(items) ? items : [];

    if (list.length === 0) {
        return (
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
                Sin evidencia asociada.
            </div>
        );
    }

    return (
        <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
            {list.map((ev) => {
                const hasGeo =
                    Number(ev.has_geo || 0) === 1 ||
                    (ev.latitude && ev.longitude);

                const mapsUrl = hasGeo
                    ? `https://www.google.com/maps?q=${ev.latitude},${ev.longitude}`
                    : "";

                const visual = getEvidenceVisual(ev);

                return (
                    <div
                        key={ev.id}
                        style={{
                            padding: 12,
                            borderRadius: 14,
                            background: "#ffffff",
                            border: "1px solid #e5e7eb",
                            boxShadow: "0 6px 18px rgba(15,23,42,.035)",
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                                <div
                                    style={{
                                        width: 34,
                                        height: 34,
                                        borderRadius: 12,
                                        background: "#eff6ff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                    }}
                                >
                                    {visual.icon}
                                </div>

                                <div>
                                    <div style={{ fontSize: 13, fontWeight: 900, color: "#111827" }}>
                                        {visual.label}
                                    </div>
                                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                                        {ev.uploaded_by_name || "Usuario"}{ev.created_at ? ` · ${formatDateTime(ev.created_at)}` : ""}
                                    </div>
                                </div>
                            </div>

                            {hasGeo ? (() => {
                                const geoVisual = getGeoValidationVisual(ev);
                                return (
                                    <Badge
                                        bg={geoVisual.visible ? geoVisual.bg : "#ecfdf5"}
                                        color={geoVisual.visible ? geoVisual.color : "#047857"}
                                        title={geoVisual.visible ? geoVisual.detail : "La evidencia tiene ubicación capturada."}
                                    >
                                        {geoVisual.visible ? `${geoVisual.icon} ${geoVisual.label}` : "📍 Ubicación registrada"}
                                    </Badge>
                                );
                            })() : null}
                        </div>

                        <div style={{ fontSize: 13, fontWeight: 800, color: "#111827", marginTop: 10 }}>
                            {ev.title || ev.file_name || "Evidencia"}
                        </div>

                        {ev.description ? (
                            <div
                                style={{
                                    fontSize: 12,
                                    color: "#374151",
                                    marginTop: 6,
                                    whiteSpace: "pre-wrap",
                                    lineHeight: 1.45,
                                }}
                            >
                                {ev.description}
                            </div>
                        ) : null}

                        {ev.external_url ? (
                            <div
                                title="Link registrado como evidencia del avance o revisión."
                                style={{
                                    marginTop: 10,
                                    padding: "9px 10px",
                                    borderRadius: 12,
                                    background: "#eff6ff",
                                    border: "1px solid #bfdbfe",
                                    color: "#1d4ed8",
                                    fontSize: 12,
                                    fontWeight: 800,
                                    wordBreak: "break-all",
                                }}
                            >
                                🔗 {ev.external_url}
                            </div>
                        ) : null}

                        {hasGeo ? (() => {
                            const geoVisual = getGeoValidationVisual(ev);
                            const boxBg = geoVisual.visible ? geoVisual.bg : "#ecfdf5";
                            const boxBorder = geoVisual.visible ? geoVisual.border : "#a7f3d0";
                            const boxColor = geoVisual.visible ? geoVisual.color : "#047857";
                            const reason = formatGeoExceptionReason(ev);
                            const requested = isGeoExceptionRequested(ev);

                            return (
                                <div
                                    style={{
                                        marginTop: 10,
                                        padding: 10,
                                        borderRadius: 12,
                                        background: boxBg,
                                        border: `1px solid ${boxBorder}`,
                                        color: boxColor,
                                        fontSize: 12,
                                        lineHeight: 1.45,
                                    }}
                                >
                                    <div style={{ fontWeight: 950, marginBottom: 4 }}>
                                        {geoVisual.visible ? `${geoVisual.icon} ${geoVisual.label}` : "📍 Ubicación registrada"}
                                    </div>
                                    {geoVisual.visible ? <div style={{ marginBottom: 5 }}>{geoVisual.detail}</div> : null}
                                    {reason ? (
                                        <div style={{ marginTop: 5 }}>
                                            <strong>Motivo:</strong> {reason}
                                        </div>
                                    ) : null}
                                    {requested && canDecideException && typeof onDecideException === "function" ? (
                                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                                            <button
                                                type="button"
                                                disabled={decidingExceptionId === ev.id}
                                                onClick={() => onDecideException(ev, "approve")}
                                                style={{
                                                    border: "1px solid #16a34a",
                                                    background: "#16a34a",
                                                    color: "#fff",
                                                    borderRadius: 10,
                                                    padding: "7px 10px",
                                                    fontSize: 11,
                                                    fontWeight: 950,
                                                    cursor: decidingExceptionId === ev.id ? "wait" : "pointer",
                                                }}
                                            >
                                                Autorizar excepción
                                            </button>
                                            <button
                                                type="button"
                                                disabled={decidingExceptionId === ev.id}
                                                onClick={() => onDecideException(ev, "reject")}
                                                style={{
                                                    border: "1px solid #fecaca",
                                                    background: "#fff",
                                                    color: "#b91c1c",
                                                    borderRadius: 10,
                                                    padding: "7px 10px",
                                                    fontSize: 11,
                                                    fontWeight: 950,
                                                    cursor: decidingExceptionId === ev.id ? "wait" : "pointer",
                                                }}
                                            >
                                                Rechazar excepción
                                            </button>
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })() : null}

                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
                            {ev.file_url ? (
                                <a
                                    href={ev.file_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: "#2563eb", fontSize: 12, fontWeight: 900 }}
                                >
                                    Abrir archivo
                                </a>
                            ) : null}

                            {ev.external_url ? (
                                <a
                                    href={ev.external_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: "#2563eb", fontSize: 12, fontWeight: 900 }}
                                >
                                    Abrir enlace
                                </a>
                            ) : null}

                            {hasGeo ? (
                                <a
                                    href={mapsUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: "#047857", fontSize: 12, fontWeight: 900 }}
                                >
                                    Ver ubicación
                                </a>
                            ) : null}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function SubmittedEvidencePanel({ title = "Evidencia enviada por el responsable", items = [], canDecideException = false, onDecideException = null, decidingExceptionId = null }) {
    const list = Array.isArray(items) ? items : [];

    return (
        <div
            style={{
                marginTop: 12,
                padding: 12,
                borderRadius: 14,
                background: "#ffffff",
                border: "1px solid #e2e8f0",
            }}
        >
            <div
                title="Evidencia vinculada al avance consultado. El revisor debe poder verla antes de tomar una decisión."
                style={{
                    fontSize: 12,
                    fontWeight: 950,
                    color: "#334155",
                    marginBottom: 6,
                    cursor: "help",
                }}
            >
                {title}
            </div>
            {list.length ? (
                <CompactEvidenceList items={list} canDecideException={canDecideException} onDecideException={onDecideException} decidingExceptionId={decidingExceptionId} />
            ) : (
                <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.45 }}>
                    No hay evidencia vinculada al último avance. Revisa el repositorio de evidencias si el archivo fue cargado sin quedar asociado al ciclo.
                </div>
            )}
        </div>
    );
}

function clampPercent(value) {
    const n = Number(value || 0);
    if (Number.isNaN(n)) return 0;
    return Math.max(0, Math.min(100, Math.round(n)));
}

function getProgressHealthStyle(progress) {
    const value = clampPercent(progress);

    if (value < 30) {
        return {
            label: "Bajo avance",
            bg: "#ffffff",
            color: "#b91c1c",
            border: "#fecaca",
            bar: "#ef4444",
        };
    }

    if (value < 70) {
        return {
            label: "Avance medio",
            bg: "#ffffff",
            color: "#92400e",
            border: "#fde68a",
            bar: "#f59e0b",
        };
    }

    return {
        label: "Avance alto",
        bg: "#ecfdf5",
        color: "#047857",
        border: "#a7f3d0",
        bar: "#10b981",
    };
}

function getTimeHealth(task) {
    if (!task?.due_date) {
        return {
            label: "Sin fecha objetivo",
            detail: "La tarea no tiene fecha compromiso definida.",
            bg: "#f8fafc",
            color: "#475569",
            border: "#e2e8f0",
        };
    }

    const status = String(task?.status || "").toLowerCase();
    if (status === "terminada") {
        return {
            label: "Cerrada",
            detail: "La tarea ya fue terminada.",
            bg: "#ecfdf5",
            color: "#047857",
            border: "#a7f3d0",
        };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = parseLocalDateOnly(task.due_date);
    if (!due) {
        return {
            label: "Fecha no válida",
            detail: "La fecha compromiso no pudo interpretarse correctamente.",
            bg: "#fff7ed",
            color: "#9a3412",
            border: "#fed7aa",
        };
    }
    due.setHours(0, 0, 0, 0);
    const diffDays = Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
        const days = Math.abs(diffDays);
        return {
            label: `Vencida hace ${days} ${days === 1 ? "día" : "días"}`,
            detail: "Requiere atención operativa o decisión PMO.",
            bg: "#ffffff",
            color: "#b91c1c",
            border: "#fecaca",
        };
    }

    if (diffDays === 0) {
        return {
            label: "Vence hoy",
            detail: "Debe atenderse durante la jornada actual.",
            bg: "#ffffff",
            color: "#92400e",
            border: "#fde68a",
        };
    }

    return {
        label: `${diffDays} días restantes`,
        detail: "Fecha objetivo vigente.",
        bg: "#eff6ff",
        color: "#1d4ed8",
        border: "#bfdbfe",
    };
}

function getRouteHealth(task, dependencyStatus) {
    const blocked =
        Number(task?.is_blocked || 0) === 1 ||
        Boolean(dependencyStatus?.is_blocked) ||
        String(task?.status || "").toLowerCase() === "bloqueada";

    if (blocked) {
        return {
            label: "Ruta bloqueada",
            detail: dependencyStatus?.reason || task?.blocked_reason || "La tarea no puede avanzar hasta resolver el bloqueo.",
            bg: "#ffffff",
            color: "#b91c1c",
            border: "#fecaca",
            icon: "🔒",
        };
    }

    return {
        label: "Ruta liberada",
        detail: "No se detecta bloqueo operativo activo.",
        bg: "#ecfdf5",
        color: "#047857",
        border: "#a7f3d0",
        icon: "🔓",
    };
}

function ProgressHealthBar({ value }) {
    const percent = clampPercent(value);
    const health = getProgressHealthStyle(percent);

    return (
        <div title={`Avance operativo actual: ${percent}% · ${health.label}`} style={{ cursor: "help" }}>
            <div
                style={{
                    height: 10,
                    width: "100%",
                    borderRadius: 999,
                    background: "#e5e7eb",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        height: "100%",
                        width: `${percent}%`,
                        background: health.bar,
                        borderRadius: 999,
                        transition: "width .2s ease",
                    }}
                />
            </div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 6,
                    fontSize: 11,
                    color: "#64748b",
                    fontWeight: 800,
                }}
            >
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
                <span>75%</span>
                <span>100%</span>
            </div>
        </div>
    );
}

function HealthMetricCard({ icon, label, value, detail, bg, color, border, title }) {
    return (
        <div
            title={title || detail || value || label}
            style={{
                padding: 12,
                borderRadius: 16,
                background: "#ffffff",
                border: `1px solid ${border || "#e5e7eb"}`,
                minHeight: 74,
                cursor: "help",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 15 }}>{icon}</span>
                <span style={{ fontSize: 11, color: color || "#475569", fontWeight: 950, textTransform: "uppercase", letterSpacing: ".02em" }}>
                    {label}
                </span>
            </div>
            <div style={{ fontSize: 14, color: color || "#111827", fontWeight: 950 }}>{value}</div>
            {detail ? <div style={{ marginTop: 3, fontSize: 11, color: "#64748b", lineHeight: 1.35 }}>{detail}</div> : null}
        </div>
    );
}

function CognitiveActionPanel({ focus, onPrimaryAction }) {
    if (!focus) return null;

    const toneMap = {
        review: { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8", iconBg: "#dbeafe" },
        correction: { bg: "#ffffff", border: "#fed7aa", color: "#9a3412", iconBg: "#ffffff" },
        warning: { bg: "#ffffff", border: "#fde68a", color: "#92400e", iconBg: "#ffffff" },
        danger: { bg: "#ffffff", border: "#fecaca", color: "#b91c1c", iconBg: "#ffffff" },
        success: { bg: "#ecfdf5", border: "#a7f3d0", color: "#047857", iconBg: "#d1fae5" },
        neutral: { bg: "#ffffff", border: "#e2e8f0", color: "#475569", iconBg: "#f8fafc" },
    };

    const tone = toneMap[focus.tone] || toneMap.neutral;

    return (
        <section
            style={{
                marginBottom: 18,
                padding: 18,
                borderRadius: 22,
                background: tone.bg,
                border: `1px solid ${tone.border}`,
                borderLeft: `5px solid ${tone.color}`,
                boxShadow: "0 12px 32px rgba(15, 23, 42, 0.055)",
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, minWidth: 0, flex: "1 1 420px" }}>
                    <div
                        style={{
                            width: 42,
                            height: 42,
                            borderRadius: 16,
                            background: tone.iconBg,
                            color: tone.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 20,
                            fontWeight: 950,
                            flexShrink: 0,
                        }}
                    >
                        {focus.icon}
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 950, color: tone.color, textTransform: "uppercase", letterSpacing: ".05em" }}>
                            {focus.kicker || "Acción requerida"}
                        </div>
                        <div style={{ marginTop: 4, fontSize: 18, fontWeight: 950, color: "#0f172a" }}>
                            {focus.title}
                        </div>
                        <div style={{ marginTop: 5, fontSize: 13, color: "#475569", lineHeight: 1.5, maxWidth: 820 }}>
                            {focus.detail}
                        </div>
                    </div>
                </div>

                {focus.showPrimaryAction ? (
                    <button
                        type="button"
                        onClick={onPrimaryAction}
                        style={{
                            border: `1px solid ${tone.color}`,
                            background: focus.tone === "danger" ? "#fff" : tone.color,
                            color: focus.tone === "danger" ? tone.color : "#fff",
                            borderRadius: 14,
                            padding: "11px 15px",
                            fontSize: 13,
                            fontWeight: 950,
                            cursor: "pointer",
                            boxShadow: focus.tone === "danger" ? "none" : "0 8px 18px rgba(15, 23, 42, 0.12)",
                        }}
                    >
                        {focus.primaryLabel || "Ir a acción"}
                    </button>
                ) : null}
            </div>
        </section>
    );
}

function getCycleDeltaText(sentLog) {
    const oldP = Number(sentLog?.old_progress_percent ?? NaN);
    const newP = Number(sentLog?.new_progress_percent ?? NaN);

    if (Number.isNaN(oldP) || Number.isNaN(newP) || oldP === newP) return "";

    const diff = Math.round((newP - oldP) * 100) / 100;
    return diff > 0 ? `+${diff}% respecto al ciclo anterior` : `${diff}% respecto al ciclo anterior`;
}

function getTimelineResultStyle(reviewLog) {
    const action = String(reviewLog?.action_type || "").toLowerCase();
    const review = String(reviewLog?.review_status || "").toLowerCase();

    if (!reviewLog) return { border: "#f59e0b", bg: "#ffffff", color: "#92400e", icon: "⏳" };
    if (action === "aprobado" || review === "aprobado") return { border: "#10b981", bg: "#ecfdf5", color: "#047857", icon: "✅" };
    if (action === "rechazado" || review === "rechazado") return { border: "#ef4444", bg: "#ffffff", color: "#b91c1c", icon: "⛔" };
    if (action === "solicitud_cambios" || action === "con_cambios" || review === "con_cambios") return { border: "#f97316", bg: "#ffffff", color: "#9a3412", icon: "↩️" };

    return { border: "#94a3b8", bg: "#f8fafc", color: "#475569", icon: "•" };
}

function TimelineNode({ icon, title, meta, children, color = "#2563eb" }) {
    return (
        <div style={{ display: "grid", gridTemplateColumns: "32px 1fr", gap: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div
                    style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        background: "#fff",
                        border: `2px solid ${color}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 14,
                    }}
                >
                    {icon}
                </div>
                <div style={{ flex: 1, width: 2, background: "#e5e7eb", marginTop: 6 }} />
            </div>
            <div style={{ paddingBottom: 18 }}>
                <div style={{ fontSize: 14, fontWeight: 950, color: "#111827" }}>{title}</div>
                {meta ? <div style={{ marginTop: 3, fontSize: 12, color: "#64748b" }}>{meta}</div> : null}
                {children ? <div style={{ marginTop: 10 }}>{children}</div> : null}
            </div>
        </div>
    );
}


function getStorylineToneStyle(tone = "default") {
    const styles = {
        default: { color: "#2563eb", border: "#bfdbfe", label: "#1d4ed8" },
        review: { color: "#7c3aed", border: "#ddd6fe", label: "#6d28d9" },
        decision: { color: "#047857", border: "#a7f3d0", label: "#047857" },
        warning: { color: "#9a3412", border: "#fdba74", label: "#9a3412" },
        danger: { color: "#b91c1c", border: "#fecaca", label: "#b91c1c" },
        neutral: { color: "#475569", border: "#cbd5e1", label: "#334155" },
    };

    return styles[tone] || styles.default;
}

function getStorylineEvidenceUrl(ev = {}) {
    if (ev.file_url) return ev.file_url;
    if (ev.external_url) return ev.external_url;

    const hasGeo =
        Number(ev.has_geo || 0) === 1 ||
        (ev.latitude && ev.longitude);

    if (hasGeo) return `https://www.google.com/maps?q=${ev.latitude},${ev.longitude}`;

    return "";
}

function StorylineEvidenceChips({ items = [] }) {
    const list = Array.isArray(items) ? items : [];

    if (!list.length) return null;

    return (
        <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 950, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 }}>
                Evidencia vinculada
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {list.map((ev) => {
                    const visual = getEvidenceVisual(ev);
                    const geoVisual = getGeoValidationVisual(ev);
                    const url = getStorylineEvidenceUrl(ev);
                    const label = ev.title || ev.file_name || visual.label || "Evidencia";
                    const chipTitle = geoVisual.visible
                        ? `${visual.label} · ${geoVisual.label}`
                        : visual.label;

                    const chipStyle = {
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 7,
                        maxWidth: 260,
                        padding: "8px 10px",
                        borderRadius: 12,
                        border: `1px solid ${geoVisual.visible ? geoVisual.border : "#cbd5e1"}`,
                        background: "#ffffff",
                        color: geoVisual.visible ? geoVisual.color : "#334155",
                        fontSize: 12,
                        fontWeight: 900,
                        textDecoration: "none",
                        cursor: url ? "pointer" : "default",
                    };

                    const content = (
                        <>
                            <span>{geoVisual.visible ? geoVisual.icon : visual.icon}</span>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
                        </>
                    );

                    if (url) {
                        return (
                            <a key={ev.id || `${label}-${Math.random()}`} href={url} target="_blank" rel="noreferrer" title={chipTitle} style={chipStyle}>
                                {content}
                            </a>
                        );
                    }

                    return (
                        <span key={ev.id || `${label}-${Math.random()}`} title={chipTitle} style={chipStyle}>
                            {content}
                        </span>
                    );
                })}
            </div>
        </div>
    );
}

function TimelineReadableRow({ icon, title, meta, children, index = 0, tone = "default", badge = "", evidenceItems = [] }) {
    const style = getStorylineToneStyle(tone);
    const isLast = false;

    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "42px 1fr",
                gap: 12,
                position: "relative",
            }}
        >
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div
                    style={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        background: "#ffffff",
                        border: `2px solid ${style.color}`,
                        color: style.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 13,
                        fontWeight: 950,
                        boxShadow: "0 8px 18px rgba(15,23,42,.06)",
                        zIndex: 2,
                    }}
                >
                    {icon}
                </div>
                {!isLast ? (
                    <div
                        style={{
                            flex: 1,
                            width: 2,
                            minHeight: 22,
                            background: index === 0 ? style.color : "#e2e8f0",
                            marginTop: 6,
                        }}
                    />
                ) : null}
            </div>

            <div
                style={{
                    marginBottom: 12,
                    border: `1px solid ${style.border}`,
                    borderLeft: `4px solid ${style.color}`,
                    borderRadius: 18,
                    background: "#ffffff",
                    boxShadow: "0 12px 28px rgba(15,23,42,.055)",
                    overflow: "hidden",
                }}
            >
                <div style={{ padding: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
                        <div style={{ minWidth: 0 }}>
                            {badge ? (
                                <div style={{ marginBottom: 8 }}>
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            padding: "5px 9px",
                                            borderRadius: 999,
                                            border: `1px solid ${style.border}`,
                                            background: "#fff",
                                            color: style.label,
                                            fontSize: 11,
                                            fontWeight: 950,
                                            textTransform: "uppercase",
                                            letterSpacing: ".035em",
                                        }}
                                    >
                                        {badge}
                                    </span>
                                </div>
                            ) : null}
                            <div style={{ fontSize: 14, fontWeight: 950, color: "#0f172a", lineHeight: 1.25 }}>{title}</div>
                            {meta ? <div style={{ marginTop: 4, fontSize: 12, color: "#64748b", fontWeight: 800 }}>{meta}</div> : null}
                        </div>
                    </div>

                    {children ? <div style={{ marginTop: 10 }}>{children}</div> : null}
                    <StorylineEvidenceChips items={evidenceItems} />
                </div>
            </div>
        </div>
    );
}

function ReviewCycleCard({ cycle, index, total }) {
    const sentLog = cycle?.sentLog || null;
    const reviewLog = cycle?.reviewLog || null;
    const otherLogs = Array.isArray(cycle?.otherLogs) ? cycle.otherLogs : [];

    const result = getCycleResultText(reviewLog);

    const reviewProgress =
        reviewLog?.new_progress_percent ||
        reviewLog?.old_progress_percent ||
        "";

    const sentProgress =
        sentLog?.new_progress_percent ||
        sentLog?.old_progress_percent ||
        reviewProgress ||
        "—";

    const cycleNumber = total - index;
    const isCurrentCycle = index === 0;

    const previousProgress = Number(sentLog?.old_progress_percent ?? 0);
    const currentProgress = Number(sentLog?.new_progress_percent ?? reviewLog?.new_progress_percent ?? previousProgress);
    const delta = currentProgress - previousProgress;
    const deltaLabel = delta > 0 ? `+${delta.toFixed(0)}% respecto al ciclo anterior` : "Sin variación de avance";

    const isFinal =
        reviewLog &&
        String(reviewLog?.action_type || "").toLowerCase() === "aprobado" &&
        (
            String(reviewLog?.new_status || "").toLowerCase() === "terminada" ||
            Number(reviewLog?.new_progress_percent || 0) >= 100
        );

    const title = isFinal
        ? `Ciclo ${cycleNumber} · Cierre de tarea · 100%`
        : `Ciclo ${cycleNumber} · Avance ${sentProgress}%`;

    let rowIndex = 0;
    const sentEvidence = Array.isArray(sentLog?.evidence_items) ? sentLog.evidence_items : [];
    const reviewEvidence = Array.isArray(reviewLog?.evidence_items) ? reviewLog.evidence_items : [];

    const reviewAction = String(reviewLog?.action_type || "").toLowerCase();
    const reviewTone = reviewAction === "rechazado"
        ? "danger"
        : reviewAction === "solicitud_cambios" || reviewAction === "con_cambios"
            ? "warning"
            : reviewLog
                ? "decision"
                : "warning";

    const content = (
        <div
            style={{
                padding: "18px 16px 8px",
                background: "linear-gradient(90deg, #f8fafc 0%, #ffffff 42%, #ffffff 100%)",
            }}
        >
            <TimelineReadableRow
                index={rowIndex++}
                icon="↑"
                badge="Avance presentado"
                title={sentLog ? `Responsable envió avance al ${sentProgress}%` : "Sin envío asociado"}
                meta={sentLog ? `${formatDateTime(sentLog.created_at)} · ${sentLog.user_name || "Responsable"}` : "Este ciclo no tiene envío a revisión asociado."}
                tone="default"
                evidenceItems={sentEvidence}
            >
                {sentLog ? (
                    <>
                        {getLogSummary(sentLog) ? (
                            <div style={{ fontSize: 13, color: "#334155", fontWeight: 900, marginBottom: 7 }}>
                                {getLogSummary(sentLog)}
                            </div>
                        ) : null}
                        <div style={{ fontSize: 13, color: "#111827", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                            {getLogComment(sentLog)}
                        </div>
                    </>
                ) : null}
            </TimelineReadableRow>

            <TimelineReadableRow
                index={rowIndex++}
                icon={reviewLog ? (reviewAction === "rechazado" ? "!" : reviewAction === "solicitud_cambios" || reviewAction === "con_cambios" ? "↩" : "✓") : "…"}
                badge="Dictamen emitido"
                title={reviewLog ? result.label : "Pendiente de decisión del revisor"}
                meta={reviewLog ? `${formatDateTime(reviewLog.created_at)} · ${reviewLog.user_name || reviewLog.reviewed_by_user_name || "Revisor"}` : "Este avance aún espera aprobación, rechazo o solicitud de cambios."}
                tone={reviewTone}
                evidenceItems={reviewEvidence}
            >
                {reviewLog ? (
                    <>
                        {getLogSummary(reviewLog) ? (
                            <div style={{ fontSize: 13, color: "#334155", fontWeight: 900, marginBottom: 7 }}>
                                {getLogSummary(reviewLog)}
                            </div>
                        ) : null}
                        <div style={{ fontSize: 13, color: "#111827", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                            {getLogComment(reviewLog)}
                        </div>
                    </>
                ) : null}
            </TimelineReadableRow>

            {otherLogs.map((log) => {
                const action = String(log?.action_type || "").toLowerCase();
                const logTone = action === "rechazado"
                    ? "danger"
                    : action === "solicitud_cambios" || action === "con_cambios"
                        ? "warning"
                        : action === "aprobado"
                            ? "decision"
                            : action === "comentario"
                                ? "neutral"
                                : "default";

                return (
                    <TimelineReadableRow
                        key={`other-${log.id}`}
                        index={rowIndex++}
                        icon={getLogIcon(log)}
                        badge={getTraceMovementType(action)}
                        title={getLogTitle(log)}
                        meta={`${formatDateTime(log.created_at)} · ${log.user_name || "Usuario"}`}
                        tone={logTone}
                        evidenceItems={log.evidence_items}
                    >
                        <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                            {getLogComment(log)}
                        </div>
                    </TimelineReadableRow>
                );
            })}
        </div>
    );

    return (
        <details
            open={isCurrentCycle}
            style={{
                border: `1px solid ${result.border}`,
                borderLeft: `5px solid ${result.color}`,
                background: "#fff",
                borderRadius: 20,
                overflow: "hidden",
                boxShadow: "0 14px 34px rgba(15, 23, 42, 0.06)",
            }}
        >
            <summary
                style={{
                    listStyle: "none",
                    cursor: "pointer",
                    padding: 16,
                    background: "#fff",
                    borderBottom: isCurrentCycle ? `1px solid ${result.border}` : "1px solid #e5e7eb",
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <div>
                        <div style={{ fontSize: 11, color: result.color, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".055em", marginBottom: 5 }}>
                            Historia operativa de la tarea
                        </div>
                        <div style={{ fontSize: 16, fontWeight: 950, color: "#111827" }}>
                            {title}
                        </div>
                        <div style={{ marginTop: 4, fontSize: 13, color: "#64748b" }}>
                            {isCurrentCycle ? "Ciclo actual abierto para revisión." : "Ciclo anterior compactado. Abre para ver avance, dictamen y evidencia."}
                        </div>
                        <div style={{ marginTop: 8, fontSize: 12, color: "#2563eb", fontWeight: 900 }}>
                            {deltaLabel}
                        </div>
                    </div>

                    <Badge bg="#ffffff" color={result.color} title={result.detail}>
                        {result.label}
                    </Badge>
                </div>
            </summary>

            {content}
        </details>
    );
}

function getTraceMovementType(actionType = "") {
    const action = String(actionType || "").toLowerCase();

    if (action === "actualizacion_avance") return "Avance";
    if (action === "enviado_revision") return "Envío";
    if (action === "aprobado") return "Decisión";
    if (action === "rechazado") return "Decisión";
    if (action === "solicitud_cambios" || action === "con_cambios") return "Decisión";
    if (action === "comentario") return "Comentario";
    return "Movimiento";
}

function getTracePmoReading({ actionType = "", progressValue = "", evidence = [], geoLabel = "—" } = {}) {
    const action = String(actionType || "").toLowerCase();
    const progress = Number(progressValue);
    const hasEvidence = Array.isArray(evidence) && evidence.length > 0;
    const geo = String(geoLabel || "").toLowerCase();

    if (geo.includes("pendiente")) {
        return { label: "Excepción por autorizar", tone: "warning" };
    }

    if (geo.includes("rechazada")) {
        return { label: "Geocerca rechazada", tone: "danger" };
    }

    if (action === "enviado_revision") {
        return { label: "Pendiente de decisión", tone: "purple" };
    }

    if (action === "aprobado" && progress >= 100) {
        return { label: "Cierre autorizado", tone: "success" };
    }

    if (action === "aprobado") {
        return { label: "Avance validado", tone: "success" };
    }

    if (action === "rechazado") {
        return { label: "Corrección requerida", tone: "danger" };
    }

    if (action === "solicitud_cambios" || action === "con_cambios") {
        return { label: "Ajustes solicitados", tone: "warning" };
    }

    if (action === "actualizacion_avance" && hasEvidence) {
        return { label: "Seguimiento con evidencia", tone: "info" };
    }

    if (action === "actualizacion_avance") {
        return { label: "Avance documentado", tone: "info" };
    }

    if (action === "comentario") {
        return { label: "Seguimiento complementario", tone: "neutral" };
    }

    return { label: "Movimiento registrado", tone: "neutral" };
}

function getTraceGridRows(logs = []) {
    return [...(logs || [])]
        .sort((a, b) => {
            const aTime = new Date(a.created_at || 0).getTime();
            const bTime = new Date(b.created_at || 0).getTime();
            if (aTime !== bTime) return bTime - aTime;
            return Number(b.id || 0) - Number(a.id || 0);
        })
        .map((log, index) => {
            const evidence = Array.isArray(log?.evidence_items) ? log.evidence_items : [];
            const withGeo = evidence.filter((ev) => Number(ev.has_geo || 0) === 1 || (ev.latitude && ev.longitude)).length;
            const hasBlockingGeo = evidence.some((ev) => isGeoExceptionBlockingApproval(ev));
            const hasRequestedGeo = evidence.some((ev) => isGeoExceptionRequested(ev));
            const hasRejectedGeo = evidence.some((ev) => isGeoExceptionRejected(ev));
            const hasApprovedGeoException = evidence.some((ev) => String(ev.geo_exception_status || "").toLowerCase() === "approved");
            const progressValue = log?.new_progress_percent ?? log?.old_progress_percent ?? "—";
            const reviewValue = log?.review_status || log?.new_review_status || "";
            const actionType = String(log?.action_type || "").toLowerCase();

            let geoLabel = "—";
            let geoTone = "neutral";

            if (hasRejectedGeo) {
                geoLabel = "Excepción rechazada";
                geoTone = "danger";
            } else if (hasRequestedGeo || hasBlockingGeo) {
                geoLabel = "Excepción pendiente";
                geoTone = "warning";
            } else if (hasApprovedGeoException) {
                geoLabel = "Excepción autorizada";
                geoTone = "purple";
            } else if (withGeo > 0) {
                geoLabel = `${withGeo} con ubicación`;
                geoTone = "success";
            }

            const pmoReading = getTracePmoReading({
                actionType,
                progressValue,
                evidence,
                geoLabel,
            });

            return {
                id: log?.id || `trace-${index}`,
                date: formatDateTime(log?.created_at),
                movementType: getTraceMovementType(actionType),
                type: getLogTitle(log),
                user: log?.user_name || log?.reviewed_by_user_name || "Usuario",
                progress: progressValue === "—" ? "—" : `${progressValue}%`,
                review: getFriendlyReviewLabel(reviewValue),
                evidence: evidence.length ? getEvidenceSummaryText(evidence) : "Sin evidencia",
                evidenceCount: evidence.length,
                geoLabel,
                geoTone,
                pmoReading,
                summary: getLogSummary(log),
                comment: getLogComment(log),
                actionType,
                log,
                evidenceItems: evidence,
                oldProgress: log?.old_progress_percent ?? "—",
                newProgress: log?.new_progress_percent ?? "—",
                oldStatus: log?.old_status || "—",
                newStatus: log?.new_status || "—",
                reviewRaw: reviewValue || "—",
            };
        });
}

function TraceGridToneBadge({ children, tone = "neutral", title = "" }) {
    const tones = {
        neutral: { bg: "#f8fafc", color: "#475569", border: "#e2e8f0" },
        info: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
        success: { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" },
        warning: { bg: "#ffffff", color: "#9a3412", border: "#fdba74" },
        danger: { bg: "#fff", color: "#b91c1c", border: "#fecaca" },
        purple: { bg: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe" },
    };
    const s = tones[tone] || tones.neutral;

    return (
        <span
            title={title || (typeof children === "string" ? children : "")}
            style={{
                display: "inline-flex",
                alignItems: "center",
                width: "fit-content",
                padding: "4px 9px",
                borderRadius: 999,
                background: s.bg,
                color: s.color,
                border: `1px solid ${s.border}`,
                fontSize: 11,
                fontWeight: 950,
                whiteSpace: "nowrap",
            }}
        >
            {children}
        </span>
    );
}

function getTraceRowTone(row = {}) {
    const action = String(row.actionType || "").toLowerCase();
    if (action === "aprobado") return "success";
    if (action === "rechazado") return "danger";
    if (action === "solicitud_cambios" || action === "con_cambios") return "warning";
    if (action === "enviado_revision") return "purple";
    if (action === "actualizacion_avance") return "info";
    return "neutral";
}

function traceGridRowMatchesFilter(row = {}, filter = "todos") {
    const action = String(row.actionType || "").toLowerCase();

    if (!filter || filter === "todos") return true;
    if (filter === "envios") return action === "enviado_revision";
    if (filter === "decisiones") return ["aprobado", "rechazado", "solicitud_cambios", "con_cambios"].includes(action);
    if (filter === "evidencias") return Number(row.evidenceCount || 0) > 0;
    if (filter === "cambios") return ["solicitud_cambios", "con_cambios"].includes(action);
    if (filter === "rechazos") return action === "rechazado";

    return true;
}

function TraceGridFilterButton({ active, children, count = 0, tone = "neutral", onClick }) {
    const tones = {
        neutral: { bg: "#f8fafc", color: "#475569", border: "#e2e8f0" },
        info: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
        success: { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" },
        warning: { bg: "#ffffff", color: "#9a3412", border: "#fdba74" },
        danger: { bg: "#fff", color: "#b91c1c", border: "#fecaca" },
        purple: { bg: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe" },
    };
    const s = tones[tone] || tones.neutral;

    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "9px 12px",
                borderRadius: 999,
                border: `1px solid ${active ? s.color : s.border}`,
                background: active ? s.bg : "#fff",
                color: active ? s.color : "#334155",
                fontSize: 12,
                fontWeight: 950,
                cursor: "pointer",
                boxShadow: active ? `0 0 0 3px ${s.bg}` : "0 6px 14px rgba(15,23,42,.04)",
            }}
        >
            <span>{children}</span>
            <span
                style={{
                    minWidth: 22,
                    height: 22,
                    padding: "0 7px",
                    borderRadius: 999,
                    background: active ? "#fff" : s.bg,
                    color: s.color,
                    border: `1px solid ${s.border}`,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 950,
                }}
            >
                {count}
            </span>
        </button>
    );
}


function TraceRecordDetailModal({ row, onClose }) {
    if (!row) return null;

    const tone = getTraceRowTone(row);
    const pmoTone = row?.pmoReading?.tone || tone;
    const pmoLabel = row?.pmoReading?.label || "Movimiento registrado";
    const evidence = Array.isArray(row?.evidenceItems) ? row.evidenceItems : [];
    const log = row?.log || {};

    const DetailItem = ({ label, value, children }) => (
        <div
            style={{
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                padding: 12,
                background: "#fff",
            }}
        >
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 950, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6 }}>
                {label}
            </div>
            {children || (
                <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 850, lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
                    {value || "—"}
                </div>
            )}
        </div>
    );

    return (
        <div
            role="dialog"
            aria-modal="true"
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 100000,
                background: "rgba(15, 23, 42, 0.48)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 18,
            }}
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: "min(920px, 100%)",
                    maxHeight: "88vh",
                    overflow: "auto",
                    background: "#ffffff",
                    borderRadius: 24,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 30px 90px rgba(15, 23, 42, 0.36)",
                }}
            >
                <div
                    style={{
                        padding: "20px 22px",
                        borderBottom: "1px solid #e2e8f0",
                        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                    }}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap", alignItems: "flex-start" }}>
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 950, color: "#2563eb", textTransform: "uppercase", letterSpacing: ".08em" }}>
                                Detalle de trazabilidad
                            </div>
                            <div style={{ marginTop: 6, fontSize: 22, fontWeight: 950, color: "#0f172a", letterSpacing: "-.03em" }}>
                                {row.type}
                            </div>
                            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <TraceGridToneBadge tone={tone}>{row.movementType}</TraceGridToneBadge>
                                <TraceGridToneBadge tone={pmoTone}>{pmoLabel}</TraceGridToneBadge>
                                <TraceGridToneBadge tone={row.geoTone}>{row.geoLabel}</TraceGridToneBadge>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                border: "1px solid #cbd5e1",
                                background: "#fff",
                                color: "#334155",
                                borderRadius: 12,
                                padding: "9px 12px",
                                fontSize: 12,
                                fontWeight: 950,
                                cursor: "pointer",
                            }}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>

                <div style={{ padding: 22, display: "grid", gap: 14 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12 }}>
                        <DetailItem label="Fecha y hora" value={row.date} />
                        <DetailItem label="Usuario" value={row.user} />
                        <DetailItem label="Avance" value={`${row.oldProgress ?? "—"}% → ${row.newProgress ?? row.progress ?? "—"}${String(row.newProgress ?? "").includes("%") ? "" : "%"}`} />
                        <DetailItem label="Estado operativo" value={`${row.oldStatus || "—"} → ${row.newStatus || "—"}`} />
                        <DetailItem label="Estado de revisión" value={getFriendlyReviewLabel(row.reviewRaw)} />
                        <DetailItem label="Evidencia" value={row.evidence} />
                    </div>

                    <DetailItem label="Lectura PMO">
                        <div style={{ display: "grid", gap: 8 }}>
                            <TraceGridToneBadge tone={pmoTone}>{pmoLabel}</TraceGridToneBadge>
                            <div style={{ fontSize: 13, color: "#475569", fontWeight: 750, lineHeight: 1.5 }}>
                                Esta lectura resume el impacto operativo del movimiento seleccionado sin modificar la bitácora original.
                            </div>
                        </div>
                    </DetailItem>

                    {row.summary ? <DetailItem label="Cambio registrado" value={row.summary} /> : null}
                    <DetailItem label="Comentario" value={row.comment || getLogComment(log)} />

                    <div
                        style={{
                            border: "1px solid #e2e8f0",
                            borderRadius: 18,
                            padding: 14,
                            background: "#f8fafc",
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
                            <div>
                                <div style={{ fontSize: 14, fontWeight: 950, color: "#0f172a" }}>Evidencia asociada</div>
                                <div style={{ marginTop: 3, fontSize: 12, color: "#64748b", fontWeight: 750 }}>
                                    Archivos, links, comentarios o ubicación vinculados a este registro.
                                </div>
                            </div>
                            <TraceGridToneBadge tone={evidence.length ? "info" : "neutral"}>{evidence.length} elemento(s)</TraceGridToneBadge>
                        </div>
                        <CompactEvidenceList items={evidence} />
                    </div>
                </div>
            </div>
        </div>
    );
}


function SyncedHorizontalScroll({ children, minWidth = 1200 }) {
    const topScrollRef = useRef(null);
    const bottomScrollRef = useRef(null);
    const syncingRef = useRef(false);

    const syncScroll = (source, target) => {
        if (!source || !target || syncingRef.current) return;
        syncingRef.current = true;
        target.scrollLeft = source.scrollLeft;
        window.requestAnimationFrame(() => {
            syncingRef.current = false;
        });
    };

    return (
        <div style={{ display: "grid", gap: 8 }}>
            <div
                ref={topScrollRef}
                onScroll={() => syncScroll(topScrollRef.current, bottomScrollRef.current)}
                style={{
                    overflowX: "auto",
                    overflowY: "hidden",
                    height: 14,
                    borderBottom: "1px solid #f1f5f9",
                }}
                title="Desplazamiento horizontal del grid"
            >
                <div style={{ width: minWidth, height: 1 }} />
            </div>
            <div
                ref={bottomScrollRef}
                onScroll={() => syncScroll(bottomScrollRef.current, topScrollRef.current)}
                style={{ overflowX: "auto" }}
            >
                {children}
            </div>
        </div>
    );
}

function TraceGridView({ rows = [], onOpenDetail = null }) {
    if (!rows.length) {
        return (
            <div style={{ color: "#6b7280", fontSize: 14 }}>
                No hay movimientos registrados para mostrar con este filtro.
            </div>
        );
    }

    const gridColumns = "130px 96px minmax(190px, 1.25fr) minmax(155px, 1fr) 90px minmax(150px, .9fr) minmax(170px, 1fr) minmax(170px, 1fr)";

    return (
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 18, overflow: "hidden", background: "#fff" }}>
            <SyncedHorizontalScroll minWidth={1200}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: gridColumns,
                        gap: 12,
                        padding: "12px 14px",
                        background: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                        color: "#475569",
                        fontSize: 11,
                        fontWeight: 950,
                        textTransform: "uppercase",
                        letterSpacing: ".04em",
                        minWidth: 1200,
                    }}
                >
                    <div>Fecha</div>
                    <div>Tipo</div>
                    <div>Movimiento</div>
                    <div>Lectura PMO</div>
                    <div>Avance</div>
                    <div>Revisión</div>
                    <div>Evidencia</div>
                    <div>Geocerca</div>
                </div>

                {rows.map((row, index) => {
                    const tone = getTraceRowTone(row);
                    const pmoTone = row?.pmoReading?.tone || tone;
                    const pmoLabel = row?.pmoReading?.label || "Movimiento registrado";

                    return (
                        <div
                            key={row.id}
                            style={{
                                display: "grid",
                                gridTemplateColumns: gridColumns,
                                gap: 12,
                                padding: "13px 14px",
                                alignItems: "start",
                                borderBottom: index === rows.length - 1 ? "none" : "1px solid #e2e8f0",
                                background: index % 2 === 0 ? "#fff" : "#f8fafc",
                                minWidth: 1200,
                                boxShadow: "inset 3px 0 0 rgba(37, 99, 235, 0.08)",
                                cursor: typeof onOpenDetail === "function" ? "pointer" : "default",
                                transition: "background .16s ease, box-shadow .16s ease",
                            }}
                            title="Abrir detalle ejecutivo del registro."
                            onClick={() => typeof onOpenDetail === "function" ? onOpenDetail(row) : null}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "#eff6ff"; e.currentTarget.style.boxShadow = "inset 3px 0 0 #2563eb"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = index % 2 === 0 ? "#fff" : "#f8fafc"; e.currentTarget.style.boxShadow = "inset 3px 0 0 rgba(37, 99, 235, 0.08)"; }}
                        >
                            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800, lineHeight: 1.35 }}>{row.date}</div>
                            <div><TraceGridToneBadge tone={tone}>{row.movementType}</TraceGridToneBadge></div>
                            <div>
                                <TraceGridToneBadge tone={tone}>{row.type}</TraceGridToneBadge>
                                {row.summary ? (
                                    <div style={{ marginTop: 6, color: "#334155", fontSize: 12, fontWeight: 850, lineHeight: 1.35 }}>
                                        {row.summary}
                                    </div>
                                ) : null}
                                {row.comment ? (
                                    <div style={{ marginTop: 5, color: "#64748b", fontSize: 12, lineHeight: 1.35, maxWidth: 320 }}>
                                        {row.comment}
                                    </div>
                                ) : null}
                            </div>
                            <div>
                                <TraceGridToneBadge tone={pmoTone}>{pmoLabel}</TraceGridToneBadge>
                                <div style={{ marginTop: 6, color: "#64748b", fontSize: 11, lineHeight: 1.35 }}>
                                    Lectura ejecutiva del movimiento.
                                </div>
                            </div>
                            <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 950 }}>{row.progress}</div>
                            <div><TraceGridToneBadge tone={tone}>{row.review || "—"}</TraceGridToneBadge></div>
                            <div style={{ color: row.evidence === "Sin evidencia" ? "#94a3b8" : "#334155", fontSize: 12, fontWeight: 850, lineHeight: 1.35 }}>{row.evidence}</div>
                            <div><TraceGridToneBadge tone={row.geoTone}>{row.geoLabel}</TraceGridToneBadge></div>
                        </div>
                    );
                })}
            </SyncedHorizontalScroll>
        </div>
    );
}


function IconViewToggle({ value = "cards", onChange, leftTitle = "Vista detalle", rightTitle = "Vista cards" }) {
    const optionStyle = (active) => ({
        width: 38,
        height: 34,
        border: "none",
        borderRadius: 10,
        background: active ? "#0f172a" : "transparent",
        color: active ? "#ffffff" : "#64748b",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all .18s ease",
    });

    return (
        <div
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: 5,
                borderRadius: 15,
                background: "#ffffff",
                border: "1px solid #cbd5e1",
                boxShadow: "0 8px 18px rgba(15,23,42,.06)",
            }}
        >
            <button type="button" title={leftTitle} onClick={() => onChange?.("detail")} style={optionStyle(value === "detail")}>
                <svg width="17" height="17" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <rect x="3" y="4" width="14" height="2" rx="1" fill="currentColor" />
                    <rect x="3" y="9" width="14" height="2" rx="1" fill="currentColor" />
                    <rect x="3" y="14" width="14" height="2" rx="1" fill="currentColor" />
                </svg>
            </button>
            <button type="button" title={rightTitle} onClick={() => onChange?.("cards")} style={optionStyle(value === "cards")}>
                <svg width="17" height="17" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <rect x="3" y="3" width="5" height="5" rx="1.4" stroke="currentColor" strokeWidth="1.7" />
                    <rect x="12" y="3" width="5" height="5" rx="1.4" stroke="currentColor" strokeWidth="1.7" />
                    <rect x="3" y="12" width="5" height="5" rx="1.4" stroke="currentColor" strokeWidth="1.7" />
                    <rect x="12" y="12" width="5" height="5" rx="1.4" stroke="currentColor" strokeWidth="1.7" />
                </svg>
            </button>
        </div>
    );
}

function getEvidenceRowTone(item = {}) {
    const geo = getGeoValidationVisual(item);
    const status = String(item.geo_validation_status || "").toLowerCase();
    const exception = String(item.geo_exception_status || "").toLowerCase();

    if (status === "out_of_range" && exception === "rejected") return "danger";
    if (status === "out_of_range" && exception !== "approved") return "warning";
    if (status === "exception" || exception === "approved") return "purple";
    if (geo.visible && geo.label?.toLowerCase?.().includes("validada")) return "success";
    if (Number(item.has_geo || 0) === 1 || (item.latitude && item.longitude)) return "info";
    return "neutral";
}

function EvidenceRepositoryGridView({ items = [], permissions = {}, onDecideException = null, decidingExceptionId = null }) {
    const list = Array.isArray(items) ? items : [];

    if (!list.length) {
        return (
            <div
                style={{
                    border: "1px dashed #cbd5e1",
                    borderRadius: 16,
                    padding: 16,
                    background: "#f8fafc",
                    color: "#64748b",
                    fontSize: 14,
                    fontWeight: 750,
                }}
            >
                No hay evidencias para mostrar con este filtro.
            </div>
        );
    }

    const gridColumns = "128px 128px minmax(220px, 1.4fr) minmax(150px, .9fr) minmax(170px, 1fr) minmax(190px, 1fr)";

    return (
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 18, overflow: "hidden", background: "#fff" }}>
            <SyncedHorizontalScroll minWidth={1040}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: gridColumns,
                        gap: 12,
                        padding: "12px 14px",
                        background: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                        color: "#475569",
                        fontSize: 11,
                        fontWeight: 950,
                        textTransform: "uppercase",
                        letterSpacing: ".04em",
                        minWidth: 1040,
                    }}
                >
                    <div>Fecha</div>
                    <div>Contexto</div>
                    <div>Evidencia</div>
                    <div>Usuario</div>
                    <div>Tipo</div>
                    <div>Geocerca</div>
                </div>

                {list.map((item, index) => {
                    const hasGeo = Number(item.has_geo || 0) === 1 || (item.latitude && item.longitude);
                    const geoVisual = getGeoValidationVisual(item);
                    const tone = getEvidenceRowTone(item);
                    const context = String(item.evidence_context || "avance").toLowerCase();
                    const contextTone = context === "revision" ? "purple" : "info";
                    const typeVisual = getEvidenceVisual(item);
                    const mapsUrl = hasGeo ? `https://www.google.com/maps?q=${item.latitude},${item.longitude}` : "";
                    const fileLink = item.file_url || item.external_url || "";

                    return (
                        <div
                            key={item.id || `${item.title}-${index}`}
                            style={{
                                display: "grid",
                                gridTemplateColumns: gridColumns,
                                gap: 12,
                                padding: "13px 14px",
                                alignItems: "start",
                                borderBottom: index === list.length - 1 ? "none" : "1px solid #e2e8f0",
                                background: index % 2 === 0 ? "#fff" : "#f8fafc",
                                minWidth: 1040,
                                boxShadow: `inset 3px 0 0 ${tone === "danger" ? "#fecaca" : tone === "warning" ? "#fdba74" : tone === "purple" ? "#ddd6fe" : tone === "success" ? "#a7f3d0" : "#bfdbfe"}`,
                                cursor: fileLink || mapsUrl ? "pointer" : "default",
                                transition: "background .16s ease, box-shadow .16s ease",
                            }}
                            title={fileLink ? "Abrir evidencia" : mapsUrl ? "Abrir ubicación" : "Evidencia sin archivo o enlace directo"}
                            onClick={() => {
                                const targetUrl = fileLink || mapsUrl;
                                if (targetUrl) window.open(targetUrl, "_blank", "noopener,noreferrer");
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "#eff6ff"; e.currentTarget.style.boxShadow = "inset 3px 0 0 #2563eb"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = index % 2 === 0 ? "#fff" : "#f8fafc"; e.currentTarget.style.boxShadow = `inset 3px 0 0 ${tone === "danger" ? "#fecaca" : tone === "warning" ? "#fdba74" : tone === "purple" ? "#ddd6fe" : tone === "success" ? "#a7f3d0" : "#bfdbfe"}`; }}
                        >
                            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800, lineHeight: 1.35 }}>
                                {formatDateTime(item.created_at)}
                            </div>
                            <div>
                                <TraceGridToneBadge tone={contextTone}>{context === "revision" ? "Revisión" : "Avance"}</TraceGridToneBadge>
                            </div>
                            <div>
                                <div style={{ color: "#0f172a", fontSize: 13, fontWeight: 950, lineHeight: 1.35 }}>
                                    {item.title || item.file_name || "Evidencia sin título"}
                                </div>
                                {item.description ? (
                                    <div style={{ marginTop: 5, color: "#64748b", fontSize: 12, lineHeight: 1.35, maxWidth: 360 }}>
                                        {item.description}
                                    </div>
                                ) : null}
                                {item.file_name ? (
                                    <div style={{ marginTop: 5, color: "#94a3b8", fontSize: 11, fontWeight: 800 }}>
                                        {item.file_name}
                                    </div>
                                ) : null}
                            </div>
                            <div style={{ color: "#334155", fontSize: 12, fontWeight: 850, lineHeight: 1.35 }}>
                                {item.uploaded_by_name || "Usuario"}
                            </div>
                            <div>
                                <TraceGridToneBadge tone="neutral">{typeVisual.icon} {item.evidence_type || "evidencia"}</TraceGridToneBadge>
                            </div>
                            <div>
                                {hasGeo ? (
                                    <>
                                        <TraceGridToneBadge tone={tone}>{geoVisual.visible ? `${geoVisual.icon} ${geoVisual.label}` : "📍 Ubicación registrada"}</TraceGridToneBadge>
                                        {formatGeoExceptionReason(item) ? (
                                            <div style={{ marginTop: 6, color: "#64748b", fontSize: 11, lineHeight: 1.35 }}>
                                                Motivo: {formatGeoExceptionReason(item)}
                                            </div>
                                        ) : null}
                                        {isGeoExceptionRequested(item) && permissions.can_review && typeof onDecideException === "function" ? (
                                            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 8 }}>
                                                <button
                                                    type="button"
                                                    disabled={decidingExceptionId === item.id}
                                                    onClick={(e) => { e.stopPropagation(); onDecideException(item, "approve"); }}
                                                    style={{
                                                        border: "1px solid #16a34a",
                                                        background: "#16a34a",
                                                        color: "#fff",
                                                        borderRadius: 11,
                                                        padding: "8px 10px",
                                                        fontSize: 11,
                                                        fontWeight: 950,
                                                        cursor: decidingExceptionId === item.id ? "wait" : "pointer",
                                                    }}
                                                >
                                                    Autorizar
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={decidingExceptionId === item.id}
                                                    onClick={(e) => { e.stopPropagation(); onDecideException(item, "reject"); }}
                                                    style={{
                                                        border: "1px solid #fecaca",
                                                        background: "#fff",
                                                        color: "#b91c1c",
                                                        borderRadius: 11,
                                                        padding: "8px 10px",
                                                        fontSize: 11,
                                                        fontWeight: 950,
                                                        cursor: decidingExceptionId === item.id ? "wait" : "pointer",
                                                    }}
                                                >
                                                    Rechazar
                                                </button>
                                            </div>
                                        ) : null}
                                    </>
                                ) : (
                                    <TraceGridToneBadge tone="neutral">Sin ubicación</TraceGridToneBadge>
                                )}
                            </div>
                        </div>
                    );
                })}
            </SyncedHorizontalScroll>
        </div>
    );
}


function SmartTaskSidePanel({
    task,
    permissions = {},
    actionFocus,
    taskTimeHealth,
    routeHealth,
    reviewCycles = [],
    evidenceSummary = {},
    latestResponsibleEvidenceItems = [],
    pendingGeoExceptions = [],
    hasBlockingGeoException = false,
    responsibleGeoEvidenceRequired = false,
}) {
    if (!task) return null;

    const statusStyle = getStatusStyle(task?.status);
    const reviewStyle = getReviewStyle(task?.review_status);
    const evidenceTotal = Number(evidenceSummary?.total || task?.evidence_count || 0);
    const geoTotal = Number(evidenceSummary?.with_geo || task?.geo_evidence_count || 0);
    const lastEvidenceCount = Array.isArray(latestResponsibleEvidenceItems) ? latestResponsibleEvidenceItems.length : 0;
    const pendingGeoCount = Array.isArray(pendingGeoExceptions) ? pendingGeoExceptions.length : 0;
    const reviewerGeoEvidenceRequired = Number(task?.reviewer_geo_evidence_required || 0) === 1;
    const reviewerGeoTotal = Number(
        evidenceSummary?.revision_with_geo ||
        evidenceSummary?.reviewer_with_geo ||
        evidenceSummary?.revision_pending_with_geo ||
        0
    );
    const responsibleGeoStatus = responsibleGeoEvidenceRequired
        ? (geoTotal > 0 ? `${geoTotal} con ubicación` : "Pendiente")
        : "No requerida";
    const reviewerGeoStatus = reviewerGeoEvidenceRequired
        ? (reviewerGeoTotal > 0 ? `${reviewerGeoTotal} con ubicación` : "Pendiente")
        : "No requerida";

    const microToneColor =
        actionFocus?.tone === "danger" ? "#b91c1c" :
            actionFocus?.tone === "correction" ? "#9a3412" :
                actionFocus?.tone === "warning" ? "#92400e" :
                    actionFocus?.tone === "success" ? "#047857" :
                        "#2563eb";

    const contextItems = [
        {
            label: "Proyecto",
            value: task?.project_name || "—",
            helper: task?.project_code ? `Código ${task.project_code}` : "Proyecto de origen de la tarea.",
        },
        {
            label: "Project Manager",
            value: task?.project_manager_name || task?.project_manager_owner_name || "Sin PM asignado",
            helper: task?.project_manager_user_id
                ? "Autoridad PMO para intervenir, reprogramar, convertir en riesgo o liberar bloqueos."
                : "Asigna un PM al proyecto para habilitar la intervención PMO.",
            color: task?.project_manager_user_id ? "#0f172a" : "#b45309",
        },
        {
            label: "Responsable",
            value: task?.owner_name || "—",
            helper: "Ejecuta y documenta el avance.",
        },
        {
            label: "Revisor",
            value: task?.reviewer_name || "—",
            helper: "Valida evidencia y dictamina el avance.",
        },
        {
            label: "Fecha compromiso",
            value: formatDate(task?.due_date),
            helper: taskTimeHealth?.label || "Sin lectura de tiempo.",
            color: taskTimeHealth?.color || "#475569",
        },
    ];

    const technicalItems = [
        {
            label: "Ruta",
            value: routeHealth?.label || "—",
            helper: routeHealth?.detail || "Sin bloqueo operativo activo.",
            color: routeHealth?.color || "#475569",
        },
        {
            label: "Tarea padre",
            value: task?.parent_wbs_code || task?.parent_task_name
                ? `${task?.parent_wbs_code || "—"} · ${task?.parent_task_name || "Tarea padre"}`
                : task?.parent_task_id ? `ID ${task.parent_task_id}` : "Sin tarea padre",
            helper: "Ubicación jerárquica dentro de la WBS.",
        },
        {
            label: "Predecesora",
            value: task?.dependency_wbs_code || task?.dependency_task_name
                ? `${task?.dependency_wbs_code || "—"} · ${task?.dependency_task_name || "Tarea predecesora"}`
                : task?.depends_on_task_id ? `ID ${task.depends_on_task_id}` : "Sin predecesora",
            helper: task?.dependency_type ? `Tipo de dependencia ${task.dependency_type}` : "Sin dependencia operativa registrada.",
        },
        {
            label: "Área / prioridad",
            value: `${task?.area_name || task?.area || "Sin área"} · ${task?.priority || "Sin prioridad"}`,
            helper: "Clasificación operativa de la tarea.",
        },
    ];

    return (
        <aside
            style={{
                position: "sticky",
                top: 18,
                alignSelf: "start",
                display: "grid",
                gap: 14,
            }}
        >
            <div
                style={{
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 24,
                    padding: 18,
                    boxShadow: "0 18px 42px rgba(15, 23, 42, 0.065)",
                }}
            >
                <div style={{ fontSize: 11, fontWeight: 950, color: "#2563eb", textTransform: "uppercase", letterSpacing: ".06em" }}>
                    Panel inteligente
                </div>
                <div style={{ marginTop: 6, fontSize: 18, fontWeight: 950, color: "#0f172a", lineHeight: 1.15 }}>
                    Contexto de tarea
                </div>
                <div style={{ marginTop: 6, color: "#64748b", fontSize: 12, fontWeight: 750, lineHeight: 1.45 }}>
                    Consulta rápida de personas, fechas, ruta y control documental sin invadir el registro del resultado.
                </div>
            </div>

            <div
                style={{
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 24,
                    padding: 16,
                    boxShadow: "0 14px 32px rgba(15, 23, 42, 0.05)",
                }}
            >
                <div style={{ fontSize: 13, fontWeight: 950, color: "#0f172a", marginBottom: 10 }}>
                    Control rápido
                </div>
                <div style={{ display: "grid", gap: 9 }}>
                    {contextItems.map((item) => (
                        <div key={item.label} style={{ paddingBottom: 9, borderBottom: "1px solid #f1f5f9" }}>
                            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 950, textTransform: "uppercase", letterSpacing: ".04em" }}>{item.label}</div>
                            <div style={{ marginTop: 3, fontSize: 13, color: item.color || "#0f172a", fontWeight: 950, lineHeight: 1.25 }}>{item.value}</div>
                            <div style={{ marginTop: 3, fontSize: 11, color: "#64748b", fontWeight: 700, lineHeight: 1.35 }}>{item.helper}</div>
                        </div>
                    ))}
                </div>
            </div>

            <div
                style={{
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 24,
                    padding: 16,
                    boxShadow: "0 14px 32px rgba(15, 23, 42, 0.05)",
                }}
            >
                <div style={{ fontSize: 13, fontWeight: 950, color: "#0f172a", marginBottom: 10 }}>
                    Control documental
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
                    <div style={{ padding: 10, borderRadius: 16, background: "#ffffff", border: "1px solid #e2e8f0" }}>
                        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 950, textTransform: "uppercase" }}>Evidencias</div>
                        <div style={{ marginTop: 4, fontSize: 20, color: "#0f172a", fontWeight: 950 }}>{evidenceTotal}</div>
                    </div>
                    <div style={{ padding: 10, borderRadius: 16, background: "#ffffff", border: `1px solid ${responsibleGeoEvidenceRequired && geoTotal <= 0 ? "#fed7aa" : "#e2e8f0"}` }}>
                        <div style={{ fontSize: 10, color: responsibleGeoEvidenceRequired && geoTotal <= 0 ? "#c2410c" : "#64748b", fontWeight: 950, textTransform: "uppercase" }}>Geo responsable</div>
                        <div style={{ marginTop: 4, fontSize: responsibleGeoEvidenceRequired && responsibleGeoStatus !== "Pendiente" ? 16 : 15, color: responsibleGeoEvidenceRequired && geoTotal <= 0 ? "#c2410c" : "#0f172a", fontWeight: 950 }}>{responsibleGeoStatus}</div>
                    </div>
                    <div style={{ padding: 10, borderRadius: 16, background: "#ffffff", border: "1px solid #e2e8f0" }}>
                        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 950, textTransform: "uppercase" }}>Último envío</div>
                        <div style={{ marginTop: 4, fontSize: 20, color: "#0f172a", fontWeight: 950 }}>{lastEvidenceCount}</div>
                    </div>
                    <div style={{ padding: 10, borderRadius: 16, background: "#ffffff", border: `1px solid ${reviewerGeoEvidenceRequired && reviewerGeoTotal <= 0 ? "#fed7aa" : "#e2e8f0"}` }}>
                        <div style={{ fontSize: 10, color: reviewerGeoEvidenceRequired && reviewerGeoTotal <= 0 ? "#c2410c" : "#64748b", fontWeight: 950, textTransform: "uppercase" }}>Geo revisor</div>
                        <div style={{ marginTop: 4, fontSize: reviewerGeoEvidenceRequired && reviewerGeoStatus !== "Pendiente" ? 16 : 15, color: reviewerGeoEvidenceRequired && reviewerGeoTotal <= 0 ? "#c2410c" : "#0f172a", fontWeight: 950 }}>{reviewerGeoStatus}</div>
                    </div>
                </div>
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f1f5f9", fontSize: 11, color: "#64748b", lineHeight: 1.45, fontWeight: 750 }}>
                    Ciclos registrados: <strong style={{ color: "#0f172a" }}>{reviewCycles.length || 0}</strong>. Revisa la historia completa en Ciclos de revisión o Repositorio de evidencias.
                </div>
            </div>

            <div
                style={{
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 24,
                    padding: 16,
                    boxShadow: "0 14px 32px rgba(15, 23, 42, 0.05)",
                }}
            >
                <div style={{ fontSize: 13, fontWeight: 950, color: "#0f172a", marginBottom: 10 }}>
                    Contexto técnico
                </div>
                <div style={{ display: "grid", gap: 9 }}>
                    {technicalItems.map((item) => (
                        <div key={item.label} style={{ paddingBottom: 9, borderBottom: "1px solid #f1f5f9" }}>
                            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 950, textTransform: "uppercase", letterSpacing: ".04em" }}>{item.label}</div>
                            <div style={{ marginTop: 3, fontSize: 12, color: item.color || "#0f172a", fontWeight: 900, lineHeight: 1.3 }}>{item.value}</div>
                            <div style={{ marginTop: 3, fontSize: 11, color: "#64748b", fontWeight: 700, lineHeight: 1.35 }}>{item.helper}</div>
                        </div>
                    ))}
                </div>
            </div>
        </aside>
    );
}

export default function MyTaskDetailPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { taskId } = useParams();
    const [searchParams] = useSearchParams();

    const queryReturnTo = searchParams.get("return_to");
    const queryReturnLabel = searchParams.get("return_label");

    const returnTo = location.state?.returnTo || queryReturnTo || "/my-work";
    const returnLabel = location.state?.returnLabel || queryReturnLabel || "Mi trabajo";
    const cameFromProjectDetail =
        !!location.state?.fromProjectDetail ||
        String(searchParams.get("return_label") || "").toLowerCase() === "ficha 360" ||
        String(searchParams.get("return_to") || "").includes("/projects/");


    const handleReturn = () => {
        navigate(returnTo, { replace: true });
    };

    const modeFromUrl = searchParams.get("mode") || "view";
    const mode = ["view", "progress", "review"].includes(modeFromUrl) ? modeFromUrl : "view";
    const loggedUserId = getLoggedUserId();

    const [loading, setLoading] = useState(true);
    const [savingProgress, setSavingProgress] = useState(false);
    const [savingReview, setSavingReview] = useState(false);
    const [error, setError] = useState("");
    const [actionError, setActionError] = useState("");
    const [platformToast, setPlatformToast] = useState(null);
    const [detail, setDetail] = useState(null);
    const [interventionHistory, setInterventionHistory] = useState(null);
    const [interventionHistoryLoading, setInterventionHistoryLoading] = useState(false);
    const [interventionModalAction, setInterventionModalAction] = useState(null);
    const [interventionForm, setInterventionForm] = useState({ comment: "", new_due_date: "" });
    const [savingIntervention, setSavingIntervention] = useState(false);
    const [savingReviewer, setSavingReviewer] = useState(false);
    const [reviewerForm, setReviewerForm] = useState({
        reviewer_user_id: "",
        requires_review: true,
    });
    const [savingDueDate, setSavingDueDate] = useState(false);
    const [dueDateForm, setDueDateForm] = useState({
        start_date: "",
        due_date: "",
    });
    const [progressForm, setProgressForm] = useState({ progress_percent: "", comment: "", enviar_revision: true });
    const [reviewForm, setReviewForm] = useState({ comment: "" });
    const [traceMainView, setTraceMainView] = useState("cycles");
    const [traceCycleView, setTraceCycleView] = useState("detail");
    const [latestSubmissionView, setLatestSubmissionView] = useState("detail");
    const [latestSubmissionOpen, setLatestSubmissionOpen] = useState(false);
    const [evidenceRepoView, setEvidenceRepoView] = useState("detail");
    const [traceGridFilter, setTraceGridFilter] = useState("todos");
    const [selectedTraceRow, setSelectedTraceRow] = useState(null);
    const [evidenceFilter, setEvidenceFilter] = useState("todas");
    const [logFilter, setLogFilter] = useState("todos");
    const [evidenceLoading, setEvidenceLoading] = useState(false);
    const [evidenceItems, setEvidenceItems] = useState([]);
    const [evidenceSummary, setEvidenceSummary] = useState({
        total: 0,
        with_geo: 0,

        avance_total: 0,
        avance_with_geo: 0,
        avance_pending_total: 0,
        avance_pending_with_geo: 0,

        revision_total: 0,
        revision_with_geo: 0,
        revision_pending_total: 0,
        revision_pending_with_geo: 0,

        evidence_required: false,
        geo_evidence_required: false,
        status: "ok",
    });
    const [savingEvidence, setSavingEvidence] = useState(false);
    const [capturingGeo, setCapturingGeo] = useState(false);
    const [evidenceFile, setEvidenceFile] = useState(null);
    const [evidenceFiles, setEvidenceFiles] = useState([]);
    const [evidenceForm, setEvidenceForm] = useState({
        evidence_type: "comentario",
        title: "",
        description: "",
        external_url: "",
        latitude: "",
        longitude: "",
        geo_accuracy: "",
        geo_source: "",
        geo_exception_reason: "",
    });
    const [geoExceptionRequired, setGeoExceptionRequired] = useState(false);
    const [forceResponsibleGeoRequired, setForceResponsibleGeoRequired] = useState(false);
    const [decidingExceptionId, setDecidingExceptionId] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState(null);

    const task = detail?.task || null;
    const taskRequiresReview = Number(task?.requires_review || 0) === 1;

    const isBackendGeoRequiredMessage = (message = "") => {
        const value = String(message || "").toLowerCase();
        return (
            value.includes("requiere ubicación") ||
            value.includes("requiere ubicacion") ||
            value.includes("ubicación automática") ||
            value.includes("ubicacion automatica") ||
            value.includes("georreferencia") ||
            value.includes("geo obligatoria") ||
            value.includes("geo requerida")
        );
    };

    // Fuente visual/operativa única para geoevidencia del responsable.
    // Importante: el backend puede exigir ubicación por geocerca del proyecto aunque el API de detalle
    // no devuelva las coordenadas completas del proyecto. Por eso se consideran banderas explícitas,
    // coordenadas visibles, mensajes del backend y un modo seguro para tareas con proyecto activo.
    const projectContext = detail?.project || detail?.project_info || detail?.project_detail || {};

    const taskProjectHasGeoPoint = Boolean(
        (task?.project_latitude && task?.project_longitude) ||
        (task?.project_lat && task?.project_lng) ||
        (task?.project_geo_latitude && task?.project_geo_longitude) ||
        (task?.latitude && task?.longitude && (task?.project_id || task?.project_name || task?.location_description)) ||
        (projectContext?.latitude && projectContext?.longitude) ||
        (projectContext?.project_latitude && projectContext?.project_longitude) ||
        (projectContext?.lat && projectContext?.lng)
    );

    const taskProjectGeoFlag = Boolean(
        Number(task?.project_has_geo || 0) === 1 ||
        Number(task?.project_geo_required || 0) === 1 ||
        Number(task?.project_geofence_enabled || 0) === 1 ||
        Number(task?.project_location_required || 0) === 1 ||
        Number(projectContext?.geo_required || 0) === 1 ||
        Number(projectContext?.geofence_enabled || 0) === 1 ||
        Number(projectContext?.location_required || 0) === 1 ||
        String(task?.location_description || task?.project_location_description || projectContext?.location_description || "").trim()
    );

    // Modo seguro: si la tarea pertenece a un proyecto, el backend puede validar contra el punto
    // autorizado del proyecto aunque el detalle no traiga latitude/longitude. Esto evita que el
    // checklist diga "No requerida" y luego el backend bloquee por ubicación automática.
    const projectGeoSafeMode = Number(task?.project_id || 0) > 0;

    const responsibleEvidenceRequired =
        Number(task?.evidence_required || 0) === 1 ||
        Boolean(evidenceSummary?.evidence_required);

    const responsibleGeoEvidenceRequired =
        Number(task?.geo_evidence_required || 0) === 1 ||
        Number(task?.require_geo_evidence || 0) === 1 ||
        Number(task?.geo_required || 0) === 1 ||
        Number(task?.location_required || 0) === 1 ||
        Boolean(evidenceSummary?.geo_evidence_required) ||
        taskProjectHasGeoPoint ||
        taskProjectGeoFlag ||
        forceResponsibleGeoRequired ||
        isBackendGeoRequiredMessage(actionError);

    const taskRequiresResponsibleEvidence =
        responsibleEvidenceRequired ||
        responsibleGeoEvidenceRequired;

    const taskRequiresOnlyReview =
        taskRequiresReview && !taskRequiresResponsibleEvidence;

    const taskRequiresNoReview =
        !taskRequiresReview;

    const progressButtonLabel = taskRequiresReview
        ? "Guardar y enviar a revisión"
        : "Guardar avance";

    const progressEvidenceBlockTitle = taskRequiresResponsibleEvidence
        ? "Evidencia de avance"
        : "Avance sin evidencia requerida";

    const needsEvidence =
        Number(task?.evidence_required || 0) === 1 &&
        Number(task?.evidence_count || 0) === 0;

    const needsGeo =
        Number(task?.geo_evidence_required || 0) === 1 &&
        Number(task?.geo_evidence_count || 0) === 0;

    const usersCatalog =
        detail?.users ||
        detail?.catalogs?.users ||
        detail?.options?.users ||
        detail?.reviewers ||
        [];

    const activeUsers = Array.isArray(usersCatalog)
        ? usersCatalog.filter((u) => Number(u.is_active ?? 1) === 1)
        : [];


    const dependencyInfo = getDependencyTypeInfo(task?.dependency_type || "FS");

    const predecessorLabel =
        task?.dependency_wbs_code || task?.dependency_task_name
            ? `${task?.dependency_wbs_code || task?.depends_on_task_id || "—"} · ${task?.dependency_task_name || "Tarea predecesora"}`
            : task?.depends_on_task_id
                ? `ID ${task.depends_on_task_id}`
                : "Sin predecesora";

    const parentTaskLabel =
        task?.parent_wbs_code || task?.parent_task_name
            ? `${task?.parent_wbs_code || task?.parent_task_id || "—"} · ${task?.parent_task_name || "Tarea padre"}`
            : task?.parent_task_id
                ? `ID ${task.parent_task_id}`
                : "Sin tarea padre";

    const logs = detail?.logs || [];
    const operationalLogs = useMemo(() => (logs || []).filter((log) => !isInterventionLog(log)), [logs]);
    const interventionMirrorLogs = useMemo(() => (logs || []).filter((log) => isInterventionLog(log)), [logs]);
    const permissions = detail?.permissions || {};
    const latestReviewSubmission = detail?.latest_review_submission || null;

    const groupedLogs = useMemo(() => groupLogsByDay(logs), [logs]);
    const reviewCycles = useMemo(() => buildReviewCycles(operationalLogs), [operationalLogs]);
    const traceGridRows = useMemo(() => getTraceGridRows(operationalLogs), [operationalLogs]);
    const filteredTraceGridRows = useMemo(
        () => traceGridRows.filter((row) => traceGridRowMatchesFilter(row, traceGridFilter)),
        [traceGridRows, traceGridFilter]
    );
    const traceGridFilterCounts = useMemo(() => {
        return {
            todos: traceGridRows.length,
            envios: traceGridRows.filter((row) => traceGridRowMatchesFilter(row, "envios")).length,
            decisiones: traceGridRows.filter((row) => traceGridRowMatchesFilter(row, "decisiones")).length,
            evidencias: traceGridRows.filter((row) => traceGridRowMatchesFilter(row, "evidencias")).length,
            cambios: traceGridRows.filter((row) => traceGridRowMatchesFilter(row, "cambios")).length,
            rechazos: traceGridRows.filter((row) => traceGridRowMatchesFilter(row, "rechazos")).length,
        };
    }, [traceGridRows]);
    const taskTimeHealth = useMemo(() => getTimeHealth(task), [task]);
    const routeHealth = useMemo(
        () => getRouteHealth(task, detail?.dependency_status),
        [task, detail?.dependency_status]
    );
    const isRouteBlocked = String(routeHealth?.label || "").toLowerCase() === "ruta bloqueada";
    const isApprovedPartial =
        String(task?.review_status || "").toLowerCase() === "aprobado" &&
        String(task?.status || "").toLowerCase() === "en_proceso";
    const progressHealth = useMemo(
        () => getProgressHealthStyle(task?.progress_percent || 0),
        [task?.progress_percent]
    );
    const latestCorrectionLog = useMemo(() => {
        return [...(operationalLogs || [])].find((log) => {
            const action = String(log.action_type || "").toLowerCase();
            const review = String(log.review_status || "").toLowerCase();

            return (
                action === "rechazado" ||
                action === "solicitud_cambios" ||
                review === "rechazado" ||
                review === "con_cambios"
            );
        });
    }, [operationalLogs]);
    const latestReviewSubmissionLog = useMemo(() => {
        return [...(operationalLogs || [])].find((log) =>
            String(log.action_type || "").toLowerCase() === "enviado_revision"
        );
    }, [operationalLogs]);

    const latestResponsibleEvidenceItems = useMemo(() => {
        const fromSubmission = Array.isArray(latestReviewSubmission?.evidence_items)
            ? latestReviewSubmission.evidence_items
            : [];

        if (fromSubmission.length) return fromSubmission;

        const fromSubmissionLog = Array.isArray(latestReviewSubmissionLog?.evidence_items)
            ? latestReviewSubmissionLog.evidence_items
            : [];

        if (fromSubmissionLog.length) return fromSubmissionLog;

        return (Array.isArray(evidenceItems) ? evidenceItems : []).filter((item) => {
            const context = String(item.evidence_context || "avance").toLowerCase();
            return context === "avance";
        });
    }, [latestReviewSubmission, latestReviewSubmissionLog, evidenceItems]);

    const complementaryFollowupEvidenceItems = useMemo(() => {
        const list = Array.isArray(evidenceItems) ? evidenceItems : [];
        const latestSubmissionId = Number(latestReviewSubmission?.id || latestReviewSubmissionLog?.id || 0);
        const latestSubmissionTime = latestReviewSubmission?.created_at || latestReviewSubmissionLog?.created_at || "";
        const latestTimeMs = latestSubmissionTime ? new Date(latestSubmissionTime).getTime() : 0;

        return list
            .filter((item) => {
                const context = String(item.evidence_context || "avance").toLowerCase();
                const uploadedBy = Number(item.uploaded_by_user_id || 0);
                const progressLogId = Number(item.progress_log_id || 0);
                const createdMs = item.created_at ? new Date(item.created_at).getTime() : 0;

                return (
                    context === "avance" &&
                    uploadedBy === Number(task?.owner_user_id || 0) &&
                    (!progressLogId || progressLogId !== latestSubmissionId) &&
                    (!latestTimeMs || createdMs >= latestTimeMs)
                );
            })
            .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
    }, [evidenceItems, latestReviewSubmission, latestReviewSubmissionLog, task?.owner_user_id]);

    const hasComplementaryFollowup = complementaryFollowupEvidenceItems.length > 0;

    const pendingGeoExceptions = useMemo(() => {
        const list = Array.isArray(latestResponsibleEvidenceItems) ? latestResponsibleEvidenceItems : [];
        return list.filter((item) => isGeoExceptionRequested(item));
    }, [latestResponsibleEvidenceItems]);

    const rejectedGeoExceptions = useMemo(() => {
        const list = Array.isArray(latestResponsibleEvidenceItems) ? latestResponsibleEvidenceItems : [];
        return list.filter((item) => isGeoExceptionRejected(item));
    }, [latestResponsibleEvidenceItems]);

    const blockingGeoExceptions = useMemo(() => {
        const list = Array.isArray(latestResponsibleEvidenceItems) ? latestResponsibleEvidenceItems : [];
        return list.filter((item) => isGeoExceptionBlockingApproval(item));
    }, [latestResponsibleEvidenceItems]);

    const hasPendingGeoException = pendingGeoExceptions.length > 0;
    const hasRejectedGeoException = rejectedGeoExceptions.length > 0;
    const hasBlockingGeoException = blockingGeoExceptions.length > 0;
    const taskIsWaitingReview = ["en_revision", "pendiente_revision"].includes(String(task?.review_status || "").toLowerCase());
    const isTaskOwner = Number(loggedUserId || 0) > 0 && Number(loggedUserId || 0) === Number(task?.owner_user_id || 0);
    const isFollowupMode = isTaskOwner && taskIsWaitingReview && (hasPendingGeoException || hasRejectedGeoException || hasBlockingGeoException);
    const canUseProgressComposer = !!permissions.can_progress || isFollowupMode;

    const statusStyle = getStatusStyle(task?.status);
    const reviewStyle = getReviewStyle(task?.review_status);

    const evidenceStatusStyle = (() => {
        switch (String(evidenceSummary?.status || "").toLowerCase()) {
            case "missing_evidence":
                return {
                    bg: "#ffffff",
                    color: "#b91c1c",
                    label: "Falta evidencia",
                };
            case "missing_geo":
                return {
                    bg: "#ffffff",
                    color: "#9a3412",
                    label: "Falta georreferencia",
                };
            default:
                return {
                    bg: "#dcfce7",
                    color: "#166534",
                    label: "Evidencia OK",
                };
        }
    })();

    const reviewEvidenceBlock = useMemo(() => {
        if (!taskRequiresReview) {
            return {
                blocked: false,
                message: "",
            };
        }

        if (
            evidenceSummary?.evidence_required &&
            Number(evidenceSummary?.avance_pending_total || 0) <= 0
        ) {
            return {
                blocked: true,
                message: "No puedes enviar a revisión. Esta tarea requiere al menos una evidencia nueva de avance.",
            };
        }

        if (
            responsibleGeoEvidenceRequired &&
            Number(evidenceSummary?.avance_pending_with_geo || 0) <= 0
        ) {
            return {
                blocked: true,
                message: "No puedes enviar a revisión. Esta tarea requiere evidencia nueva de avance con georreferencia.",
            };
        }

        return {
            blocked: false,
            message: "",
        };
    }, [taskRequiresReview, evidenceSummary]);

    const reviewerEvidenceBlock = useMemo(() => {
        // C6.8.13
        // Regla operativa estable:
        // La evidencia del revisor es documental/opcional. El dictamen no debe bloquearse
        // por no adjuntar evidencia propia del revisor. La evidencia/geocerca obligatoria
        // corresponde al responsable y a la evidencia enviada a revisión.
        return {
            blocked: false,
            message: "",
            reviewerEvidenceRequired: false,
            reviewerGeoRequired: false,
            reviewerEvidenceLabel: "Opcional. Puedes adjuntar evidencia si deseas documentar el dictamen.",
            reviewerGeoLabel: "Solo se capturará si adjuntas evidencia de revisión.",
        };
    }, [permissions?.can_review]);

    const progressRequirements = useMemo(() => {
        const wantsReview = taskRequiresReview;

        const progressPercent = Number(progressForm.progress_percent || 0);
        const requiresReview = Number(task?.requires_review || 0) === 1;
        const hasReviewer = Number(task?.reviewer_user_id || 0) > 0;

        const evidenceRequired = !!evidenceSummary?.evidence_required;
        const geoRequired = responsibleGeoEvidenceRequired;

        return [
            {
                key: "progress",
                visible: true,
                ok: progressPercent > 0,
                label: "Avance capturado",
                detail: progressPercent > 0
                    ? `Avance actual a registrar: ${progressPercent}%`
                    : "Captura un avance mayor a 0%.",
            },
            {
                key: "review",
                visible: wantsReview && requiresReview,
                ok: hasReviewer,
                label: "Revisor asignado",
                detail: hasReviewer
                    ? `Revisor: ${task?.reviewer_name || "Asignado"}`
                    : "Esta tarea requiere revisión, pero no tiene revisor asignado.",
            },
            {
                key: "evidence",
                visible: wantsReview && evidenceRequired,
                ok: Number(evidenceSummary?.avance_pending_total || 0) > 0,
                label: "Evidencia del responsable",
                detail: Number(evidenceSummary?.avance_pending_total || 0) > 0
                    ? `${evidenceSummary?.avance_pending_total || 0} evidencia(s) nueva(s) de avance registrada(s).`
                    : "Agrega al menos una evidencia nueva de avance.",
            },
            {
                key: "geo",
                visible: wantsReview && geoRequired,
                ok: Number(evidenceSummary?.avance_pending_with_geo || 0) > 0,
                label: "Georreferencia del responsable",
                detail: Number(evidenceSummary?.avance_pending_with_geo || 0) > 0
                    ? `${evidenceSummary?.avance_pending_with_geo || 0} evidencia(s) nueva(s) de avance con ubicación.`
                    : "Agrega evidencia nueva de avance con ubicación.",
            },
        ].filter((item) => item.visible);
    }, [taskRequiresReview, progressForm.progress_percent, task, evidenceSummary]);

    const reviewRequirements = useMemo(() => {
        const hasReviewerDraftEvidence = hasDraftEvidence();
        const hasReviewerGeoDraft = !!evidenceForm.latitude && !!evidenceForm.longitude;

        return [
            {
                key: "comment",
                visible: true,
                ok: true,
                label: "Comentario de revisión",
                detail: "El comentario documenta el dictamen dentro de la bitácora de revisión.",
            },
            {
                key: "review_evidence",
                visible: true,
                ok: true,
                label: "Evidencia de revisión",
                detail: hasReviewerDraftEvidence
                    ? "Evidencia opcional lista para adjuntarse al dictamen."
                    : "Opcional. Puedes aprobar, solicitar cambios o rechazar sin adjuntar evidencia propia del revisor.",
            },
            {
                key: "review_geo",
                visible: true,
                ok: true,
                label: "Ubicación automática",
                detail: hasReviewerDraftEvidence
                    ? (hasReviewerGeoDraft
                        ? "Ubicación capturada para la evidencia opcional del revisor."
                        : "Solo se capturará si adjuntas evidencia de revisión que requiera ubicación.")
                    : "No requerida para dictaminar sin evidencia adicional del revisor.",
            },
        ].filter((item) => item.visible);
    }, [evidenceForm, evidenceFile, evidenceFiles]);

    const evidenceGeoStats = useMemo(() => {
        const list = Array.isArray(evidenceItems) ? evidenceItems : [];

        return list.reduce((acc, item) => {
            const status = String(item.geo_validation_status || "").toLowerCase();
            const hasGeo = Number(item.has_geo || 0) === 1 || (item.latitude && item.longitude);

            if (hasGeo) acc.with_location += 1;
            if (status === "valid") acc.valid += 1;
            if (status === "out_of_range") {
                acc.out_of_range += 1;
                const ex = String(item.geo_exception_status || "").toLowerCase();
                if (ex === "requested") acc.exception_requested += 1;
                if (ex === "rejected") acc.exception_rejected += 1;
            }
            if (status === "exception" || String(item.geo_exception_status || "").toLowerCase() === "approved") acc.exception += 1;
            if (hasGeo && (!status || status === "pending")) acc.pending += 1;

            return acc;
        }, {
            with_location: 0,
            valid: 0,
            pending: 0,
            out_of_range: 0,
            exception: 0,
            exception_requested: 0,
            exception_rejected: 0,
        });
    }, [evidenceItems]);

    const taskIntervention = useMemo(
        () => buildTaskIntervention(task, evidenceSummary, evidenceGeoStats),
        [task, evidenceSummary, evidenceGeoStats]
    );

    const scrollToTaskSection = (id) => {
        window.setTimeout(() => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
    };

    const openInterventionModal = (actionKey) => {
        const config = INTERVENTION_ACTION_CONFIG[actionKey];
        if (!config) return;
        setInterventionForm({
            comment: config.defaultComment || "",
            new_due_date: config.requiresDate ? String(task?.due_date || "") : "",
        });
        setInterventionModalAction(actionKey);
    };

    const handleInterventionAction = (actionKey) => {
        const pmoOnlyActions = ["document_block", "request_evidence", "reschedule", "exception", "resolve_block", "convert_risk", "escalate"];
        if (pmoOnlyActions.includes(actionKey) && permissions?.can_intervene_task !== true) {
            showPlatformMessage("Esta acción requiere intervención del PM del proyecto o autoridad PMO.", "warning");
            return;
        }

        switch (actionKey) {
            case "document_block":
            case "request_evidence":
            case "reschedule":
            case "exception":
            case "resolve_block":
                openInterventionModal(actionKey);
                break;
            case "upload_evidence":
                setEvidenceForm((prev) => ({ ...prev, evidence_type: "archivo", title: prev.title || `Evidencia de intervención · ${task?.wbs_code || "Tarea"}` }));
                showPlatformMessage("Abre el bloque de evidencia para cargar archivo, imagen, link o comentario.", "info");
                scrollToTaskSection("task-action-composer");
                break;
            case "escalate":
                showPlatformMessage("En preparación: escalamiento formal con responsable directivo y bitácora de decisión.", "info");
                break;
            case "resolve_block":
                openInterventionModal(actionKey);
                break;
            case "convert_risk":
                openInterventionModal(actionKey);
                break;
            default:
                showPlatformMessage("Acción de intervención no configurada.", "info");
        }
    };

    const submitInterventionAction = async () => {
        const actionKey = interventionModalAction;
        const config = INTERVENTION_ACTION_CONFIG[actionKey];
        if (!config || !task?.id || !task?.project_id) return;

        const comment = String(interventionForm.comment || "").trim();
        if (!comment) {
            showPlatformMessage("Agrega un comentario o justificación antes de guardar la intervención.", "error");
            return;
        }

        if (config.requiresDate && !interventionForm.new_due_date) {
            showPlatformMessage("Selecciona la nueva fecha compromiso antes de guardar la reprogramación.", "error");
            return;
        }

        try {
            setSavingIntervention(true);
            const payload = {
                project_id: Number(task.project_id),
                task_id: Number(task.id),
                user_id: Number(loggedUserId || 0),
                action_type: config.endpointType,
                comment,
                severity: config.severity || taskIntervention?.severity || "warning",
                source: "task_detail_intervention_panel",
                client_metadata: {
                    ui: "MyTaskDetailPage",
                    sprint: "C3.3.2.8",
                    action_key: actionKey,
                },
            };

            if (config.requiresDate) {
                payload.new_due_date = interventionForm.new_due_date;
            }

            if (config.assignToOwner && task?.owner_user_id) {
                payload.assigned_to_user_id = Number(task.owner_user_id);
            }

            const response = await fetch(`${API_BASE}/project_task_intervention_save.php`, {
                method: "POST",
                headers: buildAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(payload),
            });
            const text = await response.text();

            if (!text || !text.trim()) {
                throw new Error(`Respuesta vacía al guardar intervención (HTTP ${response.status})`);
            }

            let json;
            try {
                json = JSON.parse(text);
            } catch {
                throw new Error(`Respuesta no JSON al guardar intervención: ${text}`);
            }

            if (!response.ok || !json.success) {
                throw new Error(json.message || "No se pudo guardar la intervención");
            }

            setInterventionModalAction(null);
            setInterventionForm({ comment: "", new_due_date: "" });
            showPlatformMessage(json.message || "Intervención registrada correctamente.", "success");
            await loadInterventionHistory();

            if (config.endpointType === "reschedule_due_date" || config.endpointType === "resolve_block" || config.reloadDetail) {
                await loadDetail();
            }
        } catch (err) {
            console.error(err);
            showPlatformMessage(err.message || "No se pudo guardar la intervención.", "error");
        } finally {
            setSavingIntervention(false);
        }
    };

    const filteredEvidenceItems = useMemo(() => {
        const list = Array.isArray(evidenceItems) ? evidenceItems : [];

        return list.filter((item) => {
            const type = String(item.evidence_type || "").toLowerCase();
            const context = String(item.evidence_context || "").toLowerCase();
            const status = String(item.geo_validation_status || "").toLowerCase();
            const hasGeo = Number(item.has_geo || 0) === 1 || (item.latitude && item.longitude);

            switch (evidenceFilter) {
                case "avance":
                    return context === "avance";
                case "revision":
                    return context === "revision";
                case "imagenes":
                    return type === "imagen" || String(item.mime_type || "").startsWith("image/");
                case "archivos":
                    return type === "archivo";
                case "links":
                    return type === "link";
                case "comentarios":
                    return type === "comentario";
                case "con_geo":
                    return !!hasGeo;
                case "geo_validada":
                    return status === "valid";
                case "geo_pendiente":
                    return !!hasGeo && (!status || status === "pending");
                case "geo_fuera_rango":
                    return status === "out_of_range";
                case "geo_excepcion":
                    return status === "exception";
                case "todas":
                default:
                    return true;
            }
        });
    }, [evidenceItems, evidenceFilter]);

    const pageTone = useMemo(() => {
        if (permissions.can_review || mode === "review") return "review";
        if (String(task?.review_status || "").toLowerCase() === "con_cambios" || String(task?.review_status || "").toLowerCase() === "rechazado") return "correction";
        return "default";
    }, [permissions.can_review, mode, task?.review_status]);

    const pageTitle = useMemo(() => {
        const status = String(task?.status || "").toLowerCase();
        const reviewStatus = String(task?.review_status || "").toLowerCase();

        if (status === "terminada") return "Tarea terminada";
        if (status === "cancelada") return "Tarea cancelada";

        if (["en_revision", "pendiente_revision"].includes(reviewStatus)) {
            return "Tarea en revisión";
        }

        if (["rechazado", "con_cambios"].includes(reviewStatus)) {
            return "Tarea con correcciones";
        }

        if (status === "pendiente") return "Tarea pendiente";
        if (status === "en_proceso") return "Tarea en atención";
        if (status === "bloqueada") return "Tarea bloqueada";

        return "Detalle de tarea";
    }, [task?.status, task?.review_status]);
    //    }, [permissions.can_review, permissions.can_progress, permissions.can_view_followup, mode]);

    const taskActionMessage = useMemo(() => getTaskActionMessage(task, permissions), [task, permissions]);
    const effectiveTaskActionMessage = (() => {
        const reviewStatus = String(task?.review_status || "").toLowerCase();
        const isCorrectionState = ["con_cambios", "rechazado"].includes(reviewStatus);

        if (taskIntervention?.requiresIntervention && taskIntervention?.severity === "critical") {
            return "Intervención crítica. La tarea presenta bloqueo, vencimiento o falta de evidencia; documenta causa, solicita soporte, reprograma o escala la decisión.";
        }

        if (isCorrectionState && !permissions.can_review) {
            return "Corrección requerida. El revisor solicitó cambios; corrige la entrega, adjunta nueva evidencia y vuelve a enviar a revisión.";
        }

        if (hasRejectedGeoException) {
            if (permissions.can_review) {
                return "Evidencia geográfica rechazada. La aprobación está bloqueada; cierra la revisión solicitando cambios o rechazando el avance.";
            }
            return "Seguimiento disponible. El revisor debe cerrar la revisión para que puedas corregir formalmente y reenviar evidencia.";
        }

        if (hasPendingGeoException) {
            if (permissions.can_review) {
                return "Excepción geográfica pendiente. Resuelve la excepción antes de aprobar; también puedes solicitar cambios o rechazar el avance.";
            }
            return "Excepción geográfica solicitada. Puedes agregar seguimiento complementario mientras el revisor resuelve la excepción.";
        }

        if (isRouteBlocked) {
            return isApprovedPartial
                ? "Aprobado parcial, pero la ruta sigue bloqueada. El avance anterior fue validado; no registres nuevo avance hasta liberar la predecesora."
                : "Ruta bloqueada. Esta tarea no debe avanzar hasta resolver la dependencia o condición que la detiene.";
        }

        return taskActionMessage;
    })();

    const cognitiveActionFocus = useMemo(() => {
        const status = String(task?.status || "").toLowerCase();
        const reviewStatus = String(task?.review_status || "").toLowerCase();
        const correctionState = ["con_cambios", "rechazado"].includes(reviewStatus);

        if (taskIntervention?.requiresIntervention && !["terminada", "cancelada"].includes(status)) {
            return {
                tone: taskIntervention.severity === "critical" || taskIntervention.severity === "high" ? "blocked" : "warning",
                icon: "!",
                kicker: taskIntervention.severity === "critical" ? "Intervención crítica" : "Intervención requerida",
                title: taskIntervention.severity === "critical" ? "Tarea crítica detectada" : "Tarea con señales de atención",
                detail: responsibleGeoEvidenceRequired
                    ? "ORBIS detectó bloqueo, vencimiento, falta de evidencia o georreferencia. La acción recomendada es documentar, solicitar evidencia, reprogramar, escalar o convertir el bloqueo en riesgo."
                    : "ORBIS detectó bloqueo, vencimiento o falta de evidencia documental. La acción recomendada es documentar, solicitar evidencia, reprogramar, escalar o convertir el bloqueo en riesgo.",
                showPrimaryAction: true,
                primaryLabel: "Ver intervención",
            };
        }

        if (status === "terminada") {
            return {
                tone: "success",
                icon: "✓",
                kicker: "Sin acción pendiente",
                title: "Resultado cerrado",
                detail: "La tarea quedó terminada. La trazabilidad, evidencia y ciclos de revisión quedan disponibles para consulta.",
                showPrimaryAction: false,
            };
        }

        if (hasRejectedGeoException) {
            return {
                tone: permissions.can_review ? "danger" : "warning",
                icon: "!",
                kicker: permissions.can_review ? "Acción del revisor" : "Seguimiento disponible",
                title: permissions.can_review ? "Evidencia geográfica rechazada" : "Esperando cierre de revisión",
                detail: permissions.can_review
                    ? "La aprobación está bloqueada. Debes cerrar la revisión solicitando cambios o rechazando el avance para que el responsable pueda corregir."
                    : "Puedes agregar seguimiento complementario, pero la corrección formal depende de que el revisor cierre la decisión.",
                showPrimaryAction: true,
                primaryLabel: permissions.can_review ? "Ir a decisión" : "Agregar seguimiento",
            };
        }

        if (hasPendingGeoException) {
            return {
                tone: "warning",
                icon: "!",
                kicker: permissions.can_review ? "Acción del revisor" : "Seguimiento disponible",
                title: "Excepción geográfica pendiente",
                detail: permissions.can_review
                    ? "Resuelve la excepción antes de aprobar. También puedes solicitar cambios o rechazar el avance si la evidencia no es suficiente."
                    : "La excepción ya fue solicitada. Puedes agregar información complementaria mientras el revisor decide.",
                showPrimaryAction: true,
                primaryLabel: permissions.can_review ? "Resolver excepción" : "Agregar seguimiento",
            };
        }

        if (isRouteBlocked) {
            return {
                tone: "warning",
                icon: "⌁",
                kicker: "Ruta operativa",
                title: "Ruta bloqueada",
                detail: isApprovedPartial
                    ? "El último avance fue aprobado, pero la ruta sigue bloqueada por dependencia. No registres nuevo avance hasta liberar la predecesora."
                    : "La tarea no debe avanzar hasta resolver la dependencia o condición que la detiene.",
                showPrimaryAction: false,
            };
        }

        if (correctionState && canUseProgressComposer) {
            return {
                tone: "correction",
                icon: "↩",
                kicker: "Acción del responsable",
                title: "Corrección requerida",
                detail: "El revisor solicitó cambios. Corrige la entrega, agrega el comentario correspondiente y vuelve a enviar el avance a revisión.",
                showPrimaryAction: true,
                primaryLabel: "Corregir y reenviar",
            };
        }

        if (["en_revision", "pendiente_revision"].includes(reviewStatus) && permissions.can_review) {
            return {
                tone: "review",
                icon: "◆",
                kicker: "Acción del revisor",
                title: "Revisión pendiente",
                detail: "El responsable ya envió avance. Revisa comentario, evidencia y ubicación antes de aprobar, solicitar cambios o rechazar.",
                showPrimaryAction: true,
                primaryLabel: "Revisar avance",
            };
        }

        if (["en_revision", "pendiente_revision"].includes(reviewStatus)) {
            return {
                tone: "neutral",
                icon: "…",
                kicker: "En espera",
                title: "Esperando decisión del revisor",
                detail: "El avance ya fue enviado. Puedes consultar la trazabilidad o agregar seguimiento si existe una excepción geográfica pendiente.",
                showPrimaryAction: isFollowupMode,
                primaryLabel: "Agregar seguimiento",
            };
        }

        if (canUseProgressComposer) {
            return {
                tone: "review",
                icon: "+",
                kicker: "Acción del responsable",
                title: isApprovedPartial ? "Registrar siguiente avance" : "Registrar resultado",
                detail: isApprovedPartial
                    ? "El avance anterior fue aprobado parcialmente. Puedes registrar el siguiente avance operativo cuando corresponda."
                    : "Actualiza el avance, agrega comentario y adjunta evidencia en un solo flujo operativo.",
                showPrimaryAction: true,
                primaryLabel: isApprovedPartial ? "Registrar avance" : progressButtonLabel,
            };
        }

        return {
            tone: "neutral",
            icon: "i",
            kicker: "Modo consulta",
            title: "Sin acción pendiente",
            detail: "Consulta el estado, la evidencia y los ciclos de validación registrados para esta tarea.",
            showPrimaryAction: false,
        };
    }, [
        task?.status,
        task?.review_status,
        taskIntervention,
        permissions.can_review,
        canUseProgressComposer,
        hasRejectedGeoException,
        hasPendingGeoException,
        isRouteBlocked,
        isApprovedPartial,
        isFollowupMode,
        progressButtonLabel,
    ]);

    // Mensajería propia de Plataforma PM.
    // Evita alert() del navegador, porque ese diálogo muestra el dominio como título.
    const showPlatformMessage = (message, type = "info") => {
        const cleanMessage = String(message || "").trim() || "Acción registrada correctamente.";
        setPlatformToast({
            message: cleanMessage,
            type,
            id: Date.now(),
        });

        window.clearTimeout(window.__platformPmToastTimer);
        window.__platformPmToastTimer = window.setTimeout(() => {
            setPlatformToast(null);
        }, 4200);
    };

    const loadDetail = async () => {
        try {
            setLoading(true);
            setError("");

            const url = `${API_BASE}/my_task_detail.php?task_id=${encodeURIComponent(taskId)}&user_id=${encodeURIComponent(loggedUserId)}&t=${Date.now()}`;
            const response = await fetch(url, { headers: buildAuthHeaders() });
            const text = await response.text();

            if (!text || !text.trim()) {
                throw new Error(`Respuesta vacía (HTTP ${response.status})`);
            }

            let json;
            try {
                json = JSON.parse(text);
            } catch {
                throw new Error(`Respuesta no JSON: ${text}`);
            }

            if (!response.ok || !json.success) {
                throw new Error(json.message || "No se pudo cargar el detalle de la tarea");
            }

            setDetail(json);
            setProgressForm({
                progress_percent: String(json.task?.progress_percent ?? "0"),
                comment: "",
                enviar_revision: Number(json.task?.requires_review || 0) === 1,
            });
            setReviewForm({ comment: "" });

            // Limpia cualquier error anterior después de una carga exitosa
            setError("");
        } catch (err) {
            console.error(err);
            setError(err.message || "Error al cargar la tarea");
        } finally {
            setLoading(false);
        }
    };
    const loadInterventionHistory = async () => {
        try {
            if (!taskId) return;

            setInterventionHistoryLoading(true);
            const url = `${API_BASE}/project_task_interventions_list.php?task_id=${encodeURIComponent(taskId)}&t=${Date.now()}`;
            const response = await fetch(url, { headers: buildAuthHeaders() });
            const text = await response.text();

            if (!text || !text.trim()) {
                throw new Error(`Respuesta vacía en bitácora de intervención (HTTP ${response.status})`);
            }

            let json;
            try {
                json = JSON.parse(text);
            } catch {
                throw new Error(`Respuesta no JSON en bitácora de intervención: ${text}`);
            }

            if (!response.ok || !json.success) {
                throw new Error(json.message || "No se pudo cargar la bitácora de intervención");
            }

            setInterventionHistory(json);
        } catch (err) {
            console.error(err);
            setInterventionHistory({ success: false, items: [], stats: {}, message: err.message || "No se pudo cargar la bitácora de intervención" });
        } finally {
            setInterventionHistoryLoading(false);
        }
    };

    const loadEvidence = async () => {
        try {
            if (!taskId) return;

            setEvidenceLoading(true);

            const projectId = detail?.task?.project_id || "";
            const url = `${API_BASE}/task_evidence_list.php?task_id=${encodeURIComponent(taskId)}${projectId ? `&project_id=${encodeURIComponent(projectId)}` : ""}&t=${Date.now()}`;

            const response = await fetch(url, {
                headers: buildAuthHeaders(),
            });

            const text = await response.text();

            if (!text || !text.trim()) {
                throw new Error(`Respuesta vacía de evidencias (HTTP ${response.status})`);
            }

            let json;
            try {
                json = JSON.parse(text);
            } catch {
                throw new Error(`Respuesta de evidencias no JSON: ${text}`);
            }

            if (!response.ok || !json.success) {
                throw new Error(json.message || "No se pudieron cargar las evidencias");
            }

            setEvidenceItems(Array.isArray(json.items) ? json.items : []);
            setEvidenceSummary(json.summary || {
                total: 0,
                with_geo: 0,

                avance_total: 0,
                avance_with_geo: 0,
                avance_pending_total: 0,
                avance_pending_with_geo: 0,

                revision_total: 0,
                revision_with_geo: 0,
                revision_pending_total: 0,
                revision_pending_with_geo: 0,

                evidence_required: false,
                geo_evidence_required: false,
                status: "ok",
            });
        } catch (err) {
            console.error(err);
            showPlatformMessage(err.message || "Error al cargar evidencias");
        } finally {
            setEvidenceLoading(false);
        }
    };

    const handleEvidenceSubmit = async () => {
        try {
            if (!task) return;

            const title = String(evidenceForm.title || "").trim();
            const description = String(evidenceForm.description || "").trim();
            const externalUrl = String(evidenceForm.external_url || "").trim();
            const evidenceType = String(evidenceForm.evidence_type || "comentario").trim();

            if (!title) {
                showPlatformMessage("El título de la evidencia es requerido.");
                return;
            }

            if (evidenceType === "link" && !externalUrl) {
                showPlatformMessage("Para una evidencia tipo link debes capturar el enlace.");
                return;
            }
            if (["archivo", "imagen"].includes(evidenceType) && !evidenceFile) {
                showPlatformMessage("Selecciona un archivo para subir.");
                return;
            }
            const geoCheck = await ensureEvidenceGeoIfRequired({
                required: responsibleGeoEvidenceRequired,
            });

            if (!geoCheck.ok) {
                showPlatformMessage(geoCheck.message);
                return;
            }

            setSavingEvidence(true);

            let response;

            if (["archivo", "imagen"].includes(evidenceType)) {
                const formData = new FormData();

                formData.append("project_id", String(task.project_id));
                formData.append("task_id", String(task.id));
                formData.append("uploaded_by_user_id", String(loggedUserId));
                formData.append(
                    "evidence_context",
                    permissions.can_review ? "revision" : "avance"
                );
                formData.append("title", title);
                formData.append("description", description);
                formData.append("file", evidenceFile);

                if (geoCheck.data.latitude && geoCheck.data.longitude) {
                    formData.append("latitude", geoCheck.data.latitude);
                    formData.append("longitude", geoCheck.data.longitude);
                    formData.append("geo_accuracy", geoCheck.data.geo_accuracy || "");
                    formData.append("geo_source", geoCheck.data.geo_source || "");
                }

                response = await fetch(`${API_BASE}/task_evidence_upload.php?t=${Date.now()}`, {
                    method: "POST",
                    headers: buildAuthHeaders(),
                    body: formData,
                });
            } else {
                response = await fetch(`${API_BASE}/task_evidence_save.php?t=${Date.now()}`, {
                    method: "POST",
                    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
                    body: JSON.stringify({
                        project_id: Number(task.project_id),
                        task_id: Number(task.id),
                        uploaded_by_user_id: Number(loggedUserId),
                        evidence_type: evidenceType,
                        evidence_context: permissions.can_review ? "revision" : "avance",
                        title,
                        description,
                        external_url: externalUrl,
                        latitude: geoCheck.data.latitude || "",
                        longitude: geoCheck.data.longitude || "",
                        geo_accuracy: geoCheck.data.geo_accuracy || "",
                        geo_source: geoCheck.data.geo_source || "",
                        geo_exception_reason: String(evidenceForm.geo_exception_reason || "").trim(),
                    }),
                });
            }

            const text = await response.text();

            let json;
            try {
                json = JSON.parse(text);
            } catch {
                throw new Error(`Respuesta no JSON: ${text}`);
            }

            if (!response.ok || !json.success) {
                throw new Error(json.message || "No se pudo registrar la evidencia");
            }

            showPlatformMessage(json.message || "Evidencia registrada correctamente");

            setEvidenceForm({
                evidence_type: "comentario",
                title: "",
                description: "",
                external_url: "",
                latitude: "",
                longitude: "",
                geo_accuracy: "",
                geo_source: "",
                geo_exception_reason: "",
            });

            await loadEvidence();
        } catch (err) {
            console.error(err);
            if (isBackendGeoRequiredMessage(err.message)) {
                setForceResponsibleGeoRequired(true);
            }
            showPlatformMessage(err.message || "Error al registrar evidencia");
        } finally {
            setSavingEvidence(false);
        }
    };


    const handleReviewEvidenceSubmit = async () => {
        try {
            if (!task) return;

            const title = String(evidenceForm.title || "").trim();
            const description = String(evidenceForm.description || "").trim();
            const externalUrl = String(evidenceForm.external_url || "").trim();
            const evidenceType = String(evidenceForm.evidence_type || "comentario").trim();

            if (!title) {
                showPlatformMessage("El título de la evidencia de revisión es requerido.");
                return;
            }

            if (evidenceType === "link" && !externalUrl) {
                showPlatformMessage("Para una evidencia tipo link debes capturar el enlace.");
                return;
            }

            if (["archivo", "imagen"].includes(evidenceType) && !evidenceFile) {
                showPlatformMessage("Selecciona un archivo para subir.");
                return;
            }

            const reviewerGeoRequired =
                Number(task?.reviewer_geo_evidence_required || 0) === 1;

            const geoCheck = await ensureEvidenceGeoIfRequired({
                required: reviewerGeoRequired,
            });

            if (!geoCheck.ok) {
                showPlatformMessage(geoCheck.message);
                return;
            }

            setSavingEvidence(true);

            let response;

            if (["archivo", "imagen"].includes(evidenceType)) {
                const formData = new FormData();

                formData.append("project_id", String(task.project_id));
                formData.append("task_id", String(task.id));
                formData.append("uploaded_by_user_id", String(loggedUserId));
                formData.append("evidence_context", "revision");
                formData.append("title", title);
                formData.append("description", description);
                formData.append("file", evidenceFile);

                if (geoCheck.data.latitude && geoCheck.data.longitude) {
                    formData.append("latitude", geoCheck.data.latitude);
                    formData.append("longitude", geoCheck.data.longitude);
                    formData.append("geo_accuracy", geoCheck.data.geo_accuracy || "");
                    formData.append("geo_source", geoCheck.data.geo_source || "");
                }

                response = await fetch(`${API_BASE}/task_evidence_upload.php?t=${Date.now()}`, {
                    method: "POST",
                    headers: buildAuthHeaders(),
                    body: formData,
                });
            } else {
                response = await fetch(`${API_BASE}/task_evidence_save.php?t=${Date.now()}`, {
                    method: "POST",
                    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
                    body: JSON.stringify({
                        project_id: Number(task.project_id),
                        task_id: Number(task.id),
                        uploaded_by_user_id: Number(loggedUserId),
                        evidence_type: evidenceType,
                        evidence_context: "revision",
                        title,
                        description,
                        external_url: externalUrl,
                        latitude: geoCheck.data.latitude || "",
                        longitude: geoCheck.data.longitude || "",
                        geo_accuracy: geoCheck.data.geo_accuracy || "",
                        geo_source: geoCheck.data.geo_source || "",
                        geo_exception_reason: String(evidenceForm.geo_exception_reason || "").trim(),
                    }),
                });
            }

            const text = await response.text();

            let json;
            try {
                json = JSON.parse(text);
            } catch {
                throw new Error(`Respuesta no JSON: ${text}`);
            }

            if (!response.ok || !json.success) {
                throw new Error(json.message || "No se pudo registrar la evidencia de revisión");
            }

            showPlatformMessage(json.message || "Evidencia de revisión registrada correctamente");

            setEvidenceForm({
                evidence_type: "comentario",
                title: "",
                description: "",
                external_url: "",
                latitude: "",
                longitude: "",
                geo_accuracy: "",
                geo_source: "",
                geo_exception_reason: "",
            });

            setEvidenceFile(null);

            await loadEvidence();
        } catch (err) {
            console.error(err);
            showPlatformMessage(err.message || "Error al registrar evidencia de revisión");
        } finally {
            setSavingEvidence(false);
        }
    };


    const handleCaptureGeo = () => {
        if (!navigator.geolocation) {
            showPlatformMessage("Tu navegador no permite capturar ubicación.");
            return;
        }

        setCapturingGeo(true);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude, accuracy } = position.coords;

                setEvidenceForm((prev) => ({
                    ...prev,
                    latitude: String(latitude),
                    longitude: String(longitude),
                    geo_accuracy: accuracy ? String(Math.round(accuracy * 100) / 100) : "",
                    geo_source: "browser_geolocation",
                }));

                setCapturingGeo(false);
            },
            (error) => {
                console.error(error);

                let message = "No se pudo capturar la ubicación.";

                if (error.code === 1) {
                    message = "Permiso de ubicación denegado. Activa el permiso del navegador para continuar.";
                } else if (error.code === 2) {
                    message = "No se pudo determinar la ubicación actual.";
                } else if (error.code === 3) {
                    message = "La captura de ubicación tardó demasiado.";
                }

                showPlatformMessage(message);
                setCapturingGeo(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 12000,
                maximumAge: 0,
            }
        );
    };

    const captureGeoSilently = () => {
        return new Promise((resolve) => {
            if (!navigator.geolocation) {
                resolve({
                    ok: false,
                    message: "Tu navegador no soporta geolocalización.",
                });
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        ok: true,
                        latitude: String(position.coords.latitude),
                        longitude: String(position.coords.longitude),
                        geo_accuracy: String(position.coords.accuracy || ""),
                        geo_source: "browser_geolocation_auto",
                    });
                },
                (error) => {
                    let message = "No se pudo capturar la ubicación.";

                    if (error.code === 1) {
                        message = "No se pudo capturar la ubicación porque el navegador no tiene permiso.";
                    } else if (error.code === 2) {
                        message = "No se pudo determinar la ubicación del dispositivo.";
                    } else if (error.code === 3) {
                        message = "La solicitud de ubicación tardó demasiado.";
                    }

                    resolve({
                        ok: false,
                        message,
                    });
                },
                {
                    enableHighAccuracy: true,
                    timeout: 12000,
                    maximumAge: 0,
                }
            );
        });
    };

    const ensureEvidenceGeoIfRequired = async ({ required = false } = {}) => {
        const hasGeo = !!evidenceForm.latitude && !!evidenceForm.longitude;

        if (hasGeo) {
            return {
                ok: true,
                data: {
                    latitude: evidenceForm.latitude,
                    longitude: evidenceForm.longitude,
                    geo_accuracy: evidenceForm.geo_accuracy || "",
                    geo_source: evidenceForm.geo_source || "",
                },
            };
        }

        if (!required) {
            return {
                ok: true,
                data: {
                    latitude: "",
                    longitude: "",
                    geo_accuracy: "",
                    geo_source: "",
                },
            };
        }

        setCapturingGeo(true);

        const geoResult = await captureGeoSilently();

        setCapturingGeo(false);

        if (!geoResult.ok) {
            return {
                ok: false,
                message:
                    geoResult.message ||
                    "Esta evidencia requiere georreferencia. Autoriza la ubicación del navegador para continuar.",
            };
        }

        setEvidenceForm((prev) => ({
            ...prev,
            latitude: geoResult.latitude,
            longitude: geoResult.longitude,
            geo_accuracy: geoResult.geo_accuracy,
            geo_source: geoResult.geo_source,
        }));

        return {
            ok: true,
            data: {
                latitude: geoResult.latitude,
                longitude: geoResult.longitude,
                geo_accuracy: geoResult.geo_accuracy,
                geo_source: geoResult.geo_source,
            },
        };
    };


    function getDraftEvidenceType() {
        return String(evidenceForm.evidence_type || "comentario").trim() || "comentario";
    }

    function hasDraftEvidence() {
        const evidenceType = getDraftEvidenceType();
        const title = String(evidenceForm.title || "").trim();
        const description = String(evidenceForm.description || "").trim();
        const externalUrl = String(evidenceForm.external_url || "").trim();

        if (evidenceFile) return true;
        if (Array.isArray(evidenceFiles) && evidenceFiles.length > 0) return true;
        if (title || description) return true;
        if (evidenceType === "link" && externalUrl) return true;

        return false;
    }

    const submitEvidenceFromComposer = async ({
        context = "avance",
        required = false,
        geoRequired = false,
        fallbackComment = "",
    } = {}) => {
        if (!task) return { ok: false, message: "No hay tarea cargada." };

        const evidenceType = getDraftEvidenceType();
        const title = String(evidenceForm.title || "").trim() ||
            `${context === "revision" ? "Evidencia de revision" : "Evidencia de avance"} · ${task?.wbs_code || task?.id || "tarea"}`;
        const description = String(evidenceForm.description || "").trim() || String(fallbackComment || "").trim();
        const externalUrl = String(evidenceForm.external_url || "").trim();
        const hasAnyDraft = hasDraftEvidence() || !!description;

        if (!required && !hasAnyDraft) {
            return { ok: true, skipped: true };
        }

        if (required && !hasAnyDraft) {
            return {
                ok: false,
                message: context === "revision"
                    ? "Falta evidencia de revision para poder decidir."
                    : "Falta evidencia para poder enviar el avance.",
            };
        }

        if (evidenceType === "link" && !externalUrl) {
            return { ok: false, message: "Agrega el enlace de la evidencia antes de continuar." };
        }

        const filesToUpload = Array.isArray(evidenceFiles) && evidenceFiles.length > 0
            ? evidenceFiles
            : (evidenceFile ? [{ file: evidenceFile, evidence_type: evidenceType }] : []);

        if (["archivo", "imagen"].includes(evidenceType) && filesToUpload.length === 0) {
            return { ok: false, message: "Selecciona un archivo o imagen antes de continuar." };
        }

        const geoCheck = await ensureEvidenceGeoIfRequired({ required: !!geoRequired });

        if (!geoCheck.ok) {
            return { ok: false, message: geoCheck.message };
        }

        setSavingEvidence(true);

        try {
            let response;

            const saveStructuredEvidence = async ({ type, externalUrlValue = "" }) => {
                const structuredResponse = await fetch(`${API_BASE}/task_evidence_save.php?t=${Date.now()}`, {
                    method: "POST",
                    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
                    body: JSON.stringify({
                        project_id: Number(task.project_id),
                        task_id: Number(task.id),
                        uploaded_by_user_id: Number(loggedUserId),
                        evidence_type: type,
                        evidence_context: context,
                        title,
                        description: description || title,
                        external_url: externalUrlValue,
                        latitude: geoCheck.data.latitude || "",
                        longitude: geoCheck.data.longitude || "",
                        geo_accuracy: geoCheck.data.geo_accuracy || "",
                        geo_source: geoCheck.data.geo_source || "",
                        geo_exception_reason: String(evidenceForm.geo_exception_reason || "").trim(),
                    }),
                });

                const structuredText = await structuredResponse.text();
                let structuredJson;
                try {
                    structuredJson = JSON.parse(structuredText);
                } catch {
                    throw new Error(`Respuesta no JSON: ${structuredText}`);
                }

                if (!structuredResponse.ok || !structuredJson.success) {
                    throw new Error(structuredJson.message || "No se pudo registrar la evidencia");
                }
            };

            if (filesToUpload.length > 0) {
                for (const item of filesToUpload) {
                    const file = item.file || item;
                    const itemType = item.evidence_type || (String(file.type || "").startsWith("image/") ? "imagen" : "archivo");
                    const formData = new FormData();

                    formData.append("project_id", String(task.project_id));
                    formData.append("task_id", String(task.id));
                    formData.append("uploaded_by_user_id", String(loggedUserId));
                    formData.append("evidence_type", itemType);
                    formData.append("evidence_context", context);
                    formData.append("title", title || file.name);
                    formData.append("description", description || title || file.name);
                    formData.append("file", file);

                    if (geoCheck.data.latitude && geoCheck.data.longitude) {
                        formData.append("latitude", geoCheck.data.latitude);
                        formData.append("longitude", geoCheck.data.longitude);
                        formData.append("geo_accuracy", geoCheck.data.geo_accuracy || "");
                        formData.append("geo_source", geoCheck.data.geo_source || "");
                    }

                    if (String(evidenceForm.geo_exception_reason || "").trim()) {
                        formData.append("geo_exception_reason", String(evidenceForm.geo_exception_reason || "").trim());
                    }

                    response = await fetch(`${API_BASE}/task_evidence_upload.php?t=${Date.now()}`, {
                        method: "POST",
                        headers: buildAuthHeaders(),
                        body: formData,
                    });

                    const text = await response.text();
                    let json;
                    try {
                        json = JSON.parse(text);
                    } catch {
                        throw new Error(`Respuesta no JSON: ${text}`);
                    }

                    if (!response.ok || !json.success) {
                        throw new Error(json.message || "No se pudo registrar la evidencia");
                    }
                }

                // Si el usuario adjunta archivos y tambien captura un link, el link debe guardarse
                // como evidencia adicional. Antes se perdia porque solo se guardaba cuando no habia archivos.
                if (externalUrl) {
                    await saveStructuredEvidence({ type: "link", externalUrlValue: externalUrl });
                }
            } else {
                await saveStructuredEvidence({
                    type: evidenceType,
                    externalUrlValue: evidenceType === "link" ? externalUrl : "",
                });
            }

            setEvidenceForm({
                evidence_type: "comentario",
                title: "",
                description: "",
                external_url: "",
                latitude: "",
                longitude: "",
                geo_accuracy: "",
                geo_source: "",
                geo_exception_reason: "",
            });
            setEvidenceFile(null);
            setEvidenceFiles([]);
            setGeoExceptionRequired(false);
            await loadEvidence();

            return { ok: true };
        } catch (err) {
            console.error(err);
            const message = err.message || "Error al registrar evidencia";
            const backendRequiresGeo = isBackendGeoRequiredMessage(message);
            if (backendRequiresGeo && context === "avance") {
                setForceResponsibleGeoRequired(true);
            }
            const needsGeoException = /excepci[oó]n/i.test(message) || /fuera de rango/i.test(message);
            return {
                ok: false,
                message: backendRequiresGeo
                    ? "La tarea requiere ubicación automática. Vuelve a guardar y permite la ubicación del navegador para adjuntarla a la evidencia."
                    : message,
                needsGeoException,
                backendRequiresGeo,
            };
        } finally {
            setSavingEvidence(false);
        }
    };

    const handleComposerProgressSubmit = async () => {
        setActionError("");

        const progressToSave = Number(progressForm.progress_percent || 0);
        const currentProgress = Number(task?.progress_percent || 0);

        if (progressToSave < currentProgress) {
            setActionError("El nuevo avance no puede ser menor al avance actual aprobado.");
            return;
        }

        if (isRouteBlocked && progressToSave > currentProgress) {
            setActionError("Ruta bloqueada: puedes agregar comentario o evidencia de seguimiento, pero no aumentar avance hasta liberar la predecesora.");
            setProgressForm((prev) => ({ ...prev, progress_percent: String(currentProgress) }));
            return;
        }

        if (!String(progressForm.comment || "").trim()) {
            setActionError("Agrega un comentario breve para explicar el resultado registrado.");
            return;
        }

        const evidenceRequired = taskRequiresReview && taskRequiresResponsibleEvidence;
        const geoRequired = responsibleGeoEvidenceRequired;

        const evidenceResult = await submitEvidenceFromComposer({
            context: "avance",
            required: evidenceRequired,
            geoRequired,
            fallbackComment: progressForm.comment,
        });

        if (!evidenceResult.ok) {
            if (evidenceResult.needsGeoException) {
                setGeoExceptionRequired(true);
            }
            setActionError(evidenceResult.message || "No se pudo registrar la evidencia.");
            return;
        }

        if (isFollowupMode) {
            showPlatformMessage("Seguimiento registrado correctamente. El avance se mantiene sin cambios mientras el revisor cierra la decisión.", "success");
            await loadDetail();
            await loadEvidence();
            return;
        }

        await handleProgressSubmit({ skipLocalEvidenceCheck: true });
    };

    const handleComposerReviewSubmit = async (accion) => {
        setActionError("");

        const normalizedAction = String(accion || "").toLowerCase();

        const isNegativeDecision =
            normalizedAction === "solicitar_cambios" ||
            normalizedAction === "rechazar";

        if (accion === "aprobar" && hasBlockingGeoException) {
            setActionError(
                hasRejectedGeoException
                    ? "No puedes aprobar este avance porque contiene evidencia geográfica rechazada. Solicita cambios o rechaza el avance."
                    : "Existe una excepción geográfica pendiente. Autoriza o rechaza la excepción antes de aprobar el avance."
            );
            return;
        }

        if (!String(reviewForm.comment || "").trim() && accion !== "aprobar") {
            setActionError("Agrega un comentario para explicar la decisión del revisor.");
            return;
        }

        // C6.8.13
        // El comentario del revisor NO debe convertirse automáticamente en evidencia.
        // Solo se registra evidencia de revisión si el revisor realmente adjuntó imagen/documento/link
        // o capturó información en el bloque de evidencia. El dictamen debe poder aplicarse
        // sin evidencia propia del revisor.
        const shouldAttachReviewerEvidence = hasDraftEvidence();

        if (shouldAttachReviewerEvidence) {
            const reviewerGeoRequired = Number(task?.reviewer_geo_evidence_required || 0) === 1;

            const evidenceResult = await submitEvidenceFromComposer({
                context: "revision",
                required: false,
                geoRequired: reviewerGeoRequired,
                fallbackComment: reviewForm.comment,
            });

            if (!evidenceResult.ok) {
                const evidenceMessage = evidenceResult.message || "No se pudo registrar la evidencia de revisión.";
                const isGeoEvidenceProblem =
                    !!evidenceResult.needsGeoException ||
                    /ubicaci[oó]n|georreferencia|geo|fuera de rango|radio autorizado/i.test(evidenceMessage);

                if (isNegativeDecision && isGeoEvidenceProblem) {
                    showPlatformMessage(
                        "La evidencia opcional del revisor no se adjuntó por validación geográfica, pero la decisión negativa se registrará para cerrar el ciclo.",
                        "warning"
                    );
                    setEvidenceFiles([]);
                    setEvidenceFile(null);
                } else {
                    setActionError(evidenceMessage);
                    return;
                }
            }
        }

        await handleReviewSubmit(accion, { skipLocalEvidenceCheck: true });
    };

    useEffect(() => {
        loadDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskId, loggedUserId]);

    useEffect(() => {
        if (detail?.task?.id) {
            loadEvidence();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detail?.task?.id]);

    useEffect(() => {
        if (detail?.task?.id) {
            loadInterventionHistory();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detail?.task?.id]);

    useEffect(() => {
        if (!task) return;

        setReviewerForm({
            reviewer_user_id: task?.reviewer_user_id ? String(task.reviewer_user_id) : "",
            requires_review: Number(task?.requires_review || 0) === 1,
        });
    }, [task?.id, task?.reviewer_user_id, task?.requires_review]);

    useEffect(() => {
        if (!task) return;

        setDueDateForm({
            start_date: task?.start_date || "",
            due_date: task?.due_date || "",
        });
    }, [task?.id, task?.start_date, task?.due_date]);


    const handleAssignReviewer = async () => {
        try {
            if (!task) return;

            if (!reviewerForm.reviewer_user_id) {
                showPlatformMessage("Selecciona un revisor.");
                return;
            }

            setSavingReviewer(true);

            const response = await fetch(`${API_BASE}/project_task_assign_reviewer.php?t=${Date.now()}`, {
                method: "POST",
                headers: buildAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    project_id: Number(task.project_id),
                    task_id: Number(task.id),
                    reviewer_user_id: Number(reviewerForm.reviewer_user_id),
                    requires_review: reviewerForm.requires_review ? 1 : 0,
                }),
            });

            const text = await response.text();

            let json;
            try {
                json = JSON.parse(text);
            } catch {
                throw new Error(`Respuesta no JSON: ${text}`);
            }

            if (!response.ok || !json.success) {
                throw new Error(json.message || json.detail || "No se pudo asignar el revisor");
            }

            showPlatformMessage(json.message || "Revisor asignado correctamente");

            await loadDetail();
            await loadEvidence();
        } catch (err) {
            console.error(err);
            showPlatformMessage(err.message || "Error al asignar revisor");
        } finally {
            setSavingReviewer(false);
        }
    };

    const handleAssignDueDate = async () => {
        try {
            if (!task) return;

            if (!dueDateForm.due_date) {
                showPlatformMessage("Selecciona una fecha objetivo.");
                return;
            }

            if (
                dueDateForm.start_date &&
                dueDateForm.due_date &&
                dueDateForm.due_date < dueDateForm.start_date
            ) {
                showPlatformMessage("La fecha objetivo no puede ser anterior a la fecha de inicio.");
                return;
            }

            setSavingDueDate(true);

            const response = await fetch(`${API_BASE}/project_task_assign_due_date.php?t=${Date.now()}`, {
                method: "POST",
                headers: buildAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    project_id: Number(task.project_id),
                    task_id: Number(task.id),
                    start_date: dueDateForm.start_date || null,
                    due_date: dueDateForm.due_date,
                }),
            });

            const text = await response.text();

            let json;
            try {
                json = JSON.parse(text);
            } catch {
                throw new Error(`Respuesta no JSON: ${text}`);
            }

            if (!response.ok || !json.success) {
                throw new Error(json.message || json.detail || "No se pudo asignar la fecha objetivo");
            }

            showPlatformMessage(json.message || "Fecha objetivo asignada correctamente");

            await loadDetail();
            await loadEvidence();
        } catch (err) {
            console.error(err);
            showPlatformMessage(err.message || "Error al asignar fecha objetivo");
        } finally {
            setSavingDueDate(false);
        }
    };


    const executeGeoExceptionDecision = async (evidence, decision) => {
        try {
            if (!task || !evidence?.id) return;

            setDecidingExceptionId(evidence.id);

            const response = await fetch(`${API_BASE}/project_task_geo_exception_decide.php?t=${Date.now()}`, {
                method: "POST",
                headers: buildAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    project_id: Number(task.project_id),
                    task_id: Number(task.id),
                    evidence_id: Number(evidence.id),
                    user_id: Number(loggedUserId),
                    decision,
                    comment: reviewForm.comment || "",
                }),
            });

            const text = await response.text();
            let json;
            try {
                json = JSON.parse(text);
            } catch {
                throw new Error(`Respuesta no JSON: ${text}`);
            }

            if (!response.ok || !json.success) {
                throw new Error(json.message || "No se pudo resolver la excepción geográfica");
            }

            showPlatformMessage(json.message || "Excepción geográfica actualizada correctamente", "success");
            await loadDetail();
            await loadEvidence();
        } catch (err) {
            console.error(err);
            showPlatformMessage(err.message || "Error al resolver excepción geográfica", "error");
        } finally {
            setDecidingExceptionId(null);
            setConfirmDialog(null);
        }
    };

    const handleGeoExceptionDecision = (evidence, decision) => {
        if (!task || !evidence?.id) return;

        const approving = decision === "approve";
        const visual = getGeoValidationVisual(evidence);
        const reason = formatGeoExceptionReason(evidence) || "Sin motivo registrado";

        setConfirmDialog({
            type: "geo_exception",
            evidence,
            decision,
            kicker: "Plataforma PM · Excepción geográfica",
            title: approving ? "Autorizar excepción geográfica" : "Rechazar excepción geográfica",
            tone: approving ? "default" : "danger",
            message: approving
                ? "La evidencia fue capturada fuera del rango autorizado. Al autorizarla, quedará aceptada con trazabilidad para auditoría."
                : "La evidencia quedará marcada como no válida para aprobar el avance. El revisor deberá solicitar cambios o rechazar el avance para que el responsable corrija.",
            confirmLabel: approving ? "Autorizar excepción" : "Rechazar excepción",
            details: [
                { label: "Evidencia", value: evidence.title || evidence.file_name || `ID ${evidence.id}` },
                { label: "Validación", value: `${visual.label} · ${visual.detail}` },
                { label: "Motivo", value: reason },
            ],
        });
    };

    const handleProgressSubmit = async ({ skipLocalEvidenceCheck = false } = {}) => {
        try {
            if (!task) return;

            const progressToSave = Number(progressForm.progress_percent || 0);

            const enviarRevisionFinal = taskRequiresReview;

            if (!skipLocalEvidenceCheck && enviarRevisionFinal && reviewEvidenceBlock.blocked) {
                showPlatformMessage(reviewEvidenceBlock.message);
                return;
            }

            setSavingProgress(true);
            const response = await fetch(`${API_BASE}/project_task_progress.php?t=${Date.now()}`, {
                method: "POST",
                headers: buildAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    project_id: Number(task.project_id),
                    task_id: Number(task.id),
                    user_id: Number(loggedUserId),
                    progress_percent: progressToSave,
                    comment: progressForm.comment || "",
                    enviar_revision: enviarRevisionFinal,
                }),
            });

            const text = await response.text();
            let json;
            try {
                json = JSON.parse(text);
            } catch {
                throw new Error(`Respuesta no JSON: ${text}`);
            }

            if (!response.ok || !json.success) {
                throw new Error(json.message || "No se pudo registrar el avance");
            }

            showPlatformMessage(json.message || "Avance registrado correctamente", "success");

            // Permanece en la tarea y actualiza la información visible.
            // El usuario confirma el nuevo estado, evidencias, ciclos y salud operativa sin perder contexto.
            await loadDetail();
            await loadEvidence();
        } catch (err) {
            console.error(err);
            showPlatformMessage(err.message || "Error al registrar avance", "error");
        } finally {
            setSavingProgress(false);
        }
    };

    const handleReviewSubmit = async (accion, { skipLocalEvidenceCheck = false } = {}) => {
        try {
            if (!task) return;

            if (!skipLocalEvidenceCheck && reviewerEvidenceBlock.blocked) {
                showPlatformMessage(reviewerEvidenceBlock.message);
                return;
            }

            setSavingReview(true);

            const response = await fetch(`${API_BASE}/project_task_review.php?t=${Date.now()}`, {
                method: "POST",
                headers: buildAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    project_id: Number(task.project_id),
                    task_id: Number(task.id),
                    reviewer_user_id: Number(loggedUserId),
                    accion,
                    comment: reviewForm.comment || "",
                }),
            });

            const text = await response.text();
            let json;
            try {
                json = JSON.parse(text);
            } catch {
                throw new Error(`Respuesta no JSON: ${text}`);
            }

            if (!response.ok || !json.success) {
                throw new Error(json.message || "No se pudo aplicar la revisión");
            }

            showPlatformMessage(json.message || "Revisión aplicada correctamente", "success");

            // Permanece en la tarea y actualiza la información visible.
            // Esto permite confirmar si quedó en revisión, aprobado parcial, con cambios o rechazado.
            await loadDetail();
            await loadEvidence();
        } catch (err) {
            console.error(err);
            showPlatformMessage(err.message || "Error al revisar tarea", "error");
        } finally {
            setSavingReview(false);
        }
    };

    if (loading) {
        return (
            <div style={{ padding: 24, background: "#f8fafc", minHeight: "100%" }}>
                <SectionCard title="Cargando tarea" subtitle="Estamos obteniendo el detalle operativo de la tarea." />
            </div>
        );
    }

    if (error && !task) {
        return (
            <div style={{ padding: 24, background: "#f8fafc", minHeight: "100%" }}>
                <SectionCard title="No se pudo cargar la tarea" subtitle={error}>
                    <ActionButton
                        onClick={handleReturn}
                        bg="#fff"
                        color="#111827"
                        border="1px solid #d1d5db"
                    >
                        ← Regresar a {returnLabel}
                    </ActionButton>
                </SectionCard>
            </div>
        );
    }

    return (
        <>
            <style>{`
                .mytask-composer-grid { display: grid; grid-template-columns: minmax(420px, 1.55fr) minmax(260px, 0.78fr); }
                .mytask-header-actions { display: flex; gap: 8px; flex-wrap: wrap; }
                .mytask-page-shell { padding: 24px; background: #f8fafc; min-height: 100%; }
                .mytask-technical-log summary::-webkit-details-marker { display: none; }
                @media (max-width: 860px) {
                    .mytask-page-shell { padding: 14px; }
                    .mytask-composer-grid { grid-template-columns: 1fr !important; }
                    .mytask-composer-checklist { border-left: 0 !important; border-top: 1px solid #e2e8f0; }
                    .mytask-header-actions { width: 100%; }
                    .mytask-header-actions button { flex: 1 1 100%; }
                    .mytask-sticky-action { position: sticky; bottom: 0; z-index: 5; }
                    .task-intervention-body { grid-template-columns: 1fr !important; }
                }
            `}</style>
            {platformToast ? (
                <div
                    role="status"
                    aria-live="polite"
                    style={{
                        position: "fixed",
                        top: 72,
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 99999,
                        width: "min(620px, calc(100vw - 28px))",
                        background: platformToast.type === "error" ? "#ffffff" : "#ecfdf5",
                        border: platformToast.type === "error" ? "1px solid #fb7185" : "1px solid #34d399",
                        borderLeft: platformToast.type === "error" ? "8px solid #dc2626" : "8px solid #059669",
                        borderRadius: 18,
                        boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
                        padding: "16px 18px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                    }}
                >
                    <div
                        style={{
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            background: platformToast.type === "error" ? "#dc2626" : "#059669",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 18,
                            fontWeight: 950,
                            flexShrink: 0,
                        }}
                    >
                        {platformToast.type === "error" ? "!" : "✓"}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 950, color: "#0f172a", marginBottom: 4 }}>
                            {platformToast.type === "error" ? "Plataforma PM · Acción requerida" : "Plataforma PM · Registro de tarea"}
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: platformToast.type === "error" ? "#991b1b" : "#065f46", lineHeight: 1.45 }}>
                            {platformToast.message}
                        </div>
                    </div>
                </div>
            ) : null}
            <TaskInterventionActionModal
                actionKey={interventionModalAction}
                task={task}
                form={interventionForm}
                onChange={setInterventionForm}
                onCancel={() => setInterventionModalAction(null)}
                onSubmit={submitInterventionAction}
                saving={savingIntervention}
            />
            <PlatformConfirmModal
                config={confirmDialog}
                onCancel={() => setConfirmDialog(null)}
                onConfirm={() => {
                    if (confirmDialog?.type === "geo_exception") {
                        executeGeoExceptionDecision(confirmDialog.evidence, confirmDialog.decision);
                    }
                }}
            />
            <div className="mytask-page-shell" style={{ background: "#f8fafc", minHeight: "100%" }}>
                <section
                    style={{
                        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 52%, #eef2ff 100%)",
                        border: "1px solid #e2e8f0",
                        borderRadius: 24,
                        padding: 22,
                        boxShadow: "0 18px 42px rgba(15, 23, 42, 0.07)",
                        marginBottom: 18,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 18,
                            flexWrap: "wrap",
                        }}
                    >
                        <div style={{ minWidth: 0, flex: "1 1 520px" }}>
                            <div style={{ fontSize: 11, fontWeight: 950, color: "#2563eb", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".06em" }}>
                                Plataforma PM · Detalle cognitivo de tarea
                            </div>

                            <div style={{ fontSize: 12, fontWeight: 950, color: "#64748b", marginBottom: 5 }}>
                                {task?.wbs_code || "SIN WBS"}
                            </div>
                            <h1 style={{ margin: 0, fontSize: 30, lineHeight: 1.12, color: "#0f172a", letterSpacing: "-.03em" }}>
                                {task?.name || "Sin nombre"}
                            </h1>
                            <div style={{ marginTop: 8, color: "#475569", fontSize: 14, lineHeight: 1.55, maxWidth: 900 }}>
                                {task?.description || effectiveTaskActionMessage}
                            </div>
                        </div>

                        <div style={{ display: "grid", gap: 10, minWidth: 260 }}>
                            <div
                                style={{
                                    padding: 14,
                                    borderRadius: 18,
                                    background: "#ffffff",
                                    border: "1px solid #e2e8f0",
                                    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.045)",
                                }}
                            >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                                    <div style={{ fontSize: 12, color: "#64748b", fontWeight: 950, textTransform: "uppercase" }}>Avance</div>
                                    <div style={{ fontSize: 24, color: "#0f172a", fontWeight: 950 }}>{clampPercent(task?.progress_percent || 0)}%</div>
                                </div>
                                <div style={{ marginTop: 8 }}>
                                    <ProgressHealthBar value={task?.progress_percent || 0} />
                                </div>
                            </div>

                            <div className="mytask-header-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                                <ActionButton
                                    onClick={handleReturn}
                                    bg="#fff"
                                    color="#111827"
                                    border="1px solid #d1d5db"
                                >
                                    ← Regresar a {returnLabel}
                                </ActionButton>

                                {task?.project_id && !cameFromProjectDetail ? (
                                    <ActionButton
                                        onClick={() => {
                                            const taskReturnParams = new URLSearchParams();

                                            taskReturnParams.set("mode", mode);

                                            if (returnTo) {
                                                taskReturnParams.set("return_to", returnTo);
                                            }

                                            if (returnLabel) {
                                                taskReturnParams.set("return_label", returnLabel);
                                            }

                                            const taskReturnUrl = `/my-work/task/${task.id}?${taskReturnParams.toString()}`;

                                            const projectParams = new URLSearchParams();

                                            projectParams.set("tab", "tasks");
                                            projectParams.set("task_id", task.id);
                                            projectParams.set("mode", "edit");
                                            projectParams.set("return_to", taskReturnUrl);
                                            projectParams.set("return_label", "Detalle de tarea");

                                            navigate(`/projects/${task.project_id}?${projectParams.toString()}`, {
                                                state: {
                                                    fromMyTaskDetail: true,
                                                    returnTo: taskReturnUrl,
                                                    returnLabel: "Detalle de tarea",
                                                    originReturnTo: returnTo,
                                                    originReturnLabel: returnLabel,
                                                },
                                            });
                                        }}
                                        bg="#fff"
                                        color="#2563eb"
                                        border="1px solid #bfdbfe"
                                    >
                                        Ver ficha 360
                                    </ActionButton>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </section>

                <div
                    className="mytask-intelligent-layout"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) minmax(250px, 300px)",
                        gap: 18,
                        alignItems: "start",
                    }}
                >
                    <main style={{ minWidth: 0 }}>
                        <div id="task-main-action" />

                        <TaskInterventionPanel
                            intervention={taskIntervention}
                            task={task}
                            permissions={permissions}
                            onAction={handleInterventionAction}
                        />

                        <TaskInterventionHistoryPanel
                            data={interventionHistory}
                            loading={interventionHistoryLoading}
                        />

                        <div id="task-action-composer">
                            <SectionCard
                                title="Registrar resultado"
                                subtitle={permissions.can_review
                                    ? "Decide el avance con comentario, evidencia y trazabilidad en un solo flujo."
                                    : canUseProgressComposer
                                        ? (isFollowupMode
                                            ? "Documenta seguimiento operativo mientras el revisor cierra la decisión del avance."
                                            : "Actualiza avance, agrega comentario, adjunta evidencia y envía en un solo hilo.")
                                        : String(task?.review_status || "").toLowerCase() === "en_revision"
                                            ? "El avance ya fue enviado y está esperando decisión del revisor."
                                            : "Consulta el estado, evidencias y ciclos de esta tarea."}
                                tone={permissions.can_review ? "review" : canUseProgressComposer ? pageTone : "default"}
                            >
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                                    <Badge bg="#eef2ff" color="#4338ca" title={permissions.can_review ? "Modo revisor: toma decisión sobre el avance enviado." : isFollowupMode ? "Modo seguimiento: documenta comentario o evidencia sin modificar el avance." : canUseProgressComposer ? "Modo responsable: registra avance y evidencia." : "Modo consulta: revisa trazabilidad sin acción operativa."}>
                                        Modo: {permissions.can_review ? "Revisor" : isFollowupMode ? "Seguimiento" : canUseProgressComposer ? "Responsable" : "Consulta"}
                                    </Badge>
                                    <Badge bg="#ffffff" color={statusStyle.color} title={getStatusTooltip(task?.status)}>{statusStyle.label}</Badge>
                                    {!isApprovedPartial ? (
                                        <Badge bg="#ffffff" color={reviewStyle.color} title={getReviewTooltip(task?.review_status, task?.status)}>{reviewStyle.label}</Badge>
                                    ) : null}
                                    {isApprovedPartial ? (
                                        <Badge bg="#ffffff" color="#047857" title={isRouteBlocked ? "Aprobado parcial, pero la ruta sigue bloqueada por dependencia." : "El avance anterior fue aprobado, pero la tarea sigue abierta para registrar nuevos avances."}>Aprobado parcial</Badge>
                                    ) : null}
                                    {evidenceSummary?.evidence_required ? <Badge bg="#ffffff" color="#92400e" title="Esta tarea requiere evidencia para sostener la trazabilidad del avance.">Evidencia requerida</Badge> : null}
                                    {responsibleGeoEvidenceRequired ? <Badge bg="#ffffff" color="#9a3412" title="La ubicación se capturará automáticamente al guardar o enviar el resultado.">Ubicación automática requerida</Badge> : null}
                                </div>

                                {(canUseProgressComposer || permissions.can_review) ? (
                                    <div style={{ border: "1px solid #e2e8f0", borderRadius: 24, background: "#ffffff", overflow: "hidden", boxShadow: "0 18px 45px rgba(15,23,42,.06)" }}>
                                        <div className="mytask-composer-grid" style={{ display: "grid", gridTemplateColumns: "minmax(420px, 1.55fr) minmax(260px, 0.78fr)", gap: 0 }}>
                                            <div className="mytask-composer-checklist" style={{ order: 2, padding: 18, background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)", borderLeft: "1px solid #e2e8f0" }}>
                                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
                                                    <div>
                                                        <div style={{ fontSize: 13, fontWeight: 950, color: "#0f172a" }}>
                                                            Checklist vivo
                                                        </div>
                                                        <div style={{ marginTop: 3, fontSize: 11, color: "#64748b", fontWeight: 800, lineHeight: 1.35 }}>
                                                            Validación rápida antes de guardar o dictaminar.
                                                        </div>
                                                    </div>
                                                    <div
                                                        title="Panel de control operativo: resume si el registro está listo para continuar."
                                                        style={{
                                                            width: 34,
                                                            height: 34,
                                                            borderRadius: 12,
                                                            background: "#ffffff",
                                                            border: "1px solid #e2e8f0",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            color: "#2563eb",
                                                            fontSize: 16,
                                                            fontWeight: 950,
                                                            flexShrink: 0,
                                                            cursor: "help",
                                                        }}
                                                    >
                                                        ✓
                                                    </div>
                                                </div>

                                                <div style={{ border: "1px solid #e2e8f0", borderRadius: 16, background: "#ffffff", padding: 12, marginBottom: 12 }}>
                                                    <div style={{ fontSize: 10, fontWeight: 950, color: "#64748b", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>
                                                        Estado de captura
                                                    </div>
                                                    <div style={{ fontSize: 14, fontWeight: 950, color: "#0f172a", lineHeight: 1.25 }}>
                                                        {permissions.can_review ? "Decisión de revisión" : isFollowupMode ? "Seguimiento operativo" : "Registro de avance"}
                                                    </div>
                                                    <div style={{ marginTop: 6, fontSize: 12, color: "#64748b", fontWeight: 750, lineHeight: 1.35 }}>
                                                        {permissions.can_review
                                                            ? "Revisa comentario, evidencia y ubicación antes de decidir."
                                                            : isFollowupMode
                                                                ? "Documenta sin modificar el avance hasta que cierre la revisión."
                                                                : "Completa avance, comentario y evidencia para continuar."}
                                                    </div>
                                                </div>

                                                <div style={{ display: "grid", gap: 10 }}>
                                                    {canUseProgressComposer ? (
                                                        <>
                                                            <RequirementItem
                                                                ok={Number(progressForm.progress_percent || 0) >= Number(task?.progress_percent || 0)}
                                                                label={isFollowupMode ? "Avance sin cambios" : "Avance listo"}
                                                                detail={isFollowupMode
                                                                    ? `Actual: ${Number(task?.progress_percent || 0).toFixed(0)}% · Se mantiene en ${Number(task?.progress_percent || 0).toFixed(0)}%`
                                                                    : `Actual: ${Number(task?.progress_percent || 0).toFixed(0)}% · Nuevo: ${Number(progressForm.progress_percent || 0).toFixed(0)}%`}
                                                            />
                                                            <RequirementItem
                                                                ok={!!String(progressForm.comment || "").trim()}
                                                                label={isFollowupMode ? "Comentario de seguimiento" : "Comentario de resultado"}
                                                                detail={String(progressForm.comment || "").trim()
                                                                    ? "Comentario capturado."
                                                                    : isFollowupMode
                                                                        ? "Explica el seguimiento, aclaración o evidencia complementaria."
                                                                        : "Explica brevemente qué se realizó."}
                                                            />
                                                            <RequirementItem
                                                                ok={!taskRequiresResponsibleEvidence || Number(evidenceSummary?.avance_pending_total || 0) > 0 || hasDraftEvidence()}
                                                                label={isFollowupMode ? "Evidencia de seguimiento" : "Evidencia de avance"}
                                                                detail={isFollowupMode
                                                                    ? (hasDraftEvidence() ? "Lista para guardar como seguimiento." : "Agrega evidencia complementaria sin modificar el avance.")
                                                                    : (!taskRequiresResponsibleEvidence ? "No obligatoria para esta tarea." : Number(evidenceSummary?.avance_pending_total || 0) > 0 || hasDraftEvidence() ? "Lista para enviar." : "Agrega evidencia para continuar.")}
                                                            />
                                                            <RequirementItem
                                                                ok={!responsibleGeoEvidenceRequired || Number(evidenceSummary?.avance_pending_with_geo || 0) > 0 || (!!evidenceForm.latitude && !!evidenceForm.longitude)}
                                                                label="Ubicación automática"
                                                                detail={responsibleGeoEvidenceRequired
                                                                    ? ((!!evidenceForm.latitude && !!evidenceForm.longitude)
                                                                        ? "Ubicación capturada y lista para guardar."
                                                                        : "Se capturará automáticamente al guardar o enviar.")
                                                                    : "No requerida."}
                                                            />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <RequirementItem
                                                                ok={!!String(reviewForm.comment || "").trim() || true}
                                                                label="Decision del revisor"
                                                                detail="Aprueba, solicita cambios o rechaza con trazabilidad."
                                                            />
                                                            <RequirementItem
                                                                ok={true}
                                                                label="Evidencia de revisión"
                                                                detail={hasDraftEvidence()
                                                                    ? "Evidencia opcional lista para adjuntarse al dictamen."
                                                                    : "Opcional. Puedes dictaminar sin adjuntar evidencia propia del revisor."}
                                                            />
                                                            <RequirementItem
                                                                ok={true}
                                                                label="Ubicación automática"
                                                                detail={hasDraftEvidence()
                                                                    ? "Solo se capturará si la evidencia opcional del revisor lo requiere."
                                                                    : "No requerida para dictaminar sin evidencia adicional del revisor."}
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="mytask-composer-main" style={{ order: 1, padding: 20, minWidth: 0, background: "#ffffff" }}>
                                                {canUseProgressComposer ? (
                                                    <>
                                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap" }}>
                                                            <div>
                                                                <div style={{ fontSize: 18, fontWeight: 950, color: "#0f172a" }}>
                                                                    {String(task?.review_status || "").toLowerCase() === "con_cambios" || String(task?.review_status || "").toLowerCase() === "rechazado"
                                                                        ? "Corregir entrega"
                                                                        : isFollowupMode
                                                                            ? "Registrar seguimiento"
                                                                            : "Registrar avance"}
                                                                </div>
                                                                <div style={{ marginTop: 4, fontSize: 13, color: "#64748b", fontWeight: 750 }}>
                                                                    {isFollowupMode
                                                                        ? "Documenta comentario o evidencia sin modificar el avance hasta que el revisor cierre la revisión."
                                                                        : "Mueve el avance, explica el resultado y adjunta evidencia en un solo flujo."}
                                                                </div>
                                                            </div>
                                                            <Badge bg="#eff6ff" color="#1d4ed8">Avance actual {Number(task?.progress_percent || 0).toFixed(0)}%</Badge>
                                                        </div>

                                                        {latestCorrectionLog || task?.last_review_comment ? (
                                                            <div style={{ padding: 12, borderRadius: 14, background: "#f8fafc", border: "1px solid #fdba74", color: "#9a3412", fontSize: 13, fontWeight: 800, marginBottom: 14, lineHeight: 1.45 }}>
                                                                {isFollowupMode ? "Referencia del ciclo anterior" : "Comentario del revisor"}: {latestCorrectionLog?.comment || task?.last_review_comment}
                                                            </div>
                                                        ) : null}

                                                        <div style={{ marginBottom: 18 }}>
                                                            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 10 }}>
                                                                <div>
                                                                    <div style={{ fontSize: 12, fontWeight: 950, color: "#334155" }}>{isFollowupMode ? "Avance sin cambios" : "Nuevo avance"}</div>
                                                                    <div style={{ marginTop: 3, fontSize: 11, color: "#64748b", fontWeight: 800 }}>
                                                                        {isFollowupMode
                                                                            ? "Este registro no modifica el avance; solo documenta seguimiento."
                                                                            : isRouteBlocked
                                                                                ? "Ruta bloqueada: no puedes aumentar avance hasta liberar la predecesora."
                                                                                : "No puede ser menor al avance aprobado actual."}
                                                                    </div>
                                                                </div>
                                                                <div style={{ color: "#2563eb", fontSize: 28, fontWeight: 950, letterSpacing: "-.04em" }}>
                                                                    {Number(progressForm.progress_percent || 0).toFixed(0)}%
                                                                </div>
                                                            </div>
                                                            <input
                                                                type="range"
                                                                min="0"
                                                                max="100"
                                                                step="5"
                                                                value={Math.max(0, Math.min(100, Number(progressForm.progress_percent || 0)))}
                                                                title={isFollowupMode
                                                                    ? `Seguimiento sin cambio de avance. Avance actual: ${Number(task?.progress_percent || 0).toFixed(0)}%.`
                                                                    : isRouteBlocked
                                                                        ? "Ruta bloqueada: el avance no puede aumentar hasta liberar la predecesora."
                                                                        : `Mueve el control para definir el nuevo avance. Avance actual: ${Number(task?.progress_percent || 0).toFixed(0)}%. Nuevo avance: ${Number(progressForm.progress_percent || 0).toFixed(0)}%.`}
                                                                disabled={isRouteBlocked || isFollowupMode}
                                                                onChange={(e) => setProgressForm((prev) => ({ ...prev, progress_percent: e.target.value }))}
                                                                style={{ width: "100%", cursor: (isRouteBlocked || isFollowupMode) ? "not-allowed" : "pointer", opacity: (isRouteBlocked || isFollowupMode) ? 0.65 : 1 }}
                                                            />
                                                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, color: "#64748b", fontSize: 11, fontWeight: 900 }}>
                                                                <span>0%</span>
                                                                <span>25%</span>
                                                                <span>50%</span>
                                                                <span>75%</span>
                                                                <span>100%</span>
                                                            </div>
                                                            <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", color: "#334155", fontSize: 12, fontWeight: 900 }}>
                                                                <span title="Avance aprobado o registrado actualmente en la tarea.">Actual: {Number(task?.progress_percent || 0).toFixed(0)}%</span>
                                                                <span title={isFollowupMode ? "El avance no cambia en este seguimiento." : "Nuevo avance que se guardará al finalizar el registro."}>{isFollowupMode ? "Se mantiene" : "Nuevo"}: {Number(progressForm.progress_percent || 0).toFixed(0)}%</span>
                                                            </div>
                                                        </div>

                                                        {isRouteBlocked ? (
                                                            <div style={{ padding: 12, borderRadius: 14, background: "#f8fafc", border: "1px solid #fdba74", color: "#9a3412", fontSize: 12, fontWeight: 850, lineHeight: 1.45, marginBottom: 14 }}>
                                                                Ruta bloqueada por dependencia. Puedes documentar seguimiento o adjuntar evidencia, pero no aumentar avance hasta que se libere la predecesora.
                                                            </div>
                                                        ) : null}
                                                    </>
                                                ) : null}

                                                {permissions.can_review ? (
                                                    <>
                                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap" }}>
                                                            <div>
                                                                <div style={{ fontSize: 18, fontWeight: 950, color: "#0f172a" }}>Registrar decision</div>
                                                                <div style={{ marginTop: 4, fontSize: 13, color: "#64748b", fontWeight: 750 }}>
                                                                    Revisa el avance enviado, deja comentario y decide sin perder contexto.
                                                                </div>
                                                            </div>
                                                            <Badge bg="#f5f3ff" color="#6d28d9">Avance enviado {Number(task?.progress_percent || 0).toFixed(0)}%</Badge>
                                                        </div>
                                                        {latestReviewSubmission ? (
                                                            <div style={{ padding: 12, borderRadius: 14, background: "#f8fafc", border: "1px solid #e2e8f0", marginBottom: 14 }}>
                                                                <div style={{ fontSize: 12, fontWeight: 950, color: "#334155", marginBottom: 4 }}>Ultimo envio del responsable</div>
                                                                <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 800, lineHeight: 1.45 }}>{getLogComment(latestReviewSubmission)}</div>
                                                                <SubmittedEvidencePanel items={latestResponsibleEvidenceItems} canDecideException={!!permissions.can_review} onDecideException={handleGeoExceptionDecision} decidingExceptionId={decidingExceptionId} />
                                                                {hasComplementaryFollowup ? (
                                                                    <SubmittedEvidencePanel
                                                                        title="Seguimientos complementarios del responsable"
                                                                        items={complementaryFollowupEvidenceItems}
                                                                        canDecideException={false}
                                                                    />
                                                                ) : null}
                                                                {hasPendingGeoException ? (
                                                                    <div style={{ marginTop: 12, padding: 12, borderRadius: 14, background: "#ffffff", border: "1px solid #fdba74", color: "#9a3412", fontSize: 12, fontWeight: 850, lineHeight: 1.45 }}>
                                                                        Existe una evidencia fuera de rango pendiente de autorización. Autoriza o rechaza la excepción antes de aprobar el avance.
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        ) : null}
                                                    </>
                                                ) : null}

                                                <div style={{ marginBottom: 14 }}>
                                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                                                        <label
                                                            title="Describe brevemente que se realizo, que se corrigio o que debe validar el revisor. Este comentario funciona como descripción principal del resultado."
                                                            style={{ display: "block", fontSize: 12, fontWeight: 950, color: "#334155", cursor: "help" }}
                                                        >
                                                            Comentario / descripción del resultado
                                                        </label>
                                                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                                            <button type="button" title="Adjunta evidencia visual del avance o resultado." onClick={() => { document.getElementById("task-composer-image-file")?.click(); }} style={{ border: "1px solid #bfdbfe", background: "#eff6ff", color: "#1d4ed8", borderRadius: 999, padding: "7px 10px", fontSize: 11, fontWeight: 950, cursor: "pointer" }}>📷 Imagen</button>
                                                            <button type="button" title="Adjunta documentos, reportes, actas, minutas o entregables como soporte." onClick={() => { document.getElementById("task-composer-doc-file")?.click(); }} style={{ border: "1px solid #cbd5e1", background: "#f8fafc", color: "#334155", borderRadius: 999, padding: "7px 10px", fontSize: 11, fontWeight: 950, cursor: "pointer" }}>📄 Documento</button>
                                                            <button type="button" title="Agrega una liga externa como evidencia complementaria." onClick={() => setEvidenceForm((prev) => ({ ...prev, evidence_type: "link" }))} style={{ border: "1px solid #bfdbfe", background: evidenceForm.evidence_type === "link" ? "#dbeafe" : "#fff", color: "#1d4ed8", borderRadius: 999, padding: "7px 10px", fontSize: 11, fontWeight: 950, cursor: "pointer" }}>🔗 Link</button>
                                                        </div>
                                                    </div>
                                                    <textarea
                                                        value={permissions.can_review ? reviewForm.comment : progressForm.comment}
                                                        onChange={(e) => permissions.can_review
                                                            ? setReviewForm((prev) => ({ ...prev, comment: e.target.value }))
                                                            : setProgressForm((prev) => ({ ...prev, comment: e.target.value }))}
                                                        placeholder={permissions.can_review ? "Escribe tu comentario de revision." : "Describe que se realizo, que se corrigio o que debe validar el revisor."}
                                                        rows={4}
                                                        style={{ width: "100%", padding: 14, borderRadius: 16, border: "1px solid #cbd5e1", resize: "vertical", boxSizing: "border-box", fontSize: 13, lineHeight: 1.5 }}
                                                    />
                                                </div>

                                                <input
                                                    id="task-composer-image-file"
                                                    type="file"
                                                    accept="image/*"
                                                    multiple
                                                    style={{ display: "none" }}
                                                    onChange={(e) => {
                                                        const files = Array.from(e.target.files || []);
                                                        if (!files.length) return;
                                                        setEvidenceFiles((prev) => [
                                                            ...prev,
                                                            ...files.map((file) => ({
                                                                id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
                                                                file,
                                                                evidence_type: "imagen",
                                                            })),
                                                        ]);
                                                        setEvidenceFile(files[0] || null);
                                                        setEvidenceForm((prev) => ({ ...prev, evidence_type: "imagen" }));
                                                        e.target.value = "";
                                                    }}
                                                />
                                                <input
                                                    id="task-composer-doc-file"
                                                    type="file"
                                                    multiple
                                                    style={{ display: "none" }}
                                                    onChange={(e) => {
                                                        const files = Array.from(e.target.files || []);
                                                        if (!files.length) return;
                                                        setEvidenceFiles((prev) => [
                                                            ...prev,
                                                            ...files.map((file) => ({
                                                                id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
                                                                file,
                                                                evidence_type: String(file.type || "").startsWith("image/") ? "imagen" : "archivo",
                                                            })),
                                                        ]);
                                                        setEvidenceFile(files[0] || null);
                                                        setEvidenceForm((prev) => ({ ...prev, evidence_type: "archivo" }));
                                                        e.target.value = "";
                                                    }}
                                                />

                                                {evidenceForm.evidence_type === "link" ? (
                                                    <input
                                                        type="url"
                                                        title="Pega el enlace externo que servira como evidencia complementaria."
                                                        value={evidenceForm.external_url}
                                                        onChange={(e) => setEvidenceForm((prev) => ({ ...prev, external_url: e.target.value }))}
                                                        placeholder="Pega aqui el enlace de evidencia"
                                                        style={{ width: "100%", padding: "12px 14px", borderRadius: 14, border: "1px solid #cbd5e1", boxSizing: "border-box", marginBottom: 10 }}
                                                    />
                                                ) : null}

                                                <div style={{ border: "1px dashed #cbd5e1", background: "#f8fafc", borderRadius: 18, padding: 14 }}>
                                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: (evidenceFiles.length || evidenceForm.external_url) ? 12 : 0 }}>
                                                        <div>
                                                            <div style={{ fontSize: 13, fontWeight: 950, color: "#0f172a" }}>Evidencia integrada</div>
                                                            <div style={{ marginTop: 2, fontSize: 12, color: "#64748b", fontWeight: 750 }}>
                                                                Puedes agregar varias imagenes y documentos. La ubicación se captura automaticamente si aplica.
                                                            </div>
                                                        </div>
                                                        {capturingGeo ? (
                                                            <Badge bg="#ecfeff" color="#0f766e" title="El sistema está solicitando la ubicación al navegador para adjuntarla al registro.">
                                                                Obteniendo ubicación...
                                                            </Badge>
                                                        ) : responsibleGeoEvidenceRequired ? (
                                                            <Badge bg="#ffffff" color="#9a3412" title="La ubicación se capturará automáticamente al guardar o enviar el resultado.">
                                                                Ubicación automática requerida
                                                            </Badge>
                                                        ) : (
                                                            <Badge bg="#ffffff" color="#047857" title="La ubicación no es obligatoria para este registro, pero puedes capturarla si quieres más trazabilidad.">
                                                                Ubicación opcional
                                                            </Badge>
                                                        )}
                                                    </div>

                                                    {evidenceFiles.length ? (
                                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
                                                            {evidenceFiles.map((item) => {
                                                                const file = item.file || item;
                                                                const isImage = item.evidence_type === "imagen" || String(file.type || "").startsWith("image/");
                                                                return (
                                                                    <div key={item.id || file.name} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #e2e8f0", background: "#fff", borderRadius: 14, padding: 10, minWidth: 0 }}>
                                                                        {isImage ? (
                                                                            <img src={URL.createObjectURL(file)} alt="Vista previa" style={{ width: 46, height: 46, objectFit: "cover", borderRadius: 10, border: "1px solid #bfdbfe", background: "#fff" }} />
                                                                        ) : (
                                                                            <div style={{ width: 46, height: 46, borderRadius: 10, border: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>DOC</div>
                                                                        )}
                                                                        <div style={{ minWidth: 0, flex: 1 }}>
                                                                            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#0f172a", fontSize: 12, fontWeight: 950 }}>{file.name}</div>
                                                                            <div style={{ color: "#64748b", fontSize: 11, fontWeight: 800 }}>{isImage ? "Imagen" : "Documento"} · {Math.round((file.size || 0) / 1024)} KB</div>
                                                                        </div>
                                                                        <button type="button" onClick={() => setEvidenceFiles((prev) => prev.filter((x) => x.id !== item.id))} style={{ border: "none", background: "transparent", color: "#b91c1c", cursor: "pointer", fontWeight: 950 }}>x</button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : evidenceForm.evidence_type === "link" && evidenceForm.external_url ? (
                                                        <div style={{ border: "1px solid #bfdbfe", background: "#eff6ff", color: "#1d4ed8", borderRadius: 14, padding: "9px 11px", fontSize: 12, fontWeight: 900 }}>
                                                            Link listo: {evidenceForm.external_url}
                                                        </div>
                                                    ) : (
                                                        <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
                                                            Aún no hay evidencia adjunta. Usa Imagen, Documento o Link. El comentario del resultado funciona como descripción principal.
                                                        </div>
                                                    )}
                                                </div>

                                                {geoExceptionRequired ? (
                                                    <div style={{ marginTop: 14, padding: 14, borderRadius: 18, background: "#ffffff", border: "1px solid #fdba74" }}>
                                                        <div style={{ fontSize: 13, fontWeight: 950, color: "#9a3412", marginBottom: 6 }}>
                                                            Solicitar excepción geográfica
                                                        </div>
                                                        <div style={{ fontSize: 12, color: "#9a3412", fontWeight: 750, lineHeight: 1.45, marginBottom: 10 }}>
                                                            La evidencia está fuera del radio autorizado. Escribe una justificación para guardarla como excepción pendiente de autorización del revisor o PM.
                                                        </div>
                                                        <textarea
                                                            value={evidenceForm.geo_exception_reason || ""}
                                                            onChange={(e) => setEvidenceForm((prev) => ({ ...prev, geo_exception_reason: e.target.value }))}
                                                            placeholder="Ejemplo: la actividad se realizó en acceso alterno autorizado por supervisión."
                                                            rows={3}
                                                            style={{ width: "100%", padding: 12, borderRadius: 14, border: "1px solid #fdba74", resize: "vertical", boxSizing: "border-box", fontSize: 13, lineHeight: 1.5 }}
                                                        />
                                                    </div>
                                                ) : null}


                                                {permissions.can_review && hasBlockingGeoException ? (
                                                    <div style={{ marginTop: 14, padding: 14, borderRadius: 18, background: "#f8fafc", border: "1px solid #e2e8f0", borderLeft: "4px solid #f97316", color: "#334155" }}>
                                                        <div style={{ fontSize: 13, fontWeight: 950, marginBottom: 6 }}>
                                                            {hasRejectedGeoException ? "Aprobación bloqueada por evidencia geográfica" : "Excepción geográfica pendiente"}
                                                        </div>
                                                        <div style={{ fontSize: 12, fontWeight: 750, lineHeight: 1.45 }}>
                                                            {hasRejectedGeoException
                                                                ? "La evidencia oficial del ciclo fue rechazada. Cierra la revisión solicitando cambios o rechazando el avance."
                                                                : "Resuelve la excepción antes de aprobar; también puedes solicitar cambios o rechazar el avance."}
                                                        </div>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>

                                        <div style={{ position: "sticky", bottom: 0, zIndex: 5, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap", padding: 16, background: "rgba(255,255,255,.96)", borderTop: "1px solid #e2e8f0", backdropFilter: "blur(10px)" }}>
                                            <div style={{ color: actionError ? "#b91c1c" : "#64748b", fontSize: 13, fontWeight: 900 }}>
                                                {actionError || (permissions.can_review
                                                    ? (hasBlockingGeoException ? "La aprobación está bloqueada. Solicita cambios o rechaza el avance para devolver acción al responsable." : "Decide con evidencia y comentario cuando aplique.")
                                                    : isFollowupMode
                                                        ? "Documenta seguimiento sin modificar el avance hasta que el revisor cierre la revisión."
                                                        : taskRequiresReview
                                                            ? (geoExceptionRequired ? "Agrega la justificación y solicita excepción geográfica." : "Finaliza y envía a revisión en un solo paso.")
                                                            : "Guarda el resultado de la tarea.")}
                                            </div>
                                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                                {canUseProgressComposer ? (
                                                    <ActionButton
                                                        title={isFollowupMode ? "Guarda comentario y evidencia de seguimiento sin modificar el avance." : "Guarda avance, comentario, evidencias y ubicación en un solo flujo."}
                                                        onClick={handleComposerProgressSubmit}
                                                        disabled={savingProgress || savingEvidence || capturingGeo}
                                                        bg="#111827"
                                                        border="1px solid #111827"
                                                    >
                                                        {savingProgress || savingEvidence
                                                            ? "Guardando..."
                                                            : geoExceptionRequired
                                                                ? "Finalizar y solicitar excepción"
                                                                : isFollowupMode
                                                                    ? "Guardar seguimiento"
                                                                    : taskRequiresReview
                                                                        ? "Finalizar y enviar"
                                                                        : "Guardar resultado"}
                                                    </ActionButton>
                                                ) : null}

                                                {permissions.can_review ? (
                                                    <>
                                                        <ActionButton title={hasBlockingGeoException ? "Aprobación bloqueada: existe evidencia geográfica pendiente o rechazada." : "Aprueba el avance enviado por el responsable y conserva la trazabilidad del ciclo."} onClick={() => handleComposerReviewSubmit("aprobar")} disabled={savingReview || savingEvidence || capturingGeo || hasBlockingGeoException} bg={hasBlockingGeoException ? "#f8fafc" : "#16a34a"} color={hasBlockingGeoException ? "#94a3b8" : "#fff"} border={hasBlockingGeoException ? "1px solid #e5e7eb" : "1px solid #16a34a"}>
                                                            {hasBlockingGeoException ? "Aprobar bloqueado" : "Aprobar"}
                                                        </ActionButton>
                                                        <ActionButton title="Solicita ajustes al responsable sin cerrar definitivamente la tarea." onClick={() => handleComposerReviewSubmit("solicitar_cambios")} disabled={savingReview || savingEvidence || capturingGeo} bg="#fff" color="#92400e" border="1px solid #fcd34d">
                                                            Solicitar cambios
                                                        </ActionButton>
                                                        <ActionButton title="Rechaza el avance y obliga al responsable a corregir antes de reenviar." onClick={() => handleComposerReviewSubmit("rechazar")} disabled={savingReview || savingEvidence || capturingGeo} bg="#fff" color="#b91c1c" border="1px solid #fecaca">
                                                            Rechazar
                                                        </ActionButton>
                                                    </>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ padding: 16, borderRadius: 16, background: "#ffffff", border: taskIntervention?.requiresIntervention ? "1px solid #fed7aa" : String(task?.review_status || "").toLowerCase() === "en_revision" ? "1px solid #fde68a" : "1px solid #e5e7eb", color: taskIntervention?.requiresIntervention ? "#9a3412" : String(task?.review_status || "").toLowerCase() === "en_revision" ? "#92400e" : "#64748B", fontSize: 14, fontWeight: 750, lineHeight: 1.5 }}>
                                        {taskIntervention?.requiresIntervention
                                            ? "Intervención pendiente: esta tarea presenta señales críticas o documentales. Usa el panel superior para documentar, solicitar evidencia, reprogramar, escalar o convertir en riesgo."
                                            : String(task?.review_status || "").toLowerCase() === "en_revision"
                                                ? "En espera de revisión: el avance ya fue enviado. No requiere acción del responsable en este momento; está pendiente de que el revisor apruebe, rechace o solicite cambios."
                                                : "Sin acciones pendientes: esta tarea no requiere acción operativa de tu parte en este momento. Puedes consultar el contexto, las evidencias y los ciclos de revisión."}
                                    </div>
                                )}
                            </SectionCard>
                        </div>

                        {!task?.reviewer_user_id &&
                            String(task?.status || "").toLowerCase() !== "terminada" &&
                            String(task?.status || "").toLowerCase() !== "cancelada" ? (
                            <SectionCard
                                title="Asignación rápida de revisor"
                                subtitle="Esta tarea no tiene responsable formal de validación. Asigna un revisor para fortalecer la trazabilidad PMO."
                                tone="review"
                            >
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "minmax(240px, 1fr) 220px auto",
                                        gap: 12,
                                        alignItems: "end",
                                    }}
                                >
                                    <div>
                                        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6, fontWeight: 800 }}>
                                            Revisor
                                        </div>

                                        <select
                                            value={reviewerForm.reviewer_user_id}
                                            onChange={(e) =>
                                                setReviewerForm((prev) => ({
                                                    ...prev,
                                                    reviewer_user_id: e.target.value,
                                                }))
                                            }
                                            style={{
                                                width: "100%",
                                                padding: "11px 12px",
                                                borderRadius: 12,
                                                border: "1px solid #bfdbfe",
                                                outline: "none",
                                                fontSize: 14,
                                                background: "#fff",
                                                color: "#111827",
                                                fontWeight: 700,
                                            }}
                                        >
                                            <option value="">Selecciona revisor...</option>
                                            {activeUsers.map((user) => (
                                                <option key={user.id} value={user.id}>
                                                    {user.display_name || user.email || `Usuario #${user.id}`}
                                                </option>
                                            ))}
                                        </select>

                                        {!activeUsers.length ? (
                                            <div style={{ marginTop: 6, fontSize: 12, color: "#b91c1c", fontWeight: 700 }}>
                                                No se recibieron usuarios activos desde el backend.
                                            </div>
                                        ) : null}
                                    </div>

                                    <label
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            padding: "11px 12px",
                                            borderRadius: 12,
                                            border: "1px solid #dbeafe",
                                            background: "#eff6ff",
                                            color: "#1d4ed8",
                                            fontSize: 13,
                                            fontWeight: 850,
                                            cursor: "pointer",
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={reviewerForm.requires_review}
                                            onChange={(e) =>
                                                setReviewerForm((prev) => ({
                                                    ...prev,
                                                    requires_review: e.target.checked,
                                                }))
                                            }
                                        />
                                        Requiere revisión
                                    </label>

                                    <ActionButton
                                        onClick={handleAssignReviewer}
                                        disabled={savingReviewer || !reviewerForm.reviewer_user_id}
                                        bg="#2563eb"
                                        color="#fff"
                                        border="1px solid #2563eb"
                                    >
                                        {savingReviewer ? "Guardando..." : "Asignar revisor"}
                                    </ActionButton>
                                </div>
                            </SectionCard>
                        ) : null}
                        {!task?.due_date &&
                            String(task?.status || "").toLowerCase() !== "terminada" &&
                            String(task?.status || "").toLowerCase() !== "cancelada" ? (
                            <div id="task-due-date-card">
                                <SectionCard
                                    title="Asignación rápida de fecha objetivo"
                                    subtitle="Esta tarea no tiene fecha compromiso. Asigna una fecha para activar Gantt, Roadmap, Aging y Readiness Meter."
                                    tone="review"
                                >
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "minmax(180px, 1fr) minmax(180px, 1fr) auto",
                                            gap: 12,
                                            alignItems: "end",
                                        }}
                                    >
                                        <div>
                                            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6, fontWeight: 800 }}>
                                                Fecha de inicio
                                            </div>

                                            <input
                                                type="date"
                                                value={dueDateForm.start_date}
                                                onChange={(e) =>
                                                    setDueDateForm((prev) => ({
                                                        ...prev,
                                                        start_date: e.target.value,
                                                    }))
                                                }
                                                style={{
                                                    width: "100%",
                                                    padding: "11px 12px",
                                                    borderRadius: 12,
                                                    border: "1px solid #bfdbfe",
                                                    outline: "none",
                                                    fontSize: 14,
                                                    background: "#fff",
                                                    color: "#111827",
                                                    fontWeight: 700,
                                                }}
                                            />
                                        </div>

                                        <div>
                                            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6, fontWeight: 800 }}>
                                                Fecha objetivo
                                            </div>

                                            <input
                                                type="date"
                                                value={dueDateForm.due_date}
                                                onChange={(e) =>
                                                    setDueDateForm((prev) => ({
                                                        ...prev,
                                                        due_date: e.target.value,
                                                    }))
                                                }
                                                style={{
                                                    width: "100%",
                                                    padding: "11px 12px",
                                                    borderRadius: 12,
                                                    border: "1px solid #bfdbfe",
                                                    outline: "none",
                                                    fontSize: 14,
                                                    background: "#fff",
                                                    color: "#111827",
                                                    fontWeight: 700,
                                                }}
                                            />
                                        </div>

                                        <ActionButton
                                            onClick={handleAssignDueDate}
                                            disabled={savingDueDate || !dueDateForm.due_date}
                                            bg="#2563eb"
                                            color="#fff"
                                            border="1px solid #2563eb"
                                        >
                                            {savingDueDate ? "Guardando..." : "Asignar fecha"}
                                        </ActionButton>
                                    </div>
                                </SectionCard>
                            </div>
                        ) : null}
                        {(needsEvidence || needsGeo) &&
                            String(task?.status || "").toLowerCase() !== "terminada" &&
                            String(task?.status || "").toLowerCase() !== "cancelada" ? (
                            <SectionCard
                                title="Evidencia requerida pendiente"
                                subtitle="Esta tarea tiene requisitos de trazabilidad pendientes. Completa la evidencia para fortalecer el control PMO."
                                tone="correction"
                            >
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                                        gap: 12,
                                    }}
                                >
                                    {needsEvidence ? (
                                        <RequirementItem
                                            ok={false}
                                            label="Evidencia documental pendiente"
                                            detail="La tarea requiere evidencia, pero aún no tiene archivos, imágenes, enlaces o comentarios válidos registrados."
                                        />
                                    ) : (
                                        <RequirementItem
                                            ok={true}
                                            label="Evidencia documental completa"
                                            detail="La tarea ya cuenta con evidencia documental registrada."
                                        />
                                    )}

                                    {needsGeo ? (
                                        <RequirementItem
                                            ok={false}
                                            label="Geoevidencia pendiente"
                                            detail="La tarea requiere evidencia con ubicación. Sube una evidencia con coordenadas para validar dónde ocurrió el trabajo."
                                        />
                                    ) : Number(task?.geo_evidence_required || 0) === 1 ? (
                                        <RequirementItem
                                            ok={true}
                                            label="Geoevidencia completa"
                                            detail="La tarea ya cuenta con evidencia con ubicación."
                                        />
                                    ) : (
                                        <RequirementItem
                                            ok={true}
                                            label="Geoevidencia no requerida"
                                            detail="Esta tarea no exige ubicación automática; la evidencia documental es suficiente para la trazabilidad solicitada."
                                        />
                                    )}
                                </div>

                                <div
                                    style={{
                                        marginTop: 14,
                                        padding: 14,
                                        borderRadius: 14,
                                        background: "#ffffff",
                                        border: "1px solid #fdba74",
                                        color: "#9a3412",
                                        fontSize: 13,
                                        fontWeight: 800,
                                        lineHeight: 1.45,
                                    }}
                                >
                                    {responsibleGeoEvidenceRequired
                                        ? "Para cerrar esta brecha, baja al bloque de evidencias de la tarea y carga el soporte correspondiente. Si es trabajo en campo, activa ubicación o registra coordenadas."
                                        : "Para cerrar esta brecha, baja al bloque de evidencias de la tarea y carga el soporte documental correspondiente."}
                                </div>
                            </SectionCard>
                        ) : null}
                        {task?.depends_on_task_id ? (
                            <div
                                style={{
                                    marginTop: 10,
                                    padding: "10px 12px",
                                    borderRadius: 12,
                                    background: "#f8fafc",
                                    border: "1px solid #e5e7eb",
                                    fontSize: 12,
                                    color: "#6b7280",
                                    lineHeight: 1.5,
                                }}
                            >
                                <strong style={{ color: "#374151" }}>
                                    {dependencyInfo.label}:
                                </strong>{" "}
                                {dependencyInfo.short} {dependencyInfo.example}
                            </div>
                        ) : null}

                        {permissions.can_view_followup ? (
                            <SectionCard
                                title="Seguimiento de corrección solicitada"
                                subtitle="Esta tarea fue devuelta al responsable. Puedes dar seguimiento, pero no registrar avance ni aprobar hasta que sea reenviada a revisión."
                                tone="correction"
                            >
                                <InfoBox label="Comentario al responsable" value={task?.last_review_comment || "Sin comentario registrado."} />
                            </SectionCard>
                        ) : null}

                        {latestReviewSubmission ? (
                            <SectionCard
                                title="Último envío a revisión"
                                subtitle="Bloque contraíble del último avance enviado. Ábrelo solo cuando necesites revisar comentario y evidencias vinculadas."
                                tone="review"
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 12,
                                        flexWrap: "wrap",
                                        padding: 14,
                                        borderRadius: 16,
                                        background: latestSubmissionOpen ? "#eff6ff" : "#f8fafc",
                                        border: latestSubmissionOpen ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
                                        marginBottom: latestSubmissionOpen ? 16 : 0,
                                    }}
                                >
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 950 }}>
                                            {latestSubmissionOpen ? "Ocultar último envío" : "Mostrar último envío"}
                                        </div>
                                        <div style={{ marginTop: 3, fontSize: 12, color: "#64748b", fontWeight: 750, lineHeight: 1.4 }}>
                                            {formatDateTime(latestReviewSubmission.created_at)} · {latestReviewSubmission.user_name || "Responsable"} · {latestResponsibleEvidenceItems.length || 0} evidencia(s) vinculadas
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => setLatestSubmissionOpen((prev) => !prev)}
                                        title={latestSubmissionOpen ? "Contraer último envío a revisión" : "Abrir último envío a revisión"}
                                        style={{
                                            border: latestSubmissionOpen ? "1px solid #2563eb" : "1px solid #cbd5e1",
                                            background: latestSubmissionOpen ? "#2563eb" : "#ffffff",
                                            color: latestSubmissionOpen ? "#ffffff" : "#0f172a",
                                            borderRadius: 14,
                                            padding: "9px 13px",
                                            fontSize: 12,
                                            fontWeight: 950,
                                            cursor: "pointer",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 8,
                                        }}
                                    >
                                        {latestSubmissionOpen ? "Contraer" : "Abrir"}
                                        <span aria-hidden="true">{latestSubmissionOpen ? "↑" : "↓"}</span>
                                    </button>
                                </div>

                                {latestSubmissionOpen ? (
                                    <>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: 12,
                                                flexWrap: "wrap",
                                                marginBottom: 16,
                                            }}
                                        >
                                            <div>
                                                <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 950 }}>
                                                    Lectura del último envío
                                                </div>
                                                <div style={{ marginTop: 3, fontSize: 12, color: "#64748b", fontWeight: 750 }}>
                                                    {latestResponsibleEvidenceItems.length || 0} evidencia(s) vinculadas · {getEvidenceSummaryText(latestResponsibleEvidenceItems)}
                                                </div>
                                            </div>

                                            <IconViewToggle
                                                value={latestSubmissionView}
                                                onChange={setLatestSubmissionView}
                                                leftTitle="Ver último envío como detalle"
                                                rightTitle="Ver último envío como cards"
                                            />
                                        </div>

                                        {latestSubmissionView === "detail" ? (
                                            <div style={{ display: "grid", gap: 12 }}>
                                                <div
                                                    style={{
                                                        overflowX: "auto",
                                                        border: "1px solid #e2e8f0",
                                                        borderRadius: 16,
                                                        background: "#ffffff",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            minWidth: 860,
                                                            display: "grid",
                                                            gridTemplateColumns: "160px 180px 180px minmax(220px, 1fr) 150px",
                                                            gap: 0,
                                                            background: "#f8fafc",
                                                            borderBottom: "1px solid #e2e8f0",
                                                            color: "#475569",
                                                            fontSize: 11,
                                                            fontWeight: 950,
                                                            textTransform: "uppercase",
                                                            letterSpacing: ".03em",
                                                        }}
                                                    >
                                                        {['Fecha', 'Enviado por', 'Revisor', 'Comentario', 'Evidencia'].map((h) => (
                                                            <div key={h} style={{ padding: "10px 12px" }}>{h}</div>
                                                        ))}
                                                    </div>
                                                    <div
                                                        style={{
                                                            minWidth: 860,
                                                            display: "grid",
                                                            gridTemplateColumns: "160px 180px 180px minmax(220px, 1fr) 150px",
                                                            gap: 0,
                                                            alignItems: "start",
                                                            fontSize: 12,
                                                            color: "#0f172a",
                                                        }}
                                                    >
                                                        <div style={{ padding: "12px", fontWeight: 850 }}>{formatDateTime(latestReviewSubmission.created_at)}</div>
                                                        <div style={{ padding: "12px", fontWeight: 850 }}>{latestReviewSubmission.user_name || "—"}</div>
                                                        <div style={{ padding: "12px", fontWeight: 850 }}>{latestReviewSubmission.reviewed_by_name || task?.reviewer_name || "—"}</div>
                                                        <div style={{ padding: "12px", color: "#334155", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>{getLogComment(latestReviewSubmission)}</div>
                                                        <div style={{ padding: "12px" }}>
                                                            <Badge bg="#eff6ff" color="#1d4ed8">
                                                                {latestResponsibleEvidenceItems.length || 0} registro(s)
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </div>

                                                <EvidenceRepositoryGridView
                                                    items={latestResponsibleEvidenceItems}
                                                    permissions={permissions}
                                                    onDecideException={handleGeoExceptionDecision}
                                                    decidingExceptionId={decidingExceptionId}
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 12 }}>
                                                    <InfoBox label="Fecha" value={formatDateTime(latestReviewSubmission.created_at)} />
                                                    <InfoBox label="Enviado por" value={latestReviewSubmission.user_name || "—"} />
                                                    <InfoBox label="Revisor" value={latestReviewSubmission.reviewed_by_name || task?.reviewer_name || "—"} />
                                                </div>
                                                <InfoBox label="Comentario del avance" value={getLogComment(latestReviewSubmission)} />
                                                <SubmittedEvidencePanel items={latestResponsibleEvidenceItems} canDecideException={!!permissions.can_review} onDecideException={handleGeoExceptionDecision} decidingExceptionId={decidingExceptionId} />
                                            </>
                                        )}
                                    </>
                                ) : null}
                            </SectionCard>
                        ) : null}

                        {hasComplementaryFollowup ? (
                            <SectionCard
                                title="Seguimientos complementarios"
                                subtitle="Registros agregados por el responsable mientras la revisión sigue abierta. No cambian el avance ni reemplazan el último envío oficial."
                                tone="default"
                            >
                                <SubmittedEvidencePanel
                                    title="Seguimientos registrados después del último envío"
                                    items={complementaryFollowupEvidenceItems}
                                    canDecideException={false}
                                />
                            </SectionCard>
                        ) : null}

                        {permissions?.can_progress &&
                            ["rechazado", "con_cambios"].includes(String(task?.review_status || "").toLowerCase()) ? (
                            <SectionCard
                                title="Última observación del revisor"
                                subtitle="Razón ejecutiva por la que esta tarea fue devuelta para corrección."
                                tone="correction"
                            >
                                <div
                                    style={{
                                        padding: 14,
                                        borderRadius: 16,
                                        background: "#ffffff",
                                        border: "1px solid #fed7aa",
                                        display: "grid",
                                        gap: 10,
                                    }}
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                                        <Badge bg="#ffffff" color="#9a3412" title="El responsable debe atender la observación y reenviar evidencia.">
                                            {String(task?.review_status || "").toLowerCase() === "rechazado"
                                                ? "Avance rechazado"
                                                : "Cambios solicitados"}
                                        </Badge>

                                        <div style={{ fontSize: 12, color: "#9a3412", fontWeight: 800 }}>
                                            {latestCorrectionLog?.created_at ? formatDateTime(latestCorrectionLog.created_at) : "Sin fecha"} · {latestCorrectionLog?.user_name || task?.reviewer_name || "Revisor"}
                                        </div>
                                    </div>

                                    <div style={{ fontSize: 15, color: "#111827", lineHeight: 1.55, fontWeight: 750, whiteSpace: "pre-wrap" }}>
                                        {latestCorrectionLog?.comment ||
                                            task?.last_review_comment ||
                                            "El revisor solicitó corregir esta tarea antes de reenviarla a revisión."}
                                    </div>
                                </div>
                            </SectionCard>
                        ) : null}

                    </main>

                    <SmartTaskSidePanel
                        task={task}
                        permissions={permissions}
                        actionFocus={cognitiveActionFocus}
                        taskTimeHealth={taskTimeHealth}
                        routeHealth={routeHealth}
                        reviewCycles={reviewCycles}
                        evidenceSummary={evidenceSummary}
                        latestResponsibleEvidenceItems={latestResponsibleEvidenceItems}
                        pendingGeoExceptions={pendingGeoExceptions}
                        hasBlockingGeoException={hasBlockingGeoException}
                        responsibleGeoEvidenceRequired={responsibleGeoEvidenceRequired}
                    />
                </div>

                <div className="mytask-fullwidth-trace" style={{ width: "100%", marginTop: 18 }}>

                    <SectionCard
                        title="Trazabilidad de tarea"
                        subtitle="Elige si deseas consultar los ciclos de revisión o el repositorio documental de evidencias."
                    >
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <TraceTabButton
                                active={traceMainView === "cycles"}
                                icon="↻"
                                title="Ciclos de revisión"
                                subtitle={`${reviewCycles.length || 0} ciclo(s) registrados`}
                                tooltip="Consultar ciclos de revisión, decisiones, cambios y bitácora de la tarea."
                                onClick={() => {
                                    setTraceMainView("cycles");
                                    setTraceCycleView("detail");
                                }}
                            />
                            <TraceTabButton
                                active={traceMainView === "evidence"}
                                icon="📁"
                                title="Repositorio de evidencias"
                                subtitle={`${evidenceSummary?.total || 0} evidencia(s) · ${evidenceGeoStats.with_location || 0} con ubicación · ${evidenceGeoStats.valid || 0} validada(s)`}
                                tooltip="Consultar evidencias documentales, archivos, enlaces, comentarios, geocerca y excepciones."
                                onClick={() => {
                                    setTraceMainView("evidence");
                                    setEvidenceRepoView("detail");
                                }}
                            />
                        </div>
                    </SectionCard>

                    {traceMainView === "cycles" ? (
                        <SectionCard
                            title="Ciclos de revisión"
                            subtitle="Consulta la trazabilidad de la tarea como narrativa ejecutiva o como grid PMO compacto."
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 12,
                                    flexWrap: "wrap",
                                    marginBottom: 16,
                                }}
                            >
                                <div>
                                    <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 950 }}>
                                        Lectura de ciclos
                                    </div>
                                    <div style={{ marginTop: 3, fontSize: 12, color: "#64748b", fontWeight: 750 }}>
                                        {reviewCycles.length || 0} ciclo(s) · {traceGridRows.length || 0} movimiento(s) registrados
                                    </div>
                                </div>

                                <IconViewToggle
                                    value={traceCycleView}
                                    onChange={setTraceCycleView}
                                    leftTitle="Ver ciclos como detalle"
                                    rightTitle="Ver ciclos como cards"
                                />
                            </div>

                            {traceCycleView === "detail" ? (
                                <>
                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                                        <TraceGridFilterButton active={traceGridFilter === "todos"} count={traceGridFilterCounts.todos} tone="neutral" onClick={() => setTraceGridFilter("todos")}>Todos</TraceGridFilterButton>
                                        <TraceGridFilterButton active={traceGridFilter === "envios"} count={traceGridFilterCounts.envios} tone="purple" onClick={() => setTraceGridFilter("envios")}>Envíos</TraceGridFilterButton>
                                        <TraceGridFilterButton active={traceGridFilter === "decisiones"} count={traceGridFilterCounts.decisiones} tone="success" onClick={() => setTraceGridFilter("decisiones")}>Decisiones</TraceGridFilterButton>
                                        <TraceGridFilterButton active={traceGridFilter === "evidencias"} count={traceGridFilterCounts.evidencias} tone="info" onClick={() => setTraceGridFilter("evidencias")}>Evidencias</TraceGridFilterButton>
                                        <TraceGridFilterButton active={traceGridFilter === "cambios"} count={traceGridFilterCounts.cambios} tone="warning" onClick={() => setTraceGridFilter("cambios")}>Cambios</TraceGridFilterButton>
                                        <TraceGridFilterButton active={traceGridFilter === "rechazos"} count={traceGridFilterCounts.rechazos} tone="danger" onClick={() => setTraceGridFilter("rechazos")}>Rechazos</TraceGridFilterButton>
                                    </div>

                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 10,
                                            flexWrap: "wrap",
                                            marginBottom: 12,
                                            padding: "10px 12px",
                                            borderRadius: 14,
                                            background: "#f8fafc",
                                            border: "1px solid #e2e8f0",
                                        }}
                                    >
                                        <div style={{ color: "#334155", fontSize: 12, fontWeight: 900 }}>
                                            Mostrando {filteredTraceGridRows.length} de {traceGridRows.length} movimiento(s)
                                        </div>
                                        <div style={{ color: "#64748b", fontSize: 12, fontWeight: 750 }}>
                                            Da clic en cualquier renglón del grid para abrir su detalle sin salir de la tarea.
                                        </div>
                                    </div>

                                    <TraceGridView rows={filteredTraceGridRows} onOpenDetail={setSelectedTraceRow} />
                                </>
                            ) : (
                                <>
                                    {reviewCycles.length === 0 ? (
                                        <div style={{ color: "#6b7280", fontSize: 14 }}>
                                            No hay ciclos de revisión registrados.
                                        </div>
                                    ) : (
                                        <div style={{ display: "grid", gap: 16 }}>
                                            {reviewCycles.map((cycle, index) => (
                                                <ReviewCycleCard
                                                    key={cycle.id || index}
                                                    cycle={cycle}
                                                    index={index}
                                                    total={reviewCycles.length}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {logs.length > 0 ? (
                                        <details className="mytask-technical-log" style={{ marginTop: 18, border: "1px solid #e2e8f0", borderRadius: 16, background: "#fff", overflow: "hidden" }}>
                                            <summary
                                                title="La bitácora técnica muestra registros completos para auditoría y trazabilidad avanzada."
                                                style={{
                                                    cursor: "pointer",
                                                    fontSize: 13,
                                                    fontWeight: 950,
                                                    color: "#0f172a",
                                                    padding: "12px 14px",
                                                    background: "#f8fafc",
                                                    borderBottom: "1px solid #e2e8f0",
                                                }}
                                            >
                                                ▾ Ver bitácora técnica completa
                                            </summary>

                                            <div style={{ padding: 14 }}>
                                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                                                    {[
                                                        ["todos", "Todos"],
                                                        ["evidencias", "Evidencias"],
                                                        ["decisiones", "Decisiones"],
                                                        ["comentarios", "Comentarios"],
                                                    ].map(([key, label]) => (
                                                        <EvidenceFilterButton
                                                            key={key}
                                                            active={logFilter === key}
                                                            onClick={() => setLogFilter(key)}
                                                            tooltip={`Filtrar bitácora: ${label}`}
                                                        >
                                                            {label}
                                                        </EvidenceFilterButton>
                                                    ))}
                                                </div>

                                                <div style={{ display: "grid", gap: 18 }}>
                                                    {groupedLogs.map((group) => ({
                                                        ...group,
                                                        items: group.items.filter((log) => logMatchesFilter(log, logFilter)),
                                                    })).filter((group) => group.items.length > 0).map((group) => (
                                                        <div key={group.label}>
                                                            <div
                                                                style={{
                                                                    fontSize: 13,
                                                                    color: "#6b7280",
                                                                    fontWeight: 900,
                                                                    marginBottom: 10,
                                                                }}
                                                            >
                                                                {group.label}
                                                            </div>

                                                            <div style={{ display: "grid", gap: 12 }}>
                                                                {group.items.map((log) => {
                                                                    const summary = getLogSummary(log);

                                                                    return (
                                                                        <div
                                                                            key={log.id}
                                                                            style={{
                                                                                display: "grid",
                                                                                gridTemplateColumns: "18px 1fr",
                                                                                gap: 10,
                                                                            }}
                                                                        >
                                                                            <div
                                                                                style={{
                                                                                    width: 12,
                                                                                    height: 12,
                                                                                    borderRadius: "50%",
                                                                                    background: getLogDotColor(log),
                                                                                    marginTop: 5,
                                                                                }}
                                                                            />

                                                                            <div
                                                                                style={{
                                                                                    border: "1px solid #e5e7eb",
                                                                                    borderRadius: 14,
                                                                                    padding: 14,
                                                                                    background: "#fff",
                                                                                }}
                                                                            >
                                                                                <div
                                                                                    style={{
                                                                                        display: "flex",
                                                                                        justifyContent: "space-between",
                                                                                        gap: 10,
                                                                                        flexWrap: "wrap",
                                                                                    }}
                                                                                >
                                                                                    <div>
                                                                                        <div
                                                                                            style={{
                                                                                                fontSize: 14,
                                                                                                fontWeight: 900,
                                                                                                color: "#111827",
                                                                                            }}
                                                                                        >
                                                                                            {getLogTitle(log)}
                                                                                        </div>

                                                                                        <div
                                                                                            style={{
                                                                                                fontSize: 12,
                                                                                                color: "#6b7280",
                                                                                                marginTop: 3,
                                                                                            }}
                                                                                        >
                                                                                            {formatDateTime(log.created_at)} · {log.user_name || "Usuario"}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                {summary ? (
                                                                                    <div
                                                                                        style={{
                                                                                            fontSize: 13,
                                                                                            color: "#374151",
                                                                                            fontWeight: 800,
                                                                                            marginTop: 8,
                                                                                        }}
                                                                                    >
                                                                                        {summary}
                                                                                    </div>
                                                                                ) : null}

                                                                                <div
                                                                                    style={{
                                                                                        fontSize: 13,
                                                                                        color: "#374151",
                                                                                        marginTop: 8,
                                                                                        whiteSpace: "pre-wrap",
                                                                                    }}
                                                                                >
                                                                                    {getLogComment(log)}
                                                                                </div>

                                                                                <CompactEvidenceList items={log.evidence_items} />
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </details>
                                    ) : null}
                                </>
                            )}
                        </SectionCard>
                    ) : null}

                    {traceMainView === "evidence" ? (
                        <SectionCard
                            title="Repositorio documental de evidencias"
                            subtitle="Consulta, filtra y valida los respaldos registrados durante avances, revisiones y decisiones de la tarea."
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 12,
                                    flexWrap: "wrap",
                                    marginBottom: 16,
                                }}
                            >
                                <div>
                                    <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 950 }}>
                                        Lectura del repositorio
                                    </div>
                                    <div style={{ marginTop: 3, fontSize: 12, color: "#64748b", fontWeight: 750 }}>
                                        {filteredEvidenceItems.length || 0} evidencia(s) filtradas · {evidenceGeoStats.with_location || 0} con ubicación
                                    </div>
                                </div>

                                <IconViewToggle
                                    value={evidenceRepoView}
                                    onChange={setEvidenceRepoView}
                                    leftTitle="Ver evidencias como detalle"
                                    rightTitle="Ver evidencias como cards"
                                />
                            </div>

                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                                <Badge bg={evidenceStatusStyle.bg} color={evidenceStatusStyle.color}>
                                    {evidenceStatusStyle.label}
                                </Badge>

                                <Badge bg="#eef2ff" color="#4338ca">
                                    Total: {evidenceSummary?.total || 0}
                                </Badge>

                                <Badge bg="#eff6ff" color="#1d4ed8">
                                    Avance: {evidenceSummary?.avance_total || 0}
                                </Badge>

                                <Badge bg="#ecfeff" color="#0f766e">
                                    Avance con ubicación: {evidenceSummary?.avance_with_geo || 0}
                                </Badge>

                                <Badge bg="#f5f3ff" color="#6d28d9">
                                    Revisión: {evidenceSummary?.revision_total || 0}
                                </Badge>

                                <Badge bg="#f0fdf4" color="#15803d">
                                    Revisión con ubicación: {evidenceSummary?.revision_with_geo || 0}
                                </Badge>

                                <Badge bg="#dcfce7" color="#166534" title="Evidencias validadas dentro del radio autorizado.">
                                    Validadas: {evidenceGeoStats.valid || 0}
                                </Badge>

                                <Badge bg="#ffffff" color="#92400e" title="Evidencias con ubicación que aún no tienen validación de rango.">
                                    Pendientes: {evidenceGeoStats.pending || 0}
                                </Badge>

                                {evidenceGeoStats.out_of_range > 0 ? (
                                    <Badge bg="#ffffff" color="#9a3412" title="Evidencias registradas fuera del radio permitido.">
                                        Fuera de rango: {evidenceGeoStats.out_of_range}
                                    </Badge>
                                ) : null}

                                {evidenceGeoStats.exception_requested > 0 ? (
                                    <Badge bg="#ffffff" color="#9a3412" title="Evidencias fuera de rango con excepción pendiente de autorización.">
                                        Excepción solicitada: {evidenceGeoStats.exception_requested}
                                    </Badge>
                                ) : null}

                                {evidenceGeoStats.exception_rejected > 0 ? (
                                    <Badge bg="#ffffff" color="#b91c1c" title="Evidencias fuera de rango cuya excepción fue rechazada.">
                                        Excepción rechazada: {evidenceGeoStats.exception_rejected}
                                    </Badge>
                                ) : null}

                                {evidenceGeoStats.exception > 0 ? (
                                    <Badge bg="#f5f3ff" color="#6d28d9" title="Evidencias aceptadas mediante excepción autorizada.">
                                        Excepción autorizada: {evidenceGeoStats.exception}
                                    </Badge>
                                ) : null}

                                {evidenceSummary?.evidence_required ? (
                                    <Badge bg="#ffffff" color="#92400e">
                                        Evidencia requerida
                                    </Badge>
                                ) : null}

                                {responsibleGeoEvidenceRequired ? (
                                    <Badge bg="#ffffff" color="#9a3412">
                                        Ubicación requerida
                                    </Badge>
                                ) : null}
                            </div>
                            <div
                                style={{
                                    marginBottom: 12,
                                    padding: 14,
                                    borderRadius: 14,
                                    background: "#f8fafc",
                                    border: "1px solid #e5e7eb",
                                }}
                            >
                                <div style={{ fontWeight: 900, color: "#111827", marginBottom: 4 }}>
                                    Filtros del repositorio
                                </div>

                                <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.45 }}>
                                    Usa estos filtros para revisar evidencias por contexto, tipo de archivo, ubicación capturada o validación geográfica.
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                                <EvidenceFilterButton active={evidenceFilter === "todas"} onClick={() => setEvidenceFilter("todas")}>
                                    Todas
                                </EvidenceFilterButton>

                                <EvidenceFilterButton active={evidenceFilter === "avance"} onClick={() => setEvidenceFilter("avance")}>
                                    Avance
                                </EvidenceFilterButton>

                                <EvidenceFilterButton active={evidenceFilter === "revision"} onClick={() => setEvidenceFilter("revision")}>
                                    Revisión
                                </EvidenceFilterButton>

                                <EvidenceFilterButton active={evidenceFilter === "imagenes"} onClick={() => setEvidenceFilter("imagenes")}>
                                    Imágenes
                                </EvidenceFilterButton>

                                <EvidenceFilterButton active={evidenceFilter === "archivos"} onClick={() => setEvidenceFilter("archivos")}>
                                    Archivos
                                </EvidenceFilterButton>

                                <EvidenceFilterButton active={evidenceFilter === "links"} onClick={() => setEvidenceFilter("links")}>
                                    Links
                                </EvidenceFilterButton>
                                <EvidenceFilterButton active={evidenceFilter === "comentarios"} onClick={() => setEvidenceFilter("comentarios")}>
                                    Comentarios
                                </EvidenceFilterButton>
                                <EvidenceFilterButton active={evidenceFilter === "con_geo"} onClick={() => setEvidenceFilter("con_geo")} tooltip="Muestra evidencias con ubicación capturada.">
                                    Con ubicación
                                </EvidenceFilterButton>

                                <EvidenceFilterButton active={evidenceFilter === "geo_validada"} onClick={() => setEvidenceFilter("geo_validada")} tooltip="Muestra evidencias validadas dentro del radio autorizado.">
                                    Validadas
                                </EvidenceFilterButton>

                                <EvidenceFilterButton active={evidenceFilter === "geo_pendiente"} onClick={() => setEvidenceFilter("geo_pendiente")} tooltip="Muestra evidencias con ubicación pendiente de validación de rango.">
                                    Pendientes
                                </EvidenceFilterButton>

                                <EvidenceFilterButton active={evidenceFilter === "geo_fuera_rango"} onClick={() => setEvidenceFilter("geo_fuera_rango")} tooltip="Muestra evidencias registradas fuera del radio permitido.">
                                    Fuera de rango
                                </EvidenceFilterButton>

                                <EvidenceFilterButton active={evidenceFilter === "geo_excepcion"} onClick={() => setEvidenceFilter("geo_excepcion")} tooltip="Muestra evidencias aceptadas mediante excepción autorizada.">
                                    Excepción
                                </EvidenceFilterButton>

                            </div>
                            <div
                                style={{
                                    marginBottom: 14,
                                    padding: 14,
                                    borderRadius: 14,
                                    background: "#f8fafc",
                                    border: "1px solid #e5e7eb",
                                    color: "#475569",
                                    fontSize: 13,
                                    lineHeight: 1.45,
                                }}
                            >
                                <strong>Repositorio de consulta.</strong> La captura de avance, evidencias y excepciones se realiza desde el panel Registrar resultado; aquí solo se consulta, filtra y audita lo registrado.
                            </div>

                            {evidenceLoading ? (
                                <div style={{ color: "#6b7280", fontSize: 14 }}>
                                    Cargando evidencias...
                                </div>
                            ) : filteredEvidenceItems.length === 0 ? (
                                <div
                                    style={{
                                        border: "1px dashed #d1d5db",
                                        borderRadius: 14,
                                        padding: 16,
                                        color: "#6b7280",
                                        background: "#f8fafc",
                                        fontSize: 14,
                                    }}
                                >
                                    No hay evidencias para mostrar con este filtro.
                                </div>
                            ) : evidenceRepoView === "detail" ? (
                                <EvidenceRepositoryGridView
                                    items={filteredEvidenceItems}
                                    permissions={permissions}
                                    onDecideException={handleGeoExceptionDecision}
                                    decidingExceptionId={decidingExceptionId}
                                />
                            ) : (
                                <div style={{ display: "grid", gap: 12 }}>
                                    {filteredEvidenceItems.map((item) => {
                                        const hasGeo =
                                            Number(item.has_geo || 0) === 1 ||
                                            (item.latitude && item.longitude);

                                        const mapsUrl = hasGeo
                                            ? `https://www.google.com/maps?q=${item.latitude},${item.longitude}`
                                            : "";

                                        return (
                                            <div
                                                key={item.id}
                                                style={{
                                                    border: "1px solid #e5e7eb",
                                                    borderRadius: 16,
                                                    padding: 16,
                                                    background: "#ffffff",
                                                    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.04)",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        gap: 12,
                                                        flexWrap: "wrap",
                                                        marginBottom: 8,
                                                    }}
                                                >
                                                    <div>
                                                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                                                            <Badge
                                                                bg={String(item.evidence_context || "").toLowerCase() === "revision" ? "#f5f3ff" : "#eff6ff"}
                                                                color={String(item.evidence_context || "").toLowerCase() === "revision" ? "#6d28d9" : "#1d4ed8"}
                                                            >
                                                                {String(item.evidence_context || "").toLowerCase() === "revision"
                                                                    ? "Evidencia de revisión"
                                                                    : "Evidencia de avance"}
                                                            </Badge>

                                                            <Badge bg="#f3f4f6" color="#374151">
                                                                {item.evidence_type || "evidencia"}
                                                            </Badge>
                                                        </div>

                                                        <div style={{ fontWeight: 900, color: "#111827", marginBottom: 4 }}>
                                                            {item.title || "Evidencia sin título"}
                                                        </div>

                                                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                                                            {formatDateTime(item.created_at)} · {item.uploaded_by_name || "Usuario"}
                                                        </div>
                                                    </div>

                                                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                                        <Badge bg="#eef2ff" color="#4338ca">
                                                            {item.evidence_type || "evidencia"}
                                                        </Badge>

                                                        {item.evidence_context ? (
                                                            <Badge bg="#f3f4f6" color="#374151">
                                                                {item.evidence_context}
                                                            </Badge>
                                                        ) : null}

                                                        {hasGeo ? (() => {
                                                            const geoVisual = getGeoValidationVisual(item);
                                                            return (
                                                                <Badge
                                                                    bg={geoVisual.visible ? geoVisual.bg : "#dcfce7"}
                                                                    color={geoVisual.visible ? geoVisual.color : "#166534"}
                                                                    title={geoVisual.visible ? geoVisual.detail : "La evidencia tiene ubicación capturada."}
                                                                >
                                                                    {geoVisual.visible ? `${geoVisual.icon} ${geoVisual.label}` : "📍 Geo capturada"}
                                                                </Badge>
                                                            );
                                                        })() : (
                                                            <Badge bg="#f3f4f6" color="#374151">
                                                                Sin geo
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>

                                                {item.description ? (
                                                    <div
                                                        style={{
                                                            fontSize: 13,
                                                            color: "#374151",
                                                            lineHeight: 1.5,
                                                            whiteSpace: "pre-wrap",
                                                            marginBottom: 10,
                                                        }}
                                                    >
                                                        {item.description}
                                                    </div>
                                                ) : null}
                                                {item.file_url &&
                                                    (
                                                        String(item.evidence_type || "").toLowerCase() === "imagen" ||
                                                        String(item.mime_type || "").toLowerCase().startsWith("image/")
                                                    ) ? (
                                                    <div style={{ marginBottom: 12 }}>
                                                        <a href={item.file_url} target="_blank" rel="noreferrer">
                                                            <img
                                                                src={item.file_url}
                                                                alt={item.title || item.file_name || "Evidencia"}
                                                                style={{
                                                                    width: "100%",
                                                                    maxWidth: 320,
                                                                    maxHeight: 180,
                                                                    objectFit: "cover",
                                                                    borderRadius: 12,
                                                                    border: "1px solid #e5e7eb",
                                                                    display: "block",
                                                                }}
                                                            />
                                                        </a>
                                                    </div>
                                                ) : null}
                                                {item.external_url ? (
                                                    <div style={{ marginBottom: 10 }}>
                                                        <a
                                                            href={item.external_url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            style={{ color: "#2563eb", fontWeight: 800, fontSize: 13 }}
                                                        >
                                                            Abrir enlace externo
                                                        </a>
                                                    </div>
                                                ) : null}

                                                {item.file_url ? (
                                                    <div style={{ marginBottom: 10 }}>
                                                        <a
                                                            href={item.file_url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            style={{ color: "#2563eb", fontWeight: 800, fontSize: 13 }}
                                                        >
                                                            Ver / Descargar archivo
                                                        </a>

                                                        {item.file_name ? (
                                                            <div style={{ marginTop: 4, color: "#6b7280", fontSize: 12 }}>
                                                                {item.file_name}
                                                                {item.file_size
                                                                    ? ` · ${Math.round(Number(item.file_size) / 1024)} KB`
                                                                    : ""}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                ) : null}

                                                {hasGeo ? (() => {
                                                    const geoVisual = getGeoValidationVisual(item);
                                                    const boxBg = geoVisual.visible ? geoVisual.bg : "#f0fdf4";
                                                    const boxBorder = geoVisual.visible ? geoVisual.border : "#bbf7d0";
                                                    const boxColor = geoVisual.visible ? geoVisual.color : "#14532d";

                                                    return (
                                                        <div
                                                            title={geoVisual.visible ? geoVisual.detail : "La evidencia tiene coordenadas capturadas."}
                                                            style={{
                                                                padding: 10,
                                                                borderRadius: 12,
                                                                border: `1px solid ${boxBorder}`,
                                                                background: boxBg,
                                                                fontSize: 13,
                                                                color: boxColor,
                                                            }}
                                                        >
                                                            <div style={{ fontWeight: 950, marginBottom: 4 }}>
                                                                {geoVisual.visible ? `${geoVisual.icon} ${geoVisual.label}` : "📍 Georreferencia registrada"}
                                                            </div>

                                                            {geoVisual.visible ? (
                                                                <div style={{ marginBottom: 6 }}>
                                                                    {geoVisual.detail}
                                                                </div>
                                                            ) : null}

                                                            <div>
                                                                Coordenadas: {item.latitude}, {item.longitude}
                                                                {item.geo_accuracy ? ` · Precisión: ${item.geo_accuracy} m` : ""}
                                                            </div>

                                                            {formatGeoExceptionReason(item) ? (
                                                                <div style={{ marginTop: 8 }}>
                                                                    <strong>Motivo de excepción:</strong> {formatGeoExceptionReason(item)}
                                                                </div>
                                                            ) : null}

                                                            <div style={{ marginTop: 8 }}>
                                                                <a
                                                                    href={mapsUrl}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    style={{ color: boxColor, fontWeight: 900 }}
                                                                >
                                                                    Ver en Google Maps
                                                                </a>
                                                            </div>

                                                            {isGeoExceptionRequested(item) && permissions.can_review ? (
                                                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                                                                    <ActionButton
                                                                        bg="#16a34a"
                                                                        border="1px solid #16a34a"
                                                                        disabled={decidingExceptionId === item.id}
                                                                        onClick={() => handleGeoExceptionDecision(item, "approve")}
                                                                    >
                                                                        Autorizar excepción
                                                                    </ActionButton>
                                                                    <ActionButton
                                                                        bg="#fff"
                                                                        color="#b91c1c"
                                                                        border="1px solid #fecaca"
                                                                        disabled={decidingExceptionId === item.id}
                                                                        onClick={() => handleGeoExceptionDecision(item, "reject")}
                                                                    >
                                                                        Rechazar excepción
                                                                    </ActionButton>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    );
                                                })() : null}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </SectionCard>
                    ) : null}


                    {false && permissions.can_progress ? (
                        <SectionCard
                            title={String(task?.review_status || "").toLowerCase() === "con_cambios" || String(task?.review_status || "").toLowerCase() === "rechazado" ? "Corregir avance" : "Registrar avance"}
                            subtitle="Actualiza el porcentaje y agrega el comentario del trabajo realizado."
                        >

                            <div style={{ display: "grid", gridTemplateColumns: "minmax(160px, 220px) 1fr", gap: 12, alignItems: "start" }}>
                                <div>
                                    <label style={{ display: "block", fontSize: 12, color: "#6b7280", fontWeight: 700, marginBottom: 6 }}>
                                        Avance (%)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={progressForm.progress_percent}
                                        onChange={(e) => setProgressForm((prev) => ({ ...prev, progress_percent: e.target.value }))}
                                        style={{
                                            width: "100%",
                                            padding: "10px 12px",
                                            borderRadius: 12,
                                            border: "1px solid #d1d5db",
                                            boxSizing: "border-box",
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: "block", fontSize: 12, color: "#6b7280", fontWeight: 700, marginBottom: 6 }}>
                                        Comentario de avance
                                    </label>
                                    <textarea
                                        rows={4}
                                        value={progressForm.comment}
                                        onChange={(e) => setProgressForm((prev) => ({ ...prev, comment: e.target.value }))}
                                        placeholder="Describe qué se corrigió, avanzó o entregó."
                                        style={{
                                            width: "100%",
                                            padding: "10px 12px",
                                            borderRadius: 12,
                                            border: "1px solid #d1d5db",
                                            boxSizing: "border-box",
                                            resize: "vertical",
                                        }}
                                    />
                                </div>
                            </div>
                            {taskRequiresResponsibleEvidence ? (
                                <>
                                    <div
                                        style={{
                                            marginTop: 14,
                                            marginBottom: 14,
                                            padding: 14,
                                            borderRadius: 14,
                                            background: "#ffffff",
                                            border: "1px solid #e5e7eb",
                                        }}
                                    >
                                        <div style={{ fontWeight: 900, color: "#111827", marginBottom: 4 }}>
                                            Evidencia de avance
                                        </div>

                                        <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>
                                            Agrega la evidencia que respalda el avance o corrección antes de enviar la tarea a revisión.
                                        </div>

                                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                                            <Badge bg="#eef2ff" color="#4338ca">
                                                Evidencia nueva: {evidenceSummary?.avance_pending_total || 0}
                                            </Badge>

                                            <Badge bg="#ecfeff" color="#0f766e">
                                                Nueva con geo: {evidenceSummary?.avance_pending_with_geo || 0}
                                            </Badge>

                                            {evidenceSummary?.evidence_required ? (
                                                <Badge bg="#ffffff" color="#92400e">
                                                    Evidencia requerida
                                                </Badge>
                                            ) : null}

                                            {responsibleGeoEvidenceRequired ? (
                                                <Badge bg="#ffffff" color="#9a3412">
                                                    Geo requerida
                                                </Badge>
                                            ) : null}
                                        </div>

                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "minmax(160px, 220px) 1fr",
                                                gap: 12,
                                                marginBottom: 12,
                                            }}
                                        >
                                            <div>
                                                <label
                                                    style={{
                                                        display: "block",
                                                        fontSize: 12,
                                                        color: "#6b7280",
                                                        fontWeight: 700,
                                                        marginBottom: 6,
                                                    }}
                                                >
                                                    Tipo
                                                </label>

                                                <select
                                                    value={evidenceForm.evidence_type}
                                                    onChange={(e) =>
                                                        setEvidenceForm((prev) => ({
                                                            ...prev,
                                                            evidence_type: e.target.value,
                                                            external_url: e.target.value === "link" ? prev.external_url : "",
                                                        }))
                                                    }
                                                    style={{
                                                        width: "100%",
                                                        padding: "10px 12px",
                                                        borderRadius: 12,
                                                        border: "1px solid #d1d5db",
                                                        boxSizing: "border-box",
                                                    }}
                                                >
                                                    <option value="comentario">Comentario</option>
                                                    <option value="link">Link externo</option>
                                                    <option value="archivo">Archivo</option>
                                                    <option value="imagen">Imagen</option>
                                                </select>
                                            </div>

                                            <div>
                                                <label
                                                    style={{
                                                        display: "block",
                                                        fontSize: 12,
                                                        color: "#6b7280",
                                                        fontWeight: 700,
                                                        marginBottom: 6,
                                                    }}
                                                >
                                                    Título de evidencia
                                                </label>

                                                <input
                                                    type="text"
                                                    value={evidenceForm.title}
                                                    onChange={(e) =>
                                                        setEvidenceForm((prev) => ({
                                                            ...prev,
                                                            title: e.target.value,
                                                        }))
                                                    }
                                                    placeholder="Ej. Captura de corrección, avance realizado, documento actualizado..."
                                                    style={{
                                                        width: "100%",
                                                        padding: "10px 12px",
                                                        borderRadius: 12,
                                                        border: "1px solid #d1d5db",
                                                        boxSizing: "border-box",
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {evidenceForm.evidence_type === "link" ? (
                                            <div style={{ marginBottom: 12 }}>
                                                <label
                                                    style={{
                                                        display: "block",
                                                        fontSize: 12,
                                                        color: "#6b7280",
                                                        fontWeight: 700,
                                                        marginBottom: 6,
                                                    }}
                                                >
                                                    Enlace externo
                                                </label>

                                                <input
                                                    type="url"
                                                    value={evidenceForm.external_url}
                                                    onChange={(e) =>
                                                        setEvidenceForm((prev) => ({
                                                            ...prev,
                                                            external_url: e.target.value,
                                                        }))
                                                    }
                                                    placeholder="https://..."
                                                    style={{
                                                        width: "100%",
                                                        padding: "10px 12px",
                                                        borderRadius: 12,
                                                        border: "1px solid #d1d5db",
                                                        boxSizing: "border-box",
                                                    }}
                                                />
                                            </div>
                                        ) : null}

                                        {["archivo", "imagen"].includes(evidenceForm.evidence_type) ? (
                                            <div style={{ marginBottom: 12 }}>
                                                <label
                                                    style={{
                                                        display: "block",
                                                        fontSize: 12,
                                                        color: "#6b7280",
                                                        fontWeight: 700,
                                                        marginBottom: 6,
                                                    }}
                                                >
                                                    Archivo de avance
                                                </label>

                                                <input
                                                    type="file"
                                                    accept={
                                                        evidenceForm.evidence_type === "imagen"
                                                            ? "image/*"
                                                            : ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,image/*"
                                                    }
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0] || null;
                                                        setEvidenceFile(file);

                                                        if (file && !evidenceForm.title) {
                                                            setEvidenceForm((prev) => ({
                                                                ...prev,
                                                                title: file.name,
                                                            }));
                                                        }
                                                    }}
                                                    style={{
                                                        width: "100%",
                                                        padding: "10px 12px",
                                                        borderRadius: 12,
                                                        border: "1px solid #d1d5db",
                                                        boxSizing: "border-box",
                                                        background: "#fff",
                                                    }}
                                                />

                                                {evidenceFile ? (
                                                    <div style={{ marginTop: 8, fontSize: 12, color: "#374151" }}>
                                                        Archivo seleccionado: <strong>{evidenceFile.name}</strong>{" "}
                                                        ({Math.round(evidenceFile.size / 1024)} KB)
                                                    </div>
                                                ) : null}
                                            </div>
                                        ) : null}

                                        <div style={{ marginBottom: 12 }}>
                                            <label
                                                style={{
                                                    display: "block",
                                                    fontSize: 12,
                                                    color: "#6b7280",
                                                    fontWeight: 700,
                                                    marginBottom: 6,
                                                }}
                                            >
                                                Descripción de evidencia
                                            </label>

                                            <textarea
                                                rows={3}
                                                value={evidenceForm.description}
                                                onChange={(e) =>
                                                    setEvidenceForm((prev) => ({
                                                        ...prev,
                                                        description: e.target.value,
                                                    }))
                                                }
                                                placeholder="Describe qué comprueba esta evidencia de avance."
                                                style={{
                                                    width: "100%",
                                                    padding: "10px 12px",
                                                    borderRadius: 12,
                                                    border: "1px solid #d1d5db",
                                                    boxSizing: "border-box",
                                                    resize: "vertical",
                                                }}
                                            />
                                        </div>

                                        <div
                                            style={{
                                                padding: 14,
                                                borderRadius: 14,
                                                background: "#ffffff",
                                                border: responsibleGeoEvidenceRequired ? "1px solid #fdba74" : "1px solid #e5e7eb",
                                                marginBottom: 12,
                                            }}
                                        >
                                            <div style={{ fontWeight: 900, color: "#111827", marginBottom: 4 }}>
                                                Georreferencia de avance
                                            </div>

                                            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 10 }}>
                                                {responsibleGeoEvidenceRequired
                                                    ? "Esta tarea requiere ubicación automática. Se capturará al guardar la evidencia o puedes capturarla manualmente aquí."
                                                    : "Puedes capturar ubicación para dar mayor trazabilidad al avance."}
                                            </div>

                                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                                <ActionButton
                                                    onClick={handleCaptureGeo}
                                                    disabled={capturingGeo || savingEvidence}
                                                    bg="#fff"
                                                    color="#0f766e"
                                                    border="1px solid #99f6e4"
                                                >
                                                    {capturingGeo ? "Capturando ubicación..." : "Capturar ubicación actual"}
                                                </ActionButton>

                                                {evidenceForm.latitude && evidenceForm.longitude ? (
                                                    <>
                                                        <Badge bg="#dcfce7" color="#166534">
                                                            Ubicación capturada
                                                        </Badge>

                                                        <a
                                                            href={`https://www.google.com/maps?q=${evidenceForm.latitude},${evidenceForm.longitude}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            style={{
                                                                color: "#166534",
                                                                fontWeight: 900,
                                                                fontSize: 13,
                                                            }}
                                                        >
                                                            Ver en Google Maps
                                                        </a>
                                                    </>
                                                ) : (
                                                    <Badge bg="#f3f4f6" color="#374151">
                                                        Sin ubicación
                                                    </Badge>
                                                )}
                                            </div>

                                            {evidenceForm.latitude && evidenceForm.longitude ? (
                                                <div style={{ marginTop: 10, fontSize: 12, color: "#374151" }}>
                                                    Lat: {evidenceForm.latitude} · Lng: {evidenceForm.longitude}
                                                    {evidenceForm.geo_accuracy ? ` · Precisión: ${evidenceForm.geo_accuracy} m` : ""}
                                                </div>
                                            ) : null}
                                        </div>

                                        <ActionButton onClick={handleEvidenceSubmit} disabled={savingEvidence}>
                                            {savingEvidence ? "Guardando evidencia..." : "Guardar evidencia de avance"}
                                        </ActionButton>
                                    </div>

                                </>
                            ) : taskRequiresOnlyReview ? (
                                <div
                                    style={{
                                        marginTop: 14,
                                        marginBottom: 14,
                                        padding: 14,
                                        borderRadius: 14,
                                        background: "#eff6ff",
                                        border: "1px solid #bfdbfe",
                                        color: "#1d4ed8",
                                        fontSize: 13,
                                        fontWeight: 800,
                                        lineHeight: 1.45,
                                    }}
                                >
                                    Esta tarea no requiere evidencia documental ni georreferencia. Al guardar el avance,
                                    se enviará directamente a revisión para conservar el ciclo PMO.
                                </div>
                            ) : (
                                <div
                                    style={{
                                        marginTop: 14,
                                        marginBottom: 14,
                                        padding: 14,
                                        borderRadius: 14,
                                        background: "#f8fafc",
                                        border: "1px solid #e5e7eb",
                                        color: "#475569",
                                        fontSize: 13,
                                        fontWeight: 800,
                                        lineHeight: 1.45,
                                    }}
                                >
                                    Esta tarea no requiere revisión ni evidencia. Registra el avance y guarda la actualización operativa.
                                </div>
                            )}

                            {taskRequiresReview && taskRequiresResponsibleEvidence ? (
                                <>
                                    {reviewEvidenceBlock.blocked ? (
                                        <div
                                            style={{
                                                marginTop: 12,
                                                padding: 14,
                                                borderRadius: 14,
                                                background: "#ffffff",
                                                border: "1px solid #fecaca",
                                                color: "#991b1b",
                                                fontSize: 13,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {reviewEvidenceBlock.message}

                                            <div style={{ marginTop: 6, fontWeight: 500, color: "#7f1d1d" }}>
                                                Agrega la evidencia de avance en este mismo bloque antes de enviar a revisión.
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                marginTop: 12,
                                                padding: 14,
                                                borderRadius: 14,
                                                background: "#ecfdf5",
                                                border: "1px solid #a7f3d0",
                                                color: "#166534",
                                                fontSize: 13,
                                                fontWeight: 700,
                                            }}
                                        >
                                            Evidencia validada. Al guardar el avance, la tarea se enviará automáticamente a revisión.
                                        </div>
                                    )}

                                    {progressRequirements.length > 0 ? (
                                        <div
                                            style={{
                                                marginTop: 14,
                                                padding: 14,
                                                borderRadius: 14,
                                                background: "#ffffff",
                                                border: "1px solid #e5e7eb",
                                            }}
                                        >
                                            <div style={{ fontWeight: 900, color: "#111827", marginBottom: 4 }}>
                                                Requisitos para enviar avance
                                            </div>

                                            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>
                                                Requisitos operativos que debe cumplir esta tarea antes de guardar y enviar a revisión.
                                            </div>

                                            <div style={{ display: "grid", gap: 8 }}>
                                                {progressRequirements.map((req) => (
                                                    <RequirementItem
                                                        key={req.key}
                                                        ok={req.ok}
                                                        label={req.label}
                                                        detail={req.detail}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}
                                </>
                            ) : null}

                            <div style={{ marginTop: 16 }}>
                                <ActionButton
                                    onClick={handleProgressSubmit}
                                    disabled={savingProgress || (taskRequiresReview && taskRequiresResponsibleEvidence && reviewEvidenceBlock.blocked)}
                                    bg={taskRequiresReview && taskRequiresResponsibleEvidence && reviewEvidenceBlock.blocked ? "#9ca3af" : "#111827"}
                                    border={taskRequiresReview && taskRequiresResponsibleEvidence && reviewEvidenceBlock.blocked ? "1px solid #9ca3af" : "1px solid #111827"}
                                >
                                    {savingProgress
                                        ? "Guardando..."
                                        : taskRequiresReview && taskRequiresResponsibleEvidence && reviewEvidenceBlock.blocked
                                            ? "Falta evidencia requerida"
                                            : progressButtonLabel}
                                </ActionButton>
                            </div>
                        </SectionCard>
                    ) : null}

                    {false && (mode === "review" || permissions?.can_review) ? (
                        <SectionCard
                            title="Tarea a revisar"
                            subtitle="Resumen ejecutivo del último avance, comentario y evidencias vinculadas."
                        >
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                                    gap: 12,
                                    marginBottom: 16,
                                }}
                            >
                                <InfoBox label="WBS" value={task?.wbs_code || "—"} />
                                <InfoBox label="Tarea" value={task?.name || "Sin nombre"} />
                                <InfoBox label="Responsable" value={task?.owner_name || "—"} />
                                <InfoBox label="Avance enviado" value={`${task?.progress_percent || 0}%`} />
                            </div>

                            <div
                                style={{
                                    padding: 14,
                                    borderRadius: 14,
                                    background: "#eff6ff",
                                    border: "1px solid #bfdbfe",
                                }}
                            >
                                <div style={{ fontSize: 12, fontWeight: 800, color: "#1d4ed8", marginBottom: 6 }}>
                                    ÚLTIMO ENVÍO A REVISIÓN
                                </div>

                                {latestReviewSubmissionLog ? (
                                    <>
                                        <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>
                                            {latestReviewSubmissionLog.created_at || "Sin fecha"}
                                        </div>

                                        <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
                                            {latestReviewSubmissionLog.user_name || task?.owner_name || "Responsable"}
                                        </div>

                                        <div style={{ fontSize: 14, color: "#111827", lineHeight: 1.5 }}>
                                            {latestReviewSubmissionLog.comment || "Sin comentario registrado."}
                                        </div>
                                    </>
                                ) : (
                                    <div style={{ fontSize: 13, color: "#6b7280" }}>
                                        No se encontró un envío reciente en la bitácora.
                                    </div>
                                )}
                            </div>
                        </SectionCard>
                    ) : null}


                    {false && permissions.can_review ? (
                        <SectionCard
                            title="Decisión de revisión"
                            subtitle="Registra tu decisión sobre el último avance enviado por el responsable."
                            tone="correction"
                        >
                            <label style={{ display: "block", fontSize: 12, color: "#6b7280", fontWeight: 700, marginBottom: 6 }}>
                                Comentario de revisión
                            </label>
                            <textarea
                                rows={4}
                                value={reviewForm.comment}
                                onChange={(e) => setReviewForm((prev) => ({ ...prev, comment: e.target.value }))}
                                placeholder="Comentario de revisión (opcional)"
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    borderRadius: 12,
                                    border: "1px solid #d1d5db",
                                    boxSizing: "border-box",
                                    resize: "vertical",
                                    marginBottom: 12,
                                }}
                            />

                            <div
                                style={{
                                    marginBottom: 14,
                                    padding: 14,
                                    borderRadius: 14,
                                    background: "#ffffff",
                                    border: "1px solid #e5e7eb",
                                }}
                            >
                                <div style={{ fontWeight: 900, color: "#111827", marginBottom: 4 }}>
                                    Evidencia de revisión
                                </div>

                                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>
                                    Adjunta la evidencia que respalda tu decisión como revisor. Esta evidencia quedará ligada al movimiento de revisión.
                                </div>

                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                                    <Badge bg="#f5f3ff" color="#6d28d9">
                                        Evidencia pendiente: {evidenceSummary?.revision_pending_total || 0}
                                    </Badge>

                                    <Badge bg="#f0fdf4" color="#15803d">
                                        Con geo pendiente: {evidenceSummary?.revision_pending_with_geo || 0}
                                    </Badge>

                                    {Number(task?.reviewer_evidence_required || 0) === 1 ? (
                                        <Badge bg="#ffffff" color="#92400e">
                                            Evidencia requerida
                                        </Badge>
                                    ) : null}

                                    {Number(task?.reviewer_geo_evidence_required || 0) === 1 ? (
                                        <Badge bg="#ffffff" color="#9a3412">
                                            Geo requerida
                                        </Badge>
                                    ) : null}
                                </div>

                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "minmax(160px, 220px) 1fr",
                                        gap: 12,
                                        marginBottom: 12,
                                    }}
                                >
                                    <div>
                                        <label
                                            style={{
                                                display: "block",
                                                fontSize: 12,
                                                color: "#6b7280",
                                                fontWeight: 700,
                                                marginBottom: 6,
                                            }}
                                        >
                                            Tipo
                                        </label>

                                        <select
                                            value={evidenceForm.evidence_type}
                                            onChange={(e) =>
                                                setEvidenceForm((prev) => ({
                                                    ...prev,
                                                    evidence_type: e.target.value,
                                                    external_url: e.target.value === "link" ? prev.external_url : "",
                                                }))
                                            }
                                            style={{
                                                width: "100%",
                                                padding: "10px 12px",
                                                borderRadius: 12,
                                                border: "1px solid #d1d5db",
                                                boxSizing: "border-box",
                                            }}
                                        >
                                            <option value="comentario">Comentario</option>
                                            <option value="link">Link externo</option>
                                            <option value="archivo">Archivo</option>
                                            <option value="imagen">Imagen</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label
                                            style={{
                                                display: "block",
                                                fontSize: 12,
                                                color: "#6b7280",
                                                fontWeight: 700,
                                                marginBottom: 6,
                                            }}
                                        >
                                            Título de evidencia
                                        </label>

                                        <input
                                            type="text"
                                            value={evidenceForm.title}
                                            onChange={(e) =>
                                                setEvidenceForm((prev) => ({
                                                    ...prev,
                                                    title: e.target.value,
                                                }))
                                            }
                                            placeholder="Ej. Validación de revisión, captura, documento firmado..."
                                            style={{
                                                width: "100%",
                                                padding: "10px 12px",
                                                borderRadius: 12,
                                                border: "1px solid #d1d5db",
                                                boxSizing: "border-box",
                                            }}
                                        />
                                    </div>
                                </div>

                                {evidenceForm.evidence_type === "link" ? (
                                    <div style={{ marginBottom: 12 }}>
                                        <label
                                            style={{
                                                display: "block",
                                                fontSize: 12,
                                                color: "#6b7280",
                                                fontWeight: 700,
                                                marginBottom: 6,
                                            }}
                                        >
                                            Enlace externo
                                        </label>

                                        <input
                                            type="url"
                                            value={evidenceForm.external_url}
                                            onChange={(e) =>
                                                setEvidenceForm((prev) => ({
                                                    ...prev,
                                                    external_url: e.target.value,
                                                }))
                                            }
                                            placeholder="https://..."
                                            style={{
                                                width: "100%",
                                                padding: "10px 12px",
                                                borderRadius: 12,
                                                border: "1px solid #d1d5db",
                                                boxSizing: "border-box",
                                            }}
                                        />
                                    </div>
                                ) : null}

                                {["archivo", "imagen"].includes(evidenceForm.evidence_type) ? (
                                    <div style={{ marginBottom: 12 }}>
                                        <label
                                            style={{
                                                display: "block",
                                                fontSize: 12,
                                                color: "#6b7280",
                                                fontWeight: 700,
                                                marginBottom: 6,
                                            }}
                                        >
                                            Archivo de revisión
                                        </label>

                                        <input
                                            type="file"
                                            accept={
                                                evidenceForm.evidence_type === "imagen"
                                                    ? "image/*"
                                                    : ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,image/*"
                                            }
                                            onChange={(e) => {
                                                const file = e.target.files?.[0] || null;
                                                setEvidenceFile(file);

                                                if (file && !evidenceForm.title) {
                                                    setEvidenceForm((prev) => ({
                                                        ...prev,
                                                        title: file.name,
                                                    }));
                                                }
                                            }}
                                            style={{
                                                width: "100%",
                                                padding: "10px 12px",
                                                borderRadius: 12,
                                                border: "1px solid #d1d5db",
                                                boxSizing: "border-box",
                                                background: "#fff",
                                            }}
                                        />

                                        {evidenceFile ? (
                                            <div style={{ marginTop: 8, fontSize: 12, color: "#374151" }}>
                                                Archivo seleccionado: <strong>{evidenceFile.name}</strong>{" "}
                                                ({Math.round(evidenceFile.size / 1024)} KB)
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}

                                <div style={{ marginBottom: 12 }}>
                                    <label
                                        style={{
                                            display: "block",
                                            fontSize: 12,
                                            color: "#6b7280",
                                            fontWeight: 700,
                                            marginBottom: 6,
                                        }}
                                    >
                                        Descripción de evidencia
                                    </label>

                                    <textarea
                                        rows={3}
                                        value={evidenceForm.description}
                                        onChange={(e) =>
                                            setEvidenceForm((prev) => ({
                                                ...prev,
                                                description: e.target.value,
                                            }))
                                        }
                                        placeholder="Describe qué comprueba esta evidencia de revisión."
                                        style={{
                                            width: "100%",
                                            padding: "10px 12px",
                                            borderRadius: 12,
                                            border: "1px solid #d1d5db",
                                            boxSizing: "border-box",
                                            resize: "vertical",
                                        }}
                                    />
                                </div>

                                <div
                                    style={{
                                        padding: 14,
                                        borderRadius: 14,
                                        background: "#ffffff",
                                        border: Number(task?.reviewer_geo_evidence_required || 0) === 1 ? "1px solid #fdba74" : "1px solid #e5e7eb",
                                        marginBottom: 12,
                                    }}
                                >
                                    <div style={{ fontWeight: 900, color: "#111827", marginBottom: 4 }}>
                                        Georreferencia de revisión
                                    </div>

                                    <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 10 }}>
                                        {Number(task?.reviewer_geo_evidence_required || 0) === 1
                                            ? "Esta revisión requiere ubicación. La plataforma intentará capturarla automáticamente al guardar la evidencia."
                                            : "Puedes capturar ubicación para dar mayor trazabilidad a tu revisión."}
                                    </div>

                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                        <ActionButton
                                            onClick={handleCaptureGeo}
                                            disabled={capturingGeo || savingEvidence}
                                            bg="#fff"
                                            color="#0f766e"
                                            border="1px solid #99f6e4"
                                        >
                                            {capturingGeo ? "Capturando ubicación..." : "Capturar ubicación actual"}
                                        </ActionButton>

                                        {evidenceForm.latitude && evidenceForm.longitude ? (
                                            <>
                                                <Badge bg="#dcfce7" color="#166534">
                                                    Ubicación capturada
                                                </Badge>

                                                <a
                                                    href={`https://www.google.com/maps?q=${evidenceForm.latitude},${evidenceForm.longitude}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    style={{
                                                        color: "#166534",
                                                        fontWeight: 900,
                                                        fontSize: 13,
                                                    }}
                                                >
                                                    Ver en Google Maps
                                                </a>
                                            </>
                                        ) : (
                                            <Badge bg="#f3f4f6" color="#374151">
                                                Sin ubicación
                                            </Badge>
                                        )}
                                    </div>

                                    {evidenceForm.latitude && evidenceForm.longitude ? (
                                        <div style={{ marginTop: 10, fontSize: 12, color: "#374151" }}>
                                            Lat: {evidenceForm.latitude} · Lng: {evidenceForm.longitude}
                                            {evidenceForm.geo_accuracy ? ` · Precisión: ${evidenceForm.geo_accuracy} m` : ""}
                                        </div>
                                    ) : null}
                                </div>

                                <ActionButton onClick={handleReviewEvidenceSubmit} disabled={savingEvidence}>
                                    {savingEvidence ? "Guardando evidencia..." : "Guardar evidencia de revisión"}
                                </ActionButton>
                            </div>

                            {reviewRequirements.length > 0 ? (
                                <div
                                    style={{
                                        marginBottom: 14,
                                        padding: 14,
                                        borderRadius: 14,
                                        background: "#ffffff",
                                        border: "1px solid #e5e7eb",
                                    }}
                                >
                                    <div style={{ fontWeight: 900, color: "#111827", marginBottom: 4 }}>
                                        Checklist para aplicar revisión
                                    </div>

                                    <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>
                                        Requisitos que deben cumplirse antes de aprobar, rechazar o solicitar cambios.
                                    </div>

                                    <div style={{ display: "grid", gap: 8 }}>
                                        {reviewRequirements.map((req) => (
                                            <RequirementItem
                                                key={req.key}
                                                ok={req.ok}
                                                label={req.label}
                                                detail={req.detail}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ) : null}

                            {reviewerEvidenceBlock.blocked ? (
                                <div
                                    style={{
                                        marginBottom: 12,
                                        padding: 14,
                                        borderRadius: 14,
                                        background: "#ffffff",
                                        border: "1px solid #fecaca",
                                        color: "#991b1b",
                                        fontSize: 13,
                                        fontWeight: 700,
                                    }}
                                >
                                    {reviewerEvidenceBlock.message}

                                    <div style={{ marginTop: 6, fontWeight: 500, color: "#7f1d1d" }}>
                                        Agrega la evidencia de revisión en este mismo bloque antes de aplicar la decisión.
                                    </div>
                                </div>
                            ) : permissions.can_review ? (
                                <div
                                    style={{
                                        marginBottom: 12,
                                        padding: 14,
                                        borderRadius: 14,
                                        background: "#ecfdf5",
                                        border: "1px solid #a7f3d0",
                                        color: "#166534",
                                        fontSize: 13,
                                        fontWeight: 700,
                                    }}
                                >
                                    Requisitos de revisión completos. Puedes aplicar la decisión.
                                </div>
                            ) : null}

                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <ActionButton
                                    onClick={() => handleComposerReviewSubmit("aprobar")}
                                    disabled={savingReview || hasBlockingGeoException}
                                    bg={hasBlockingGeoException ? "#9ca3af" : "#16a34a"}
                                    border={hasBlockingGeoException ? "1px solid #9ca3af" : "1px solid #16a34a"}
                                    title={
                                        hasBlockingGeoException
                                            ? "No puedes aprobar mientras exista evidencia geográfica rechazada o pendiente."
                                            : "Aprobar avance"
                                    }
                                >
                                    {hasBlockingGeoException ? "Aprobar bloqueado" : "Aprobar"}
                                </ActionButton>

                                <ActionButton
                                    onClick={() => handleComposerReviewSubmit("solicitar_cambios")}
                                    disabled={savingReview}
                                    bg="#fff"
                                    color="#92400e"
                                    border="1px solid #fcd34d"
                                    title="Solicitar cambios al responsable"
                                >
                                    Solicitar cambios
                                </ActionButton>

                                <ActionButton
                                    onClick={() => handleComposerReviewSubmit("rechazar")}
                                    disabled={savingReview}
                                    bg="#fff"
                                    color="#b91c1c"
                                    border="1px solid #fecaca"
                                    title="Rechazar avance"
                                >
                                    Rechazar
                                </ActionButton>
                            </div>
                        </SectionCard>
                    ) : null}

                    {!canUseProgressComposer && !permissions.can_review && !permissions.can_view_followup && !hasBlockingGeoException && !taskIntervention?.requiresIntervention ? (
                        <SectionCard title="Sin acciones pendientes" subtitle="Esta tarea no requiere acción operativa de tu parte en este momento." />
                    ) : null}
                </div>
            </div>

            <TraceRecordDetailModal
                row={selectedTraceRow}
                onClose={() => setSelectedTraceRow(null)}
            />
        </>
    );
}
